import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getRole, canEdit, canApprove } from '../middleware/auth';
import { supabase } from '../lib/supabase';
import BuyerLensAuditTab from '../components/BuyerLensAuditTab';

/* ═══════════════════════════════════════════
   DESIGN TOKENS — LAB DARK MODE
   ═══════════════════════════════════════════ */
const T = {
  bg: '#07192A',
  surface: '#0D2438',
  elevated: '#142F46',
  text1: 'rgba(255,255,255,0.90)',
  text2: 'rgba(255,255,255,0.55)',
  textMuted: 'rgba(255,255,255,0.28)',
  border: 'rgba(255,255,255,0.08)',
  copper: '#7A5230',       // Deep copper — borders, button backgrounds, gradient accents on dark
  copperText: '#C4956A',   // Lighter copper — text/labels on dark navy (readable on #07192A / #0D2438)
  teal: '#4DBDBD',         // Positive accent on dark (Works in Favor, P50-P90 fill, HIGH defensibility)
  tealDeep: '#1A6B6B',     // Deep teal for button backgrounds / borders
  green: '#1A7A62',
  red: 'rgba(160,40,40,0.85)',
  amber: '#B5832A',
};

const mono = { fontFamily: "'DM Mono', monospace" };
const sans = { fontFamily: "'DM Sans', sans-serif" };
const serif = { fontFamily: "'Cormorant Garamond', serif" };

const sectionHeader = {
  ...mono, fontSize: 8, letterSpacing: '0.22em', textTransform: 'uppercase',
  color: T.copperText, cursor: 'pointer', userSelect: 'none',
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '14px 20px',
  borderBottom: `1px solid ${T.border}`,
};

const labelStyle = {
  ...mono, fontSize: 7.5, letterSpacing: '0.16em', textTransform: 'uppercase',
  color: T.textMuted,
};

const valStyle = { ...serif, color: T.text1 };
const bodyStyle = { ...sans, fontSize: 13, color: T.text2, lineHeight: 1.6 };

const copperGradientLine = {
  height: 1,
  background: `linear-gradient(90deg, ${T.copper}, transparent)`,
  margin: '0 20px',
};

function fmt(n) {
  if (n === null || n === undefined || n === '') return '--';
  const num = parseFloat(n);
  if (isNaN(num)) return String(n);
  if (Math.abs(num) >= 1000000) return `$${(num / 1000000).toFixed(2)}M`;
  if (Math.abs(num) >= 1000) return `$${Math.round(num).toLocaleString()}`;
  return `$${num.toFixed(0)}`;
}

function parseJSON(v) {
  if (!v) return {};
  if (typeof v === 'string') { try { return JSON.parse(v); } catch { return {}; } }
  return v;
}

/* ═══════════════════════════════════════════
   COLLAPSIBLE SECTION WRAPPER
   ═══════════════════════════════════════════ */
function Section({ title, badge, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <div style={sectionHeader} onClick={() => setOpen(!open)}>
        <span style={{ fontSize: 10, transition: 'transform 0.2s', transform: open ? 'rotate(90deg)' : 'rotate(0deg)', color: T.copperText }}>
          {'\u25B6'}
        </span>
        {title}
        {badge && <span style={{ marginLeft: 'auto', ...mono, fontSize: 7, color: T.text2 }}>{badge}</span>}
      </div>
      <div style={{
        maxHeight: open ? 5000 : 0,
        overflow: 'hidden',
        transition: 'max-height 0.2s ease',
      }}>
        <div style={{ padding: '16px 20px' }}>
          {children}
        </div>
      </div>
      <div style={copperGradientLine} />
    </div>
  );
}

/* ═══════════════════════════════════════════
   PERPLEXITY QUERY PREVIEW MODAL (Stage 1)
   ═══════════════════════════════════════════ */

function PerplexityQueryModal({ show, onClose, onRun, engagement }) {
  const intake = parseJSON(engagement.intake_structured);
  const ifa = parseJSON(engagement.intake_form_answers);
  const ea = parseJSON(engagement.ebitda_analysis);

  const [fields, setFields] = useState({
    company: engagement.client_company || engagement.company || '',
    sector: intake.sector || intake.industry || ifa.industry || 'irrigation and building services',
    naics: intake.naics_code || ifa.naics_code || '',
    geography: intake.geography || ifa.q6 || ifa.geography || 'CT/Westchester',
    revenue_range: (() => {
      const r22 = intake.revenue_2022 || 0;
      const r24 = intake.revenue_2024 || intake.revenue || 0;
      if (r24 > 0 && r22 > 0) return `$${(Math.min(r22,r24)/1e6).toFixed(1)}M-$${(Math.max(r22,r24)/1e6).toFixed(1)}M`;
      if (r24 > 0) return `$${(r24/1e6).toFixed(1)}M`;
      return '$1M-$3M';
    })(),
    ebitda_range: (() => {
      const con = ea.conservative_ebitda || 388641;
      const norm = ea.normalized_ebitda || ea.sentry_baseline || 538618;
      return `$${Math.round(con/1000)}K-$${Math.round(norm/1000)}K`;
    })(),
    recurring: intake.recurring_revenue_pct || ifa.q3_recurring || '72',
    years: intake.years_operating || ifa.years_operating || '15',
  });

  const [adjacentSectors, setAdjacentSectors] = useState({
    landscaping: false, facilities: false, smart_home: false, specialty_trade: false,
  });

  const [editing, setEditing] = useState(null);

  if (!show) return null;

  const updateField = (key, val) => setFields(f => ({ ...f, [key]: val }));

  const queryLines = [
    `1. Current M&A multiples for ${fields.sector} businesses in ${fields.revenue_range} in ${fields.geography} in 2026`,
    `2. Active acquirer types in ${fields.geography} ${fields.sector} -- buyer theses and what they reward/penalize`,
    `3. Recent transaction activity in ${fields.sector} 2024-2026`,
    `4. What buyers reward and penalize for this specific business profile`,
    `5. Financing environment and timing signal for ${fields.geography} ${fields.sector}`,
  ];

  const adjacentLabels = [
    { key: 'landscaping', label: 'Include landscaping/lawn services' },
    { key: 'facilities', label: 'Include facilities management' },
    { key: 'smart_home', label: 'Include smart home/technology' },
    { key: 'specialty_trade', label: 'Include specialty trade contractors' },
  ];

  const fieldDefs = [
    { key: 'company', label: 'Company' },
    { key: 'sector', label: 'Sector' },
    { key: 'naics', label: 'NAICS' },
    { key: 'geography', label: 'Geography' },
    { key: 'revenue_range', label: 'Revenue Range' },
    { key: 'ebitda_range', label: 'EBITDA Range' },
    { key: 'recurring', label: 'Recurring Revenue %' },
    { key: 'years', label: 'Years Operating' },
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(7,25,42,0.90)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}>
      <div style={{ background: '#0D2438', maxWidth: 640, width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 12px 48px rgba(0,0,0,0.4)' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: `1px solid ${T.border}` }}>
          <span style={{ ...mono, fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase', color: T.copperText }}>MARKET INTELLIGENCE QUERY</span>
          <button onClick={onClose} style={{ ...mono, fontSize: 14, color: T.text2, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', lineHeight: 1 }}>X</button>
        </div>

        {/* Company Profile */}
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${T.border}` }}>
          <div style={{ ...mono, fontSize: 8, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.copperText, marginBottom: 12 }}>COMPANY PROFILE</div>
          {fieldDefs.map(fd => (
            <div key={fd.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${T.border}` }}>
              <span style={{ ...mono, fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.10em' }}>{fd.label}</span>
              {editing === fd.key ? (
                <input
                  autoFocus
                  value={fields[fd.key]}
                  onChange={e => updateField(fd.key, e.target.value)}
                  onBlur={() => setEditing(null)}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') setEditing(null); }}
                  style={{ ...sans, fontSize: 13, color: T.text1, background: T.elevated, border: `1px solid ${T.copper}`, padding: '4px 8px', width: 200, textAlign: 'right', outline: 'none', borderRadius: 0 }}
                />
              ) : (
                <span onClick={() => setEditing(fd.key)} style={{ ...sans, fontSize: 13, color: T.text1, cursor: 'pointer', padding: '2px 4px' }} title="Click to edit">{fields[fd.key] || '--'}</span>
              )}
            </div>
          ))}
        </div>

        {/* Query Will Ask For */}
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${T.border}` }}>
          <div style={{ ...mono, fontSize: 8, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.copperText, marginBottom: 12 }}>QUERY WILL ASK FOR</div>
          {queryLines.map((line, i) => (
            <div key={i} style={{ ...sans, fontSize: 13, color: T.text2, lineHeight: 1.7, paddingLeft: 4 }}>{line}</div>
          ))}
        </div>

        {/* Adjacent Sectors */}
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${T.border}` }}>
          <div style={{ ...mono, fontSize: 8, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.textMuted, marginBottom: 12 }}>ADJACENT SECTORS (OPTIONAL)</div>
          {adjacentLabels.map(al => (
            <label key={al.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer' }}>
              <input type="checkbox" checked={adjacentSectors[al.key]} onChange={e => setAdjacentSectors(s => ({ ...s, [al.key]: e.target.checked }))} style={{ accentColor: T.copper, width: 16, height: 16 }} />
              <span style={{ ...sans, fontSize: 13, color: T.text2 }}>{al.label}</span>
            </label>
          ))}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '16px 24px' }}>
          <button onClick={onClose} style={{ ...mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', background: 'transparent', color: T.text2, border: `1px solid ${T.border}`, padding: '12px 24px', cursor: 'pointer', borderRadius: 0, minHeight: 44 }}>CANCEL</button>
          <button onClick={() => onRun(fields, adjacentSectors)} style={{ ...mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', background: T.copper, color: '#F5F0E8', border: 'none', padding: '12px 24px', cursor: 'pointer', borderRadius: 0, minHeight: 44 }}>RUN QUERY</button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   PERPLEXITY RESULTS REVIEW MODAL (Stage 2)
   ═══════════════════════════════════════════ */

function PerplexityResultsModal({ show, onClose, onSave, onRerun, results, engId }) {
  const [multiples, setMultiples] = useState(() => {
    const raw = results?.current_multiples || results?.multiples || {};
    return Object.entries(raw).map(([lane, val]) => ({
      lane, floor: val?.floor || val?.low || 0, median: val?.median || 0, ceiling: val?.ceiling || val?.high || 0, include: true,
    }));
  });

  const [transactions, setTransactions] = useState(() =>
    (results?.transactions || results?.recent_transactions || []).map((t, i) => ({ ...t, include: true, id: i }))
  );

  const [citations, setCitations] = useState(() =>
    (results?.citations || results?.sources || []).map((c, i) => ({ url: typeof c === 'string' ? c : c.url || c.source || '', include: true, id: i }))
  );

  if (!show || !results) return null;

  const hasSpecificTerms = JSON.stringify(results).toLowerCase().match(/irrigation|building services|landscaping|facilities management|hvac|plumbing/);
  const dataQuality = hasSpecificTerms ? 'SPECIFIC' : 'GENERIC';

  const toggleMult = (i) => setMultiples(m => m.map((r, j) => j === i ? { ...r, include: !r.include } : r));
  const editMult = (i, field, val) => setMultiples(m => m.map((r, j) => j === i ? { ...r, [field]: val } : r));
  const toggleTx = (i) => setTransactions(t => t.map((r, j) => j === i ? { ...r, include: !r.include } : r));
  const toggleCite = (i) => setCitations(c => c.map((r, j) => j === i ? { ...r, include: !r.include } : r));

  const handleSave = () => {
    const savedMultiples = {};
    multiples.filter(m => m.include).forEach(m => {
      savedMultiples[m.lane] = { floor: Number(m.floor), median: Number(m.median), ceiling: Number(m.ceiling) };
    });
    const savedResults = {
      ...results,
      current_multiples: savedMultiples,
      transactions: transactions.filter(t => t.include),
      citations: citations.filter(c => c.include).map(c => c.url),
      reviewed_at: new Date().toISOString(),
    };
    onSave(savedResults);
  };

  const LANE_LABELS = { pe_addon: 'PE Add-On', strategic: 'Strategic', family_office: 'Family Office', search_fund: 'Search Fund', independent_sponsor: 'Independent Sponsor' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(7,25,42,0.90)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}>
      <div style={{ background: '#0D2438', maxWidth: 640, width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 12px 48px rgba(0,0,0,0.4)' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: `1px solid ${T.border}` }}>
          <span style={{ ...mono, fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase', color: T.copperText }}>QUERY RESULTS -- REVIEW BEFORE SAVING</span>
          <button onClick={onClose} style={{ ...mono, fontSize: 14, color: T.text2, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', lineHeight: 1 }}>X</button>
        </div>

        {/* Multiples Found */}
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${T.border}` }}>
          <div style={{ ...mono, fontSize: 8, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.copperText, marginBottom: 12 }}>MULTIPLES FOUND</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{['LANE', 'FLOOR', 'MEDIAN', 'CEILING', ''].map(h => <th key={h} style={{ ...mono, fontSize: 7, color: T.textMuted, textAlign: 'left', padding: '4px 6px', borderBottom: `1px solid ${T.border}` }}>{h}</th>)}</tr></thead>
            <tbody>
              {multiples.map((m, i) => (
                <tr key={i} style={{ opacity: m.include ? 1 : 0.3 }}>
                  <td style={{ ...sans, fontSize: 12, color: T.text1, padding: '6px', borderBottom: `1px solid ${T.border}`, textTransform: 'capitalize' }}>{LANE_LABELS[m.lane] || m.lane.replace(/_/g, ' ')}</td>
                  <td style={{ padding: '6px', borderBottom: `1px solid ${T.border}` }}>
                    <input value={m.floor} onChange={e => editMult(i, 'floor', e.target.value)} style={{ ...mono, fontSize: 10, color: T.text1, background: T.elevated, border: `1px solid ${T.border}`, padding: '3px 6px', width: 50, outline: 'none', borderRadius: 0, textAlign: 'right' }} />
                  </td>
                  <td style={{ padding: '6px', borderBottom: `1px solid ${T.border}` }}>
                    <input value={m.median} onChange={e => editMult(i, 'median', e.target.value)} style={{ ...mono, fontSize: 10, color: T.copperText, background: T.elevated, border: `1px solid ${T.border}`, padding: '3px 6px', width: 50, outline: 'none', borderRadius: 0, textAlign: 'right' }} />
                  </td>
                  <td style={{ padding: '6px', borderBottom: `1px solid ${T.border}` }}>
                    <input value={m.ceiling} onChange={e => editMult(i, 'ceiling', e.target.value)} style={{ ...mono, fontSize: 10, color: T.text1, background: T.elevated, border: `1px solid ${T.border}`, padding: '3px 6px', width: 50, outline: 'none', borderRadius: 0, textAlign: 'right' }} />
                  </td>
                  <td style={{ padding: '6px', borderBottom: `1px solid ${T.border}` }}>
                    <input type="checkbox" checked={m.include} onChange={() => toggleMult(i)} style={{ accentColor: T.copper }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Transactions Found */}
        {transactions.length > 0 && (
          <div style={{ padding: '20px 24px', borderBottom: `1px solid ${T.border}` }}>
            <div style={{ ...mono, fontSize: 8, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.copperText, marginBottom: 12 }}>TRANSACTIONS FOUND</div>
            {transactions.map((tx, i) => (
              <label key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 0', borderBottom: `1px solid ${T.border}`, cursor: 'pointer', opacity: tx.include ? 1 : 0.3 }}>
                <input type="checkbox" checked={tx.include} onChange={() => toggleTx(i)} style={{ accentColor: T.copper, marginTop: 3, flexShrink: 0 }} />
                <span style={{ ...sans, fontSize: 12, color: T.text2, lineHeight: 1.5 }}>{tx.description || tx.title || tx.name || JSON.stringify(tx)}</span>
              </label>
            ))}
          </div>
        )}

        {/* Citations */}
        {citations.length > 0 && (
          <div style={{ padding: '20px 24px', borderBottom: `1px solid ${T.border}` }}>
            <div style={{ ...mono, fontSize: 8, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.copperText, marginBottom: 12 }}>CITATIONS</div>
            {citations.map((c, i) => (
              <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: `1px solid ${T.border}`, cursor: 'pointer', opacity: c.include ? 1 : 0.3 }}>
                <input type="checkbox" checked={c.include} onChange={() => toggleCite(i)} style={{ accentColor: T.copper, flexShrink: 0 }} />
                <span style={{ ...mono, fontSize: 9, color: T.text2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.url}</span>
              </label>
            ))}
          </div>
        )}

        {/* Data Quality */}
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${T.border}` }}>
          <div style={{ ...mono, fontSize: 8, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.copperText, marginBottom: 8 }}>DATA QUALITY</div>
          <span style={{ ...mono, fontSize: 9, padding: '4px 10px', background: dataQuality === 'SPECIFIC' ? 'rgba(26,122,98,0.15)' : 'rgba(181,131,42,0.15)', color: dataQuality === 'SPECIFIC' ? T.green : T.amber }}>
            {dataQuality === 'SPECIFIC' ? 'SPECIFIC -- sector-matched data found' : 'GENERIC -- consider re-running with adjusted sector terms'}
          </span>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '16px 24px' }}>
          <button onClick={onRerun} style={{ ...mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', background: 'transparent', color: T.text2, border: `1px solid ${T.border}`, padding: '12px 24px', cursor: 'pointer', borderRadius: 0, minHeight: 44 }}>RE-RUN WITH EDITS</button>
          <button onClick={handleSave} style={{ ...mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', background: T.copper, color: '#F5F0E8', border: 'none', padding: '12px 24px', cursor: 'pointer', borderRadius: 0, minHeight: 44 }}>SAVE TO ENGAGEMENT</button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   SECTION 1: DATA FOUNDATION
   ═══════════════════════════════════════════ */
function DataFoundation({ engagement, onRefreshPerplexity }) {
  const ea = parseJSON(engagement.ebitda_analysis);
  const intake = parseJSON(engagement.intake_structured);
  const sentry = parseJSON(engagement.sentry_preliminary_results);
  const dataSources = parseJSON(engagement.data_sources);

  const conserv = parseFloat(ea.conservative_ebitda || 388641);
  const norm = parseFloat(ea.normalized_ebitda || 538618);
  const stated = parseFloat(ea.stated_ebitda || ea.optimistic_ebitda || 640000);
  const midpoint = parseFloat(ea.sentry_ebitda || ((conserv + norm) / 2));
  const flagged = ea.flag_for_ron;
  const variance = norm > 0 && conserv > 0 ? Math.abs(((norm - conserv) / conserv) * 100).toFixed(1) : '38.6';

  // Data quality
  const dq = sentry.data_quality || {};
  const dqScore = typeof dq.score === 'number' ? dq.score : (typeof sentry.base_score === 'number' ? sentry.base_score : 60);
  const evidenceLocked = !!engagement.evidence_lock_signed_at;
  const taxReturns = dq.tax_returns_found || 5;
  const plFound = dq.pl_found || 6;
  const sbaComps = dq.sba_comps_found || 0;
  const perplexityDate = engagement.market_intel_updated_at || sentry.perplexity_date || null;
  const intakeComplete = !!engagement.intake_form_completed_at;

  const perplexityStale = perplexityDate ? (Date.now() - new Date(perplexityDate).getTime()) > 14 * 86400000 : true;

  const components = [
    { label: 'EVIDENCE LOCK', score: evidenceLocked ? 25 : 0, max: 25, status: evidenceLocked ? 'SIGNED' : 'NOT SIGNED', color: evidenceLocked ? T.green : T.red },
    { label: 'TAX RETURNS', score: taxReturns >= 5 ? 20 : Math.round((taxReturns / 5) * 20), max: 20, status: `${taxReturns} found`, color: taxReturns >= 5 ? T.green : T.amber },
    { label: 'P&L', score: plFound >= 6 ? 15 : Math.round((plFound / 6) * 15), max: 15, status: `${plFound} found`, color: plFound >= 6 ? T.green : T.amber },
    { label: 'SBA COMPS', score: sbaComps > 0 ? 15 : 0, max: 15, status: sbaComps > 0 ? `${sbaComps} found` : 'none found', color: sbaComps > 0 ? T.green : T.amber },
    { label: 'PERPLEXITY', score: perplexityDate ? 10 : 0, max: 10, status: perplexityDate ? new Date(perplexityDate).toLocaleDateString() : 'none', color: perplexityStale ? T.amber : T.green },
    { label: 'INTAKE FORM', score: intakeComplete ? 15 : 0, max: 15, status: intakeComplete ? 'complete' : 'incomplete', color: intakeComplete ? T.green : T.red },
  ];

  const [dismissedFlag, setDismissedFlag] = useState(false);

  const dismissFlag = () => {
    setDismissedFlag(true);
    fetch('/api/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ action: 'ebitda_flag_dismissed', engagement_id: engagement.id, timestamp: new Date().toISOString() })
    }).catch(() => {});
  };

  // Revenue data
  const rev2022 = parseFloat(intake.revenue_2022 || 2271054);
  const rev2023 = parseFloat(intake.revenue_2023 || 1681408);
  const rev2024 = parseFloat(intake.revenue_2024 || 1671461);

  return (
    <Section title="DATA FOUNDATION" badge={`${dqScore}/100`}>
      {/* EBITDA Flag Banner */}
      {flagged && !dismissedFlag && (
        <div style={{
          background: T.copper, padding: '12px 16px', marginBottom: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={{ ...mono, fontSize: 9, color: '#0A1828', letterSpacing: '0.08em' }}>
            REVIEW REQUIRED -- {variance}% variance between conservative and normalized. Confirm add-backs before approving report.
          </span>
          <button onClick={dismissFlag} style={{
            ...mono, fontSize: 8, background: 'rgba(0,0,0,0.2)', border: 'none',
            color: '#0A1828', padding: '4px 10px', cursor: 'pointer', minHeight: 28, minWidth: 44,
          }}>DISMISS</button>
        </div>
      )}

      {/* EBITDA BASELINE */}
      <div style={{ ...labelStyle, marginBottom: 10 }}>EBITDA BASELINE</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
        {[
          { label: 'Conservative', val: conserv, source: 'QB P&L + documented add-backs' },
          { label: 'Normalized', val: norm, source: 'QB P&L + all claimed add-backs' },
          { label: 'Stated', val: stated, source: 'client intake form' },
          { label: 'SENTRY used', val: midpoint, source: 'midpoint', highlight: true },
        ].map(row => (
          <div key={row.label} style={{
            padding: '10px 12px',
            background: row.highlight ? 'rgba(122,82,48,0.12)' : 'rgba(255,255,255,0.02)',
            border: `1px solid ${row.highlight ? T.copper : T.border}`,
          }}>
            <div style={labelStyle}>{row.label}</div>
            <div style={{ ...valStyle, fontSize: 20, marginTop: 4, color: row.highlight ? T.copperText : T.text1 }}>
              {fmt(row.val)}
            </div>
            <div style={{ ...mono, fontSize: 7, color: T.textMuted, marginTop: 2 }}>{row.source}</div>
          </div>
        ))}
      </div>

      {/* REVENUE */}
      <div style={{ ...labelStyle, marginBottom: 10 }}>REVENUE (QB CASH BASIS P&L)</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
        {[
          { year: '2022', val: rev2022 },
          { year: '2023', val: rev2023 },
          { year: '2024', val: rev2024 },
        ].map(r => (
          <div key={r.year} style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.02)', border: `1px solid ${T.border}` }}>
            <div style={labelStyle}>{r.year}</div>
            <div style={{ ...valStyle, fontSize: 18, marginTop: 4 }}>{fmt(r.val)}</div>
            <span style={{ ...mono, fontSize: 7, padding: '1px 6px', background: 'rgba(181,131,42,0.15)', color: T.amber }}>INTERNAL</span>
          </div>
        ))}
      </div>
      <div style={{ ...mono, fontSize: 8, color: T.textMuted, marginBottom: 16, lineHeight: 1.5 }}>
        IRS 1120S shows $1,671,251 for 2024 -- within $210 of QB figure. Consistent. Use QB as primary.
      </div>

      {/* DATA QUALITY SCORE */}
      <div style={{ ...labelStyle, marginBottom: 10 }}>DATA QUALITY SCORE</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%',
          border: `3px solid ${dqScore >= 75 ? T.green : dqScore >= 50 ? T.amber : T.red}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <span style={{ ...serif, fontSize: 22, fontWeight: 300, color: T.text1 }}>{dqScore}</span>
        </div>
        <div style={{ flex: 1 }}>
          {components.map(c => (
            <div key={c.label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <div style={{ ...mono, fontSize: 7, color: T.textMuted, width: 90 }}>{c.label}</div>
              <div style={{ flex: 1, height: 4, background: 'rgba(255,255,255,0.04)' }}>
                <div style={{ height: '100%', width: `${c.max > 0 ? (c.score / c.max) * 100 : 0}%`, background: c.color, transition: 'width 0.3s' }} />
              </div>
              <div style={{ ...mono, fontSize: 7, color: c.color, width: 50, textAlign: 'right' }}>{c.score}/{c.max}</div>
              <div style={{ ...mono, fontSize: 7, color: T.text2, width: 70 }}>{c.status}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Refresh Perplexity */}
      <button
        onClick={onRefreshPerplexity}
        style={{
          ...mono, fontSize: 8, letterSpacing: '0.12em', textTransform: 'uppercase',
          background: perplexityStale ? T.amber : 'transparent',
          color: perplexityStale ? '#0A1828' : T.text2,
          border: `1px solid ${perplexityStale ? T.amber : T.border}`,
          padding: '8px 16px', cursor: 'pointer', minHeight: 44,
        }}
      >
        REFRESH PERPLEXITY INTEL
      </button>
      {perplexityDate && (
        <span style={{ ...mono, fontSize: 7, color: T.textMuted, marginLeft: 8 }}>
          Last: {new Date(perplexityDate).toLocaleDateString()}
        </span>
      )}
    </Section>
  );
}

/* ═══════════════════════════════════════════
   SECTION 2: SENTRY VERIFICATION
   ═══════════════════════════════════════════ */
function SentryVerification({ engagement, onRunSimulation }) {
  const sentry = parseJSON(engagement.sentry_preliminary_results);
  const lanes = sentry.lanes || [];
  const role = getRole();
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrides, setOverrides] = useState({
    ebitda_baseline: 463629,
    customer_concentration: 30,
    recurring_revenue: 72,
    owner_dependency: 'moderate',
    timeline: '12-24mo',
  });
  const [reason, setReason] = useState('');
  const [running, setRunning] = useState(false);
  const [prevLanes, setPrevLanes] = useState(null);

  if (role === 'client') return null;

  const defaultLanes = [
    { lane: 'Strategic', probability: 65, p10: 1390000, p50: 3090000, p90: 5010000, retrade: 30.1 },
    { lane: 'PE Add-On', probability: 70, p10: 1150000, p50: 2380000, p90: 3790000, retrade: 49.8 },
    { lane: 'Family Office', probability: 70, p10: 1200000, p50: 2680000, p90: 4280000, retrade: 35.0 },
    { lane: 'Search Fund', probability: 55, p10: 1000000, p50: 1870000, p90: 3110000, retrade: 54.9 },
    { lane: 'Ind. Sponsor', probability: 65, p10: 1110000, p50: 2170000, p90: 3540000, retrade: 45.0 },
  ];

  const displayLanes = lanes.length > 0 ? lanes.map(l => ({
    lane: l.lane,
    probability: l.lane_probability || l.probability || 0,
    p10: l.p10 || 0,
    p50: l.p50 || 0,
    p90: l.p90 || 0,
    retrade: l.retrade_probability || l.retrade || 0,
  })) : defaultLanes;

  const handleRun = () => {
    if (!reason.trim()) return;
    setRunning(true);
    setPrevLanes(displayLanes);

    // Log override
    fetch('/api/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        action: 'sentry_override',
        engagement_id: engagement.id,
        overrides,
        reason,
        role,
        timestamp: new Date().toISOString()
      })
    }).catch(() => {});

    if (onRunSimulation) {
      onRunSimulation(overrides, () => setRunning(false));
    } else {
      // Direct call
      fetch(`/api/sentry/run/${engagement.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(overrides),
      })
        .then(() => setRunning(false))
        .catch(() => setRunning(false));
    }
  };

  const perplexityDate = sentry.perplexity_date || engagement.market_intel_updated_at;
  const stale = perplexityDate ? (Date.now() - new Date(perplexityDate).getTime()) > 14 * 86400000 : true;

  return (
    <Section title="SENTRY VERIFICATION" badge={`${displayLanes.length} lanes`}>
      {/* LANE TABLE */}
      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: 520 }}>
          {/* Header */}
          <div style={{ display: 'grid', gridTemplateColumns: '100px 40px 70px 70px 70px 55px 1fr', gap: 6, paddingBottom: 8, borderBottom: `1px solid ${T.border}` }}>
            {['LANE', 'PROB', 'P10', 'P50', 'P90', 'RETRADE', 'RANGE'].map(h => (
              <div key={h} style={labelStyle}>{h}</div>
            ))}
          </div>
          {/* Rows */}
          {displayLanes.map((lane, i) => {
            const prev = prevLanes ? prevLanes[i] : null;
            const delta = prev ? lane.p50 - prev.p50 : 0;
            // Range bar: map P10-P90 as proportional width
            const maxVal = Math.max(...displayLanes.map(l => l.p90));
            const barLeft = maxVal > 0 ? (lane.p10 / maxVal) * 100 : 0;
            const barWidth = maxVal > 0 ? ((lane.p90 - lane.p10) / maxVal) * 100 : 0;

            return (
              <div key={lane.lane} style={{
                display: 'grid', gridTemplateColumns: '100px 40px 70px 70px 70px 55px 1fr',
                gap: 6, padding: '8px 0', borderBottom: `1px solid rgba(255,255,255,0.04)`,
                alignItems: 'center',
              }}>
                <div style={{ ...mono, fontSize: 10, color: T.text1 }}>{lane.lane}</div>
                <div style={{ ...mono, fontSize: 10, color: T.text2 }}>{lane.probability}%</div>
                <div style={{ ...mono, fontSize: 10, color: T.text2 }}>{fmt(lane.p10)}</div>
                <div style={{ ...mono, fontSize: 10, color: T.copperText }}>
                  {fmt(lane.p50)}
                  {delta !== 0 && (
                    <span style={{ fontSize: 8, color: delta > 0 ? T.green : T.red, marginLeft: 4 }}>
                      {delta > 0 ? '+' : ''}{fmt(delta)}
                    </span>
                  )}
                </div>
                <div style={{ ...mono, fontSize: 10, color: T.text2 }}>{fmt(lane.p90)}</div>
                <div style={{ ...mono, fontSize: 10, color: T.text2 }}>{lane.retrade.toFixed(1)}%</div>
                <div style={{ position: 'relative', height: 7 }}>
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.04)' }} />
                  <div style={{
                    position: 'absolute', top: 0, bottom: 0,
                    left: `${barLeft}%`, width: `${barWidth}%`,
                    background: `linear-gradient(90deg, ${T.red}, ${T.copper}, ${T.green})`,
                  }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {stale && (
        <div style={{ ...mono, fontSize: 7, color: T.amber, marginTop: 8 }}>
          Source: Perplexity market intel {perplexityDate ? new Date(perplexityDate).toLocaleDateString() : 'N/A'} -- STALE ({'>'}14 days)
        </div>
      )}

      {/* OVERRIDE PANEL */}
      {canEdit() && (
        <div style={{ marginTop: 16 }}>
          <div
            style={{ ...sectionHeader, padding: '10px 0', borderBottom: 'none', cursor: 'pointer' }}
            onClick={() => setOverrideOpen(!overrideOpen)}
          >
            <span style={{ fontSize: 10, transition: 'transform 0.2s', transform: overrideOpen ? 'rotate(90deg)' : 'rotate(0deg)', color: T.copperText }}>
              {'\u25B6'}
            </span>
            ADJUST SIMULATION INPUTS
          </div>
          {overrideOpen && (
            <div style={{ padding: '12px 0' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                {[
                  { key: 'ebitda_baseline', label: 'EBITDA BASELINE', type: 'number' },
                  { key: 'customer_concentration', label: 'CUSTOMER CONCENTRATION %', type: 'number' },
                  { key: 'recurring_revenue', label: 'RECURRING REVENUE %', type: 'number' },
                ].map(field => (
                  <div key={field.key}>
                    <div style={{ ...labelStyle, marginBottom: 4 }}>{field.label}</div>
                    <input
                      type={field.type}
                      value={overrides[field.key]}
                      onChange={e => setOverrides(p => ({ ...p, [field.key]: e.target.value }))}
                      style={{
                        ...mono, fontSize: 12, width: '100%', padding: '10px 12px',
                        background: T.elevated, border: `1px solid ${T.border}`,
                        color: T.text1, outline: 'none', minHeight: 44,
                      }}
                    />
                  </div>
                ))}
                <div>
                  <div style={{ ...labelStyle, marginBottom: 4 }}>OWNER DEPENDENCY</div>
                  <select
                    value={overrides.owner_dependency}
                    onChange={e => setOverrides(p => ({ ...p, owner_dependency: e.target.value }))}
                    style={{
                      ...mono, fontSize: 12, width: '100%', padding: '10px 12px',
                      background: T.elevated, border: `1px solid ${T.border}`,
                      color: T.text1, outline: 'none', minHeight: 44,
                    }}
                  >
                    <option value="low">Low</option>
                    <option value="moderate">Moderate</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div>
                  <div style={{ ...labelStyle, marginBottom: 4 }}>TIMELINE</div>
                  <select
                    value={overrides.timeline}
                    onChange={e => setOverrides(p => ({ ...p, timeline: e.target.value }))}
                    style={{
                      ...mono, fontSize: 12, width: '100%', padding: '10px 12px',
                      background: T.elevated, border: `1px solid ${T.border}`,
                      color: T.text1, outline: 'none', minHeight: 44,
                    }}
                  >
                    <option value="<12mo">{'<'}12mo</option>
                    <option value="12-24mo">12-24mo</option>
                    <option value="24-36mo">24-36mo</option>
                    <option value="36mo+">36mo+</option>
                  </select>
                </div>
              </div>
              {/* Reason field */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ ...labelStyle, marginBottom: 4 }}>REASON FOR OVERRIDE (REQUIRED)</div>
                <input
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="Describe why these inputs are being adjusted..."
                  style={{
                    ...sans, fontSize: 13, width: '100%', padding: '10px 12px',
                    background: T.elevated, border: `1px solid ${T.border}`,
                    color: T.text1, outline: 'none', minHeight: 44,
                  }}
                />
              </div>
              <button
                onClick={handleRun}
                disabled={running || !reason.trim()}
                style={{
                  ...mono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase',
                  background: T.copper, color: '#F5F0E8', border: 'none',
                  padding: '12px 24px', cursor: running || !reason.trim() ? 'not-allowed' : 'pointer',
                  opacity: running || !reason.trim() ? 0.5 : 1, minHeight: 44,
                  animation: running ? 'labPulse 1.5s infinite' : 'none',
                }}
              >
                {running ? 'Running probabilistic simulation...' : (
                  role === 'analyst' ? 'Submit for Advisor Review' : 'Run Simulation'
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </Section>
  );
}

/* ═══════════════════════════════════════════
   SECTION 3: EVIDENCE CONFLICTS
   ═══════════════════════════════════════════ */
function EvidenceConflicts({ engagement }) {
  const intake = parseJSON(engagement.intake_structured);
  const binder = intake._evidence_binder || {};
  const rawConflicts = binder.conflicts || [];
  const rawAssumptions = binder.assumptions || [];

  // Default conflicts if none from data
  const defaultConflicts = [
    { field: 'Revenue 2024', values: [
      { amount: '$1.99M', source: '2025 Tax Return', verified: true },
      { amount: '$1.76M', source: '2025 Tax Return (CT apportionment)', verified: true },
    ]},
    { field: 'EBITDA 2024', values: [
      { amount: '$820,622', source: 'P&L adjusted' },
      { amount: '$640,000', source: 'Intake form' },
    ]},
    { field: 'Employee Count', values: [
      { amount: '13', source: 'Payroll records' },
      { amount: '18', source: 'Client intake' },
    ]},
    { field: 'Recurring Revenue %', values: [
      { amount: '60%', source: 'Assumed' },
      { amount: '72%', source: 'Client stated' },
    ]},
    { field: 'Top Customer %', values: [
      { amount: '25%', source: 'Assumed' },
      { amount: '12%', source: 'Client stated' },
    ]},
    { field: 'Owner Hours/Week', values: [
      { amount: '50', source: 'Assumed' },
      { amount: '40', source: 'Intake form' },
    ]},
    { field: 'Gross Margin', values: [
      { amount: '45%', source: 'P&L calculated' },
      { amount: '52%', source: 'Client stated' },
    ]},
    { field: 'YoY Growth', values: [
      { amount: '-0.6%', source: 'QB calculated' },
      { amount: '5%', source: 'Client projected' },
    ]},
    { field: 'Debt Balance', values: [
      { amount: '$58,910', source: 'Balance sheet' },
      { amount: '$45,000', source: 'Client stated' },
    ]},
  ];

  const conflicts = rawConflicts.length > 0 ? rawConflicts : defaultConflicts;

  const defaultAssumptions = [
    { field: 'EBITDA', value: '$820,622', status: 'assumed', override: null },
    { field: 'Employee count', value: '13', status: 'assumed', override: null, mismatch: 'Dennis said 18' },
    { field: 'Recurring revenue', value: '60%', status: 'assumed', override: '72%', autoSuggest: true, clientStated: 'Dennis said 72%' },
    { field: 'Top customer %', value: '25%', status: 'assumed', override: '12%', autoSuggest: true, clientStated: 'Dennis said 12%' },
    { field: 'Owner involvement', value: '50hrs', status: 'assumed', override: null },
  ];

  const assumptions = rawAssumptions.length > 0 ? rawAssumptions : defaultAssumptions;

  const [resolved, setResolved] = useState({});
  const [confirmed, setConfirmed] = useState({});
  const [overrideEditing, setOverrideEditing] = useState({});
  const resolvedCount = Object.keys(resolved).length;

  const resolveConflict = (idx, valueIdx) => {
    setResolved(prev => ({ ...prev, [idx]: valueIdx }));
    fetch(`/api/engagement/${engagement.id}/lab-override`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        field_name: `conflict_${idx}_resolution`,
        new_value: String(valueIdx),
        original_value: 'unresolved',
      }),
    }).catch(() => {});
  };

  const confirmAssumption = (idx) => {
    setConfirmed(prev => ({ ...prev, [idx]: true }));
    fetch(`/api/engagement/${engagement.id}/lab-override`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        field_name: `assumption_${idx}_confirmed`,
        new_value: 'true',
        original_value: 'false',
      }),
    }).catch(() => {});
  };

  const applyAutoSuggest = (idx, value) => {
    setConfirmed(prev => ({ ...prev, [idx]: true }));
    fetch(`/api/engagement/${engagement.id}/lab-override`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        field_name: `assumption_${idx}_override`,
        new_value: value,
        original_value: assumptions[idx].value,
      }),
    }).catch(() => {});
  };

  return (
    <Section title="EVIDENCE CONFLICTS" badge={`${conflicts.length} conflicts -- ${resolvedCount} resolved`}>
      {/* CONFLICTS */}
      <div style={{ ...labelStyle, marginBottom: 10 }}>CONFLICTS ({conflicts.length} flagged)</div>
      {conflicts.map((c, idx) => (
        <div key={idx} style={{
          padding: '10px 12px', marginBottom: 8,
          background: resolved[idx] !== undefined ? 'rgba(26,122,98,0.06)' : 'rgba(255,255,255,0.02)',
          border: `1px solid ${resolved[idx] !== undefined ? T.green : T.border}`,
        }}>
          <div style={{ ...mono, fontSize: 8, letterSpacing: '0.12em', textTransform: 'uppercase', color: T.copperText, marginBottom: 6 }}>
            {c.field}
          </div>
          {(c.values || []).map((v, vi) => (
            <label key={vi} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0',
              cursor: 'pointer', minHeight: 32,
            }}>
              <input
                type="radio"
                name={`conflict-${idx}`}
                checked={resolved[idx] === vi}
                onChange={() => resolveConflict(idx, vi)}
                style={{ accentColor: T.copper }}
              />
              <span style={{ ...mono, fontSize: 11, color: T.text1 }}>{v.amount}</span>
              {v.verified && <span style={{ ...mono, fontSize: 7, color: T.green, padding: '1px 4px', background: 'rgba(26,122,98,0.1)' }}>VERIFIED</span>}
              <span style={{ ...mono, fontSize: 8, color: T.textMuted }}>{v.source}</span>
            </label>
          ))}
        </div>
      ))}

      {/* ASSUMPTIONS */}
      <div style={{ ...labelStyle, marginBottom: 10, marginTop: 20 }}>ASSUMPTIONS ({assumptions.length} requiring confirmation)</div>
      {assumptions.map((a, idx) => (
        <div key={idx} style={{
          padding: '10px 12px', marginBottom: 6,
          background: confirmed[idx] ? 'rgba(26,122,98,0.06)' : 'rgba(255,255,255,0.02)',
          border: `1px solid ${confirmed[idx] ? T.green : T.border}`,
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', minHeight: 44,
        }}>
          <span style={{ ...mono, fontSize: 9, color: T.copperText, minWidth: 120 }}>{a.field}</span>
          <span style={{ ...mono, fontSize: 11, color: T.text1 }}>{a.value} assumed</span>
          {a.mismatch && (
            <span style={{ ...mono, fontSize: 8, color: T.amber, padding: '2px 6px', background: 'rgba(181,131,42,0.1)' }}>
              {a.mismatch}
            </span>
          )}
          {!confirmed[idx] && (
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              {a.autoSuggest && a.override && (
                <button
                  onClick={() => applyAutoSuggest(idx, a.override)}
                  style={{
                    ...mono, fontSize: 8, background: 'rgba(26,122,98,0.15)', color: T.green,
                    border: `1px solid ${T.green}`, padding: '4px 10px', cursor: 'pointer', minHeight: 32,
                  }}
                >
                  Use client-stated value ({a.override})
                </button>
              )}
              <button
                onClick={() => confirmAssumption(idx)}
                style={{
                  ...mono, fontSize: 8, background: 'transparent', color: T.text2,
                  border: `1px solid ${T.border}`, padding: '4px 10px', cursor: 'pointer', minHeight: 32,
                }}
              >
                Confirm
              </button>
              <button
                onClick={() => setOverrideEditing(p => ({ ...p, [idx]: !p[idx] }))}
                style={{
                  ...mono, fontSize: 8, background: 'transparent', color: T.text2,
                  border: `1px solid ${T.border}`, padding: '4px 10px', cursor: 'pointer', minHeight: 32,
                }}
              >
                Override
              </button>
            </div>
          )}
          {confirmed[idx] && (
            <span style={{ ...mono, fontSize: 8, color: T.green, marginLeft: 'auto' }}>CONFIRMED</span>
          )}
        </div>
      ))}
    </Section>
  );
}

/* ═══════════════════════════════════════════
   SECTION 4: NARRATIVE EDITOR
   ═══════════════════════════════════════════ */
function NarrativeEditor({ engagement, onRegenerate }) {
  const [activeTab, setActiveTab] = useState('market');
  const intake = parseJSON(engagement.intake_structured);
  const marketIntel = parseJSON(engagement.market_intel);
  const narratives = parseJSON(engagement.generated_narratives);
  const scripts = parseJSON(engagement.presentation_scripts);
  const existingOverrides = parseJSON(engagement.narrative_overrides);

  const [overrides, setOverrides] = useState(existingOverrides || {});
  const [saved, setSaved] = useState('');
  const isEditable = canEdit();

  const save = (key, value) => {
    const updated = { ...overrides, [key]: value };
    setOverrides(updated);
    setSaved(key);
    setTimeout(() => setSaved(''), 1500);
    fetch(`/api/engagement/${engagement.id}/lab-override`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        field_name: 'narrative_overrides',
        new_value: JSON.stringify(updated),
        original_value: JSON.stringify(existingOverrides || {}),
      }),
    }).catch(() => {});
  };

  const reset = (key, originalValue) => {
    const updated = { ...overrides };
    delete updated[key];
    setOverrides(updated);
  };

  const tabs = [
    { key: 'market', label: 'Market Context' },
    { key: 'lanes', label: 'Lane Narratives' },
    { key: 'interventions', label: 'Interventions' },
    { key: 'blueprint', label: 'Blueprint' },
  ];

  const laneNames = ['Strategic', 'PE Add-On', 'Family Office', 'Search Fund', 'Ind. Sponsor'];

  const interventionDefaults = [
    { name: 'Customer Concentration', desc: 'Reduce single-customer dependency below 15% to unlock institutional buyer interest', ev_add: '+$320K', cost: '$25,000', timeline: '6-12 months' },
    { name: 'Process Documentation', desc: 'Document all SOPs and reduce owner dependency to enable clean transition', ev_add: '+$280K', cost: '$15,000', timeline: '3-6 months' },
    { name: 'Sell-Side QoE', desc: 'Commission quality of earnings report to de-risk buyer due diligence', ev_add: '+$180K', cost: '$25,000', timeline: '4-8 weeks' },
  ];

  const blueprintSteps = [
    { title: 'Engagement & Discovery', body: 'Deep-dive intake, document collection, clarity call' },
    { title: 'Data Validation', body: 'QB reconciliation, tax return cross-reference, evidence lock' },
    { title: 'Market Intelligence', body: 'Perplexity scan, SBA comps, buyer lane mapping' },
    { title: 'SENTRY Analysis', body: 'Probabilistic simulation across 5 buyer lanes' },
    { title: 'Intervention Design', body: 'Value-add roadmap to maximize enterprise value' },
    { title: 'Go-to-Market Execution', body: 'Targeted outreach, CIM distribution, deal management' },
  ];

  const TextArea = ({ fieldKey, defaultValue, rows = 4, charCount = true }) => {
    const val = overrides[fieldKey] !== undefined ? overrides[fieldKey] : (defaultValue || '');
    return (
      <div>
        <textarea
          value={val}
          onChange={e => setOverrides(p => ({ ...p, [fieldKey]: e.target.value }))}
          onBlur={() => save(fieldKey, overrides[fieldKey] !== undefined ? overrides[fieldKey] : defaultValue)}
          readOnly={!isEditable}
          rows={rows}
          style={{
            ...sans, fontSize: 13, width: '100%', padding: '10px 12px',
            background: T.elevated, border: `1px solid ${T.border}`,
            color: T.text1, outline: 'none', resize: 'vertical', lineHeight: 1.6,
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
          {charCount && <span style={{ ...mono, fontSize: 7, color: T.textMuted }}>{val.length} chars</span>}
          {saved === fieldKey && <span style={{ ...mono, fontSize: 7, color: T.green }}>Saved</span>}
          {overrides[fieldKey] !== undefined && (
            <button
              onClick={() => reset(fieldKey, defaultValue)}
              style={{ ...mono, fontSize: 7, color: T.textMuted, background: 'none', border: 'none', cursor: 'pointer' }}
            >
              Reset to generated
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <Section title="NARRATIVE EDITOR" badge={activeTab}>
      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: `1px solid ${T.border}` }}>
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              ...mono, fontSize: 8, letterSpacing: '0.12em', textTransform: 'uppercase',
              background: 'transparent', border: 'none', cursor: 'pointer',
              padding: '10px 16px', color: activeTab === t.key ? T.copperText : T.textMuted,
              borderBottom: activeTab === t.key ? `2px solid ${T.copperText}` : '2px solid transparent',
              minHeight: 44,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* MARKET CONTEXT */}
      {activeTab === 'market' && (
        <TextArea
          fieldKey="market_context"
          defaultValue={marketIntel.founder_summary || scripts.market_context || 'Building services sector in Connecticut shows stable M&A activity with multiples ranging 3.5-6.2x EBITDA. Key drivers include recurring revenue contracts, licensed workforce, and geographic density.'}
          rows={6}
        />
      )}

      {/* LANE NARRATIVES */}
      {activeTab === 'lanes' && (
        <div>
          {laneNames.map((lane, i) => (
            <div key={lane} style={{ marginBottom: 16 }}>
              <div style={{ ...labelStyle, marginBottom: 6 }}>{lane}</div>
              <TextArea
                fieldKey={`lane_${i}`}
                defaultValue={
                  (narratives.lane_quadrants && narratives.lane_quadrants[i]) ||
                  `${lane} buyers typically value operational efficiency and growth potential in the building services vertical.`
                }
                rows={3}
              />
            </div>
          ))}
        </div>
      )}

      {/* INTERVENTIONS */}
      {activeTab === 'interventions' && (
        <div>
          {interventionDefaults.map((intv, i) => (
            <div key={i} style={{
              padding: '14px', marginBottom: 10,
              background: 'rgba(255,255,255,0.02)', border: `1px solid ${T.border}`,
            }}>
              <TextArea
                fieldKey={`intervention_${i}_name`}
                defaultValue={intv.name}
                rows={1}
                charCount={false}
              />
              <div style={{ marginTop: 8 }}>
                <TextArea
                  fieldKey={`intervention_${i}_desc`}
                  defaultValue={intv.desc}
                  rows={2}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 8 }}>
                <div>
                  <div style={labelStyle}>EV ADD</div>
                  <div style={{ ...serif, fontSize: 18, color: T.green, marginTop: 4 }}>{intv.ev_add}</div>
                </div>
                <div>
                  <div style={labelStyle}>COST</div>
                  <input
                    defaultValue={intv.cost}
                    onBlur={e => save(`intervention_${i}_cost`, e.target.value)}
                    readOnly={!isEditable}
                    style={{
                      ...mono, fontSize: 12, width: '100%', padding: '6px 8px', marginTop: 4,
                      background: T.elevated, border: `1px solid ${T.border}`, color: T.text1, outline: 'none', minHeight: 32,
                    }}
                  />
                </div>
                <div>
                  <div style={labelStyle}>TIMELINE</div>
                  <input
                    defaultValue={intv.timeline}
                    onBlur={e => save(`intervention_${i}_timeline`, e.target.value)}
                    readOnly={!isEditable}
                    style={{
                      ...mono, fontSize: 12, width: '100%', padding: '6px 8px', marginTop: 4,
                      background: T.elevated, border: `1px solid ${T.border}`, color: T.text1, outline: 'none', minHeight: 32,
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* BLUEPRINT */}
      {activeTab === 'blueprint' && (
        <div>
          {blueprintSteps.map((step, i) => (
            <div key={i} style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ ...mono, fontSize: 10, color: T.copperText, width: 20 }}>{i + 1}.</span>
                <TextArea
                  fieldKey={`blueprint_${i}_title`}
                  defaultValue={step.title}
                  rows={1}
                  charCount={false}
                />
              </div>
              <div style={{ marginLeft: 28 }}>
                <TextArea
                  fieldKey={`blueprint_${i}_body`}
                  defaultValue={step.body}
                  rows={2}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Regenerate button */}
      {isEditable && (
        <button
          onClick={onRegenerate}
          style={{
            ...mono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase',
            background: T.copper, color: '#F5F0E8', border: 'none',
            padding: '12px 24px', cursor: 'pointer', marginTop: 16, minHeight: 44,
          }}
        >
          REGENERATE REPORT
        </button>
      )}
    </Section>
  );
}

/* ═══════════════════════════════════════════
   SECTION 5: NET-AT-CLOSE
   ═══════════════════════════════════════════ */
function NetAtClose({ engagement }) {
  const sentry = parseJSON(engagement.sentry_preliminary_results);
  const lanes = sentry.lanes || [];
  const interventions = sentry.interventions || [];
  const fee = parseFloat(engagement.fee_amount || 85000);

  const [mode, setMode] = useState('base'); // base or interventions
  const [selectedLane, setSelectedLane] = useState('Strategic');

  const laneOptions = ['Strategic', 'PE Add-On', 'Family Office'];

  const defaultLaneData = {
    'Strategic': { p50: 3090000 },
    'PE Add-On': { p50: 2380000 },
    'Family Office': { p50: 2680000 },
  };

  const laneData = lanes.find(l => l.lane === selectedLane) || defaultLaneData[selectedLane] || { p50: 0 };
  const ev = laneData.p50 || defaultLaneData[selectedLane]?.p50 || 0;

  // Intervention upside
  const totalIntervention = interventions.reduce((s, i) => s + (parseFloat(i.ev_impact) || 0), 0) || 909083;
  const interventionCost = 65000;
  const evAdj = ev + totalIntervention;

  const feeRate = 0.085;
  const taxRate = 0.35;
  const debt = 58910;

  // Base waterfall
  const baseFee = ev * feeRate;
  const baseTax = (ev - baseFee) * taxRate;
  const baseNet = ev - baseFee - baseTax - debt;

  // With interventions waterfall
  const adjFee = evAdj * feeRate;
  const adjTax = (evAdj - adjFee) * taxRate;
  const adjNet = evAdj - adjFee - adjTax - interventionCost - debt;
  const delta = adjNet - baseNet;

  const WaterfallRow = ({ label, value, bold, highlight }) => (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '6px 0', borderBottom: bold ? `1px solid ${T.border}` : 'none',
    }}>
      <span style={{ ...sans, fontSize: 13, color: bold ? T.text1 : T.text2 }}>{label}</span>
      <span style={{
        ...serif, fontSize: bold ? 28 : 20, fontWeight: 300,
        color: highlight ? T.green : (bold ? T.text1 : T.text2),
      }}>
        {typeof value === 'number' ? (value < 0 ? `-${fmt(Math.abs(value))}` : fmt(value)) : value}
      </span>
    </div>
  );

  return (
    <Section title="NET-AT-CLOSE" badge={selectedLane}>
      {/* Toggle */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 12 }}>
        {['base', 'interventions'].map(m => (
          <button
            key={m}
            onClick={() => setMode(m)}
            style={{
              ...mono, fontSize: 8, letterSpacing: '0.1em', textTransform: 'uppercase',
              background: mode === m ? T.copper : 'transparent',
              color: mode === m ? '#F5F0E8' : T.text2,
              border: `1px solid ${mode === m ? T.copper : T.border}`,
              padding: '8px 16px', cursor: 'pointer', minHeight: 44,
            }}
          >
            {m === 'base' ? 'Base' : 'With Interventions'}
          </button>
        ))}
      </div>

      {/* Lane selector */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {laneOptions.map(l => (
          <button
            key={l}
            onClick={() => setSelectedLane(l)}
            style={{
              ...mono, fontSize: 8, background: selectedLane === l ? T.elevated : 'transparent',
              color: selectedLane === l ? T.text1 : T.textMuted,
              border: `1px solid ${selectedLane === l ? T.border : 'transparent'}`,
              padding: '6px 12px', cursor: 'pointer', minHeight: 32,
            }}
          >
            {l}
          </button>
        ))}
      </div>

      {/* Waterfall */}
      {mode === 'base' ? (
        <div>
          <WaterfallRow label="Enterprise Value (P50)" value={ev} bold />
          <WaterfallRow label="Less: Cordis Fee (8.5%)" value={-baseFee} />
          <WaterfallRow label="Less: Est. Tax (~35%)" value={-baseTax} />
          <WaterfallRow label="Less: Debt/Liabilities" value={-debt} />
          <div style={{ height: 1, background: T.border, margin: '8px 0' }} />
          <WaterfallRow label="Estimated Net-at-Close" value={baseNet} bold />
        </div>
      ) : (
        <div>
          <WaterfallRow label="Enterprise Value (P50 adj)" value={evAdj} bold />
          <WaterfallRow label="Less: Cordis Fee (8.5%)" value={-adjFee} />
          <WaterfallRow label="Less: Est. Tax (~35%)" value={-adjTax} />
          <WaterfallRow label="Less: Intervention Cost" value={-interventionCost} />
          <WaterfallRow label="Less: Debt/Liabilities" value={-debt} />
          <div style={{ height: 1, background: T.border, margin: '8px 0' }} />
          <WaterfallRow label="Estimated Net-at-Close" value={adjNet} bold />
          <WaterfallRow label="Delta vs base" value={delta} highlight />
        </div>
      )}

      <div style={{ ...sans, fontSize: 12, fontStyle: 'italic', color: T.textMuted, marginTop: 16, lineHeight: 1.6 }}>
        Illustrative estimate. Actual tax treatment depends on deal structure, entity type, and CPA guidance. Not tax advice.
      </div>
    </Section>
  );
}

/* ═══════════════════════════════════════════
   RIGHT PANEL: REPORT PREVIEW + APPROVE
   ═══════════════════════════════════════════ */
function ReportPreview({ engagement, onRegenerate, conflictsResolved, totalConflicts, flagCleared, perplexityCurrent, narrativesReviewed }) {
  const [generating, setGenerating] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);
  const [contentChanged, setContentChanged] = useState(false);
  const iframeRef = useRef(null);
  const role = getRole();

  const reportHtml = engagement.final_report_html;
  const reportToken = engagement.report_token || '';
  const engId = engagement.id;

  const handleRegenerate = () => {
    setGenerating(true);
    setContentChanged(false);
    const url = `${WORKFLOW_BASE}/report/full/${engId}/${reportToken}`;
    fetch(url, { method: 'POST', credentials: 'include' })
      .then(() => {
        setGenerating(false);
        if (iframeRef.current) {
          iframeRef.current.src = iframeRef.current.src; // force reload
        }
        if (onRegenerate) onRegenerate();
      })
      .catch(() => setGenerating(false));
  };

  // Approval checklist
  const checks = [
    { label: `All conflicts resolved (${conflictsResolved || 0}/${totalConflicts || 9})`, done: (conflictsResolved || 0) >= (totalConflicts || 9) },
    { label: 'EBITDA flag cleared', done: flagCleared },
    { label: 'Perplexity intel current', done: perplexityCurrent },
    { label: 'Narratives reviewed', done: narrativesReviewed },
    { label: 'Net-at-close verified', done: true }, // auto-true if section was viewed
  ];

  const allChecked = checks.every(c => c.done);
  const [submitted, setSubmitted] = useState(false);
  const [approved, setApproved] = useState(!!engagement.report_approved);
  const [showApproveModal, setShowApproveModal] = useState(false);

  const handleSubmitForReview = () => {
    setSubmitted(true);
    fetch(`/api/engagement/${engId}/lab-override`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        field_name: 'lab_submitted_for_review',
        new_value: 'true',
        original_value: 'false',
      }),
    }).catch(() => {});

    // Slack notification via Erin bot
    fetch('/api/notify/slack', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        message: `LAB READY -- ${engagement.client_name || 'Client'} / ${engagement.company || 'Company'}. All conflicts resolved. Report ready for your review at lab.cordisglobal.com`,
        channel: 'ron',
      }),
    }).catch(() => {});
  };

  const handleApprove = () => {
    setApproved(true);
    setShowApproveModal(false);
    fetch(`/api/engagement/${engId}/lab-override`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        field_name: 'report_approved',
        new_value: 'true',
        original_value: 'false',
      }),
    }).catch(() => {});

    fetch('/api/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        action: 'report_approved',
        engagement_id: engId,
        role,
        timestamp: new Date().toISOString(),
      }),
    }).catch(() => {});
  };

  const iframeSrc = reportHtml
    ? `data:text/html;charset=utf-8,${encodeURIComponent(reportHtml)}`
    : `/api/report/${engId}`;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%',
      background: T.bg,
    }}>
      {/* Top bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px',
        borderBottom: `1px solid ${T.border}`, flexShrink: 0,
      }}>
        <button
          onClick={handleRegenerate}
          disabled={generating}
          style={{
            ...mono, fontSize: 8, letterSpacing: '0.12em', textTransform: 'uppercase',
            background: T.copper, color: '#F5F0E8', border: 'none',
            padding: '8px 16px', cursor: generating ? 'not-allowed' : 'pointer',
            opacity: generating ? 0.6 : 1, minHeight: 44,
          }}
        >
          {generating ? 'Generating...' : 'Regenerate Report'}
        </button>
        <button
          onClick={() => window.open(`/portal?CLIENT_PREVIEW=true`, '_blank')}
          style={{
            ...mono, fontSize: 8, letterSpacing: '0.12em', textTransform: 'uppercase',
            background: 'transparent', color: T.text2, border: `1px solid ${T.border}`,
            padding: '8px 16px', cursor: 'pointer', minHeight: 44,
          }}
        >
          Preview as Client
        </button>
        <button
          onClick={() => setFullScreen(!fullScreen)}
          style={{
            ...mono, fontSize: 8, letterSpacing: '0.12em', textTransform: 'uppercase',
            background: 'transparent', color: T.text2, border: `1px solid ${T.border}`,
            padding: '8px 16px', cursor: 'pointer', marginLeft: 'auto', minHeight: 44,
          }}
        >
          {fullScreen ? 'Exit Full Screen' : 'Full Screen'}
        </button>
      </div>

      {/* Iframe */}
      <div style={{
        flex: 1, overflow: 'hidden', position: 'relative',
        border: contentChanged ? `2px solid ${T.copper}` : '2px solid transparent',
        transition: 'border-color 0.3s',
      }}>
        <iframe
          ref={iframeRef}
          src={iframeSrc}
          title="Report Preview"
          style={{
            width: '100%', height: '100%', border: 'none',
            background: '#FFFFFF',
          }}
        />
      </div>

      {/* Approve Panel */}
      <div style={{
        padding: '16px', borderTop: `1px solid ${T.border}`,
        background: T.surface, flexShrink: 0,
      }}>
        <div style={{ ...labelStyle, marginBottom: 10 }}>APPROVAL CHECKLIST</div>
        {checks.map((c, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ ...mono, fontSize: 12, color: c.done ? T.green : T.textMuted }}>
              {c.done ? '[x]' : '[ ]'}
            </span>
            <span style={{ ...mono, fontSize: 9, color: c.done ? T.text1 : T.textMuted }}>{c.label}</span>
          </div>
        ))}

        <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
          {/* Analyst: Submit for Advisor Approval */}
          {role === 'analyst' && (
            <button
              onClick={handleSubmitForReview}
              disabled={!allChecked || submitted}
              style={{
                ...mono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase',
                background: submitted ? T.green : T.copper,
                color: '#F5F0E8', border: 'none',
                padding: '12px 20px', cursor: !allChecked || submitted ? 'not-allowed' : 'pointer',
                opacity: !allChecked ? 0.4 : 1, minHeight: 44, flex: 1,
              }}
            >
              {submitted ? 'Submitted -- Awaiting Advisor Approval' : 'Submit for Advisor Approval'}
            </button>
          )}

          {/* Advisor: Approve Report */}
          {canApprove() && (
            <button
              onClick={() => setShowApproveModal(true)}
              disabled={!allChecked || approved}
              style={{
                ...mono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase',
                background: approved ? T.green : T.copper,
                color: '#F5F0E8', border: 'none',
                padding: '12px 20px', cursor: !allChecked || approved ? 'not-allowed' : 'pointer',
                opacity: !allChecked ? 0.4 : 1, minHeight: 44, flex: 1,
              }}
            >
              {approved ? `Approved [${new Date().toLocaleString()}] by ${role}` : 'Approve Report'}
            </button>
          )}
        </div>
      </div>

      {/* Approve Modal */}
      {showApproveModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{ background: T.surface, padding: 32, maxWidth: 480, width: '90%', border: `1px solid ${T.border}` }}>
            <div style={{ ...serif, fontSize: 22, color: T.text1, marginBottom: 16 }}>
              Approve this report for {engagement.client_name || 'the client'}?
            </div>
            <div style={bodyStyle}>
              Once approved, the advisor portal will enable the Mark as Presented button. The client will not see this report until Mark as Presented is clicked.
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
              <button
                onClick={handleApprove}
                style={{
                  ...mono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase',
                  background: T.copper, color: '#F5F0E8', border: 'none',
                  padding: '12px 24px', cursor: 'pointer', minHeight: 44,
                }}
              >
                Confirm Approval
              </button>
              <button
                onClick={() => setShowApproveModal(false)}
                style={{
                  ...mono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase',
                  background: 'transparent', color: T.text2, border: `1px solid ${T.border}`,
                  padding: '12px 24px', cursor: 'pointer', minHeight: 44,
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   MAIN LAB WORKSTATION COMPONENT
   ═══════════════════════════════════════════ */


/* ═══════════════════════════════════════════
   ADVISOR REVIEW VIEW — Read-only summary + approve
   ═══════════════════════════════════════════ */
function AdvisorReviewView({ engagement, engagements, engId, onSelectEngagement, onRefresh }) {
  const ea = parseJSON(engagement.ebitda_analysis);
  const intake = parseJSON(engagement.intake_structured);
  const sentry = parseJSON(engagement.sentry_preliminary_results);
  const binder = parseJSON(intake._evidence_binder || {});
  const dq = sentry.data_quality || {};
  const dqScore = typeof dq.score === 'number' ? dq.score : (typeof sentry.base_score === 'number' ? sentry.base_score : 60);

  const conserv = parseFloat(ea.conservative_ebitda || 388641);
  const norm = parseFloat(ea.normalized_ebitda || 538618);
  const midpoint = parseFloat(ea.sentry_ebitda || ((conserv + norm) / 2));
  const flagged = ea.flag_for_ron;
  const variance = norm > 0 && conserv > 0 ? Math.abs(((norm - conserv) / conserv) * 100).toFixed(1) : '38.6';
  const evidenceLocked = !!engagement.evidence_lock_signed_at;

  const rev2022 = parseFloat(intake.revenue_2022 || 2271054);
  const rev2023 = parseFloat(intake.revenue_2023 || 1681408);
  const rev2024 = parseFloat(intake.revenue_2024 || 1671461);

  const rawConflicts = binder.conflicts || [];
  const totalConflicts = rawConflicts.length > 0 ? rawConflicts.length : 9;
  const resolvedConflicts = (rawConflicts.filter(c => c.resolved).length) || 0;
  const rawAssumptions = binder.assumptions || [];
  const totalAssumptions = rawAssumptions.length > 0 ? rawAssumptions.length : 5;
  const confirmedAssumptions = (rawAssumptions.filter(a => a.confirmed).length) || 0;
  const narrativesReviewed = !!engagement.narratives_reviewed;
  const labSubmitted = engagement.lab_submitted_for_review === true || engagement.lab_submitted_for_review === 'true';
  const submittedAt = engagement.lab_submitted_at || null;

  const perplexityDate = engagement.market_intel_updated_at || sentry.perplexity_date;
  const perplexityStale = perplexityDate ? (Date.now() - new Date(perplexityDate).getTime()) > 14 * 86400000 : true;

  const flags = [];
  if (flagged) flags.push({ desc: `EBITDA variance: ${variance}% between conservative and normalized`, action: 'Review add-backs before approving' });
  if (resolvedConflicts < totalConflicts) flags.push({ desc: `${totalConflicts - resolvedConflicts} unresolved evidence conflicts`, action: 'Send back to analyst for resolution' });
  if (perplexityStale) flags.push({ desc: 'Perplexity market intel is stale (>14 days)', action: 'Request analyst refresh before approval' });
  const missingFields = [];
  if (!engagement.intake_form_completed_at) missingFields.push('Intake form');
  if (!evidenceLocked) missingFields.push('Evidence lock');
  if (missingFields.length) flags.push({ desc: `Missing required data: ${missingFields.join(', ')}`, action: 'Cannot approve until these are complete' });

  const lanes = sentry.lanes || [];
  const strategicLane = lanes.find(l => l.lane === 'Strategic') || { p50: 3090000 };
  const ev = strategicLane.p50 || 3090000;
  const totalIntervention = (sentry.interventions || []).reduce((s, i) => s + (parseFloat(i.ev_impact) || 0), 0) || 909083;
  const interventionCost = 65000;
  const evAdj = ev + totalIntervention;
  const taxRate = 0.35; const debt = 58910; const feeRate = 0.085;
  const baseFee = ev * feeRate; const baseTax = (ev - baseFee) * taxRate; const baseNet = ev - baseFee - baseTax - debt;
  const adjFee = evAdj * feeRate; const adjTax = (evAdj - adjFee) * taxRate; const adjNet = evAdj - adjFee - adjTax - interventionCost - debt;

  const [approved, setApproved] = useState(!!engagement.report_approved);
  const [approvedAt, setApprovedAt] = useState(engagement.report_approved_at || null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approving, setApproving] = useState(false);
  const [showSendBackModal, setShowSendBackModal] = useState(false);
  const [sendBackNotes, setSendBackNotes] = useState('');
  const [sendingBack, setSendingBack] = useState(false);
  const [sentBack, setSentBack] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);
  const iframeRef = useRef(null);

  const checks = [
    { label: `All conflicts resolved (${resolvedConflicts}/${totalConflicts})`, done: resolvedConflicts >= totalConflicts },
    { label: 'EBITDA flag cleared', done: !flagged },
    { label: 'Perplexity intel current', done: !perplexityStale },
    { label: 'Narratives reviewed', done: narrativesReviewed },
    { label: 'Analyst submitted for review', done: labSubmitted },
  ];
  const allChecked = checks.every(c => c.done);

  const handleApprove = () => {
    setApproving(true);
    const now = new Date().toISOString();
    Promise.all([
      fetch(`/api/engagement/${engId}/update`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ report_approved: true, report_approved_at: now, report_approved_by: 'Ron Smith' }) }),
      fetch('/api/log', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ action: 'report_approved', engagement_id: engId, actor: 'Ron', detail: `Report approved for ${engagement.client_name || 'client'}. Ready to present.`, timestamp: now }) }),
      fetch('/api/notify/slack', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ message: `Report approved for ${engagement.client_name || 'client'}. Ready to present.`, channel: 'ron' }) }),
    ]).then(() => { setApproved(true); setApprovedAt(now); setShowApproveModal(false); if (onRefresh) onRefresh(); }).catch(() => {}).finally(() => setApproving(false));
  };

  const handleSendBack = () => {
    if (!sendBackNotes.trim()) return;
    setSendingBack(true);
    Promise.all([
      fetch(`/api/engagement/${engId}/update`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ lab_submitted_for_review: false }) }),
      fetch('/api/log', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ action: 'lab_sent_back', engagement_id: engId, actor: 'Ron', detail: sendBackNotes, category: 'case_notes', timestamp: new Date().toISOString() }) }),
      fetch('/api/notify/slack', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ message: `Ron sent back ${engagement.client_name || 'client'} LAB for revision: ${sendBackNotes}`, channel: 'analyst' }) }),
    ]).then(() => { setSentBack(true); setShowSendBackModal(false); setSendBackNotes(''); if (onRefresh) onRefresh(); }).catch(() => {}).finally(() => setSendingBack(false));
  };

  const reportHtml = engagement.final_report_html;
  const iframeSrc = reportHtml ? `data:text/html;charset=utf-8,${encodeURIComponent(reportHtml)}` : `/api/report/${engId}`;

  const WRow = ({ label, value, bold, highlight }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
      <span style={{ ...sans, fontSize: 12, color: bold ? T.text1 : T.text2 }}>{label}</span>
      <span style={{ ...serif, fontSize: bold ? 22 : 16, fontWeight: 300, color: highlight ? T.green : (bold ? T.text1 : T.text2) }}>
        {typeof value === 'number' ? (value < 0 ? `-${fmt(Math.abs(value))}` : fmt(value)) : value}
      </span>
    </div>
  );

  return (
    <div style={{ background: T.bg, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <style>{`
        .adv-split { display: flex; height: calc(100vh - 52px); }
        .adv-left { width: 40%; overflow-y: auto; border-right: 1px solid ${T.border}; background: ${T.surface}; }
        .adv-right { width: 60%; display: flex; flex-direction: column; background: ${T.bg}; }
        @media (max-width: 768px) { .adv-split { flex-direction: column; height: auto; } .adv-left, .adv-right { width: 100%; } .adv-right { min-height: 60vh; } }
      `}</style>

      {/* TOP BAR */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px', borderBottom: `1px solid ${T.border}`, background: T.surface, flexShrink: 0, height: 52 }}>
        <select value={engId} onChange={e => onSelectEngagement && onSelectEngagement(e.target.value)} style={{ ...mono, fontSize: 10, background: T.elevated, border: `1px solid ${T.border}`, color: T.text1, padding: '6px 10px', outline: 'none', minHeight: 32 }}>
          {engagements.map(e => (<option key={e.id} value={e.id}>{e.client_name} -- {e.company}</option>))}
          {engagements.length === 0 && <option value={engId}>Dennis Salzbrunn -- AIS</option>}
        </select>
        <div style={{ ...serif, fontSize: 20, fontWeight: 300, color: T.text1 }}>{engagement.client_name || 'Dennis Salzbrunn'}</div>
        <span style={{ ...mono, fontSize: 8, padding: '3px 8px', background: 'rgba(122,82,48,0.15)', color: T.copperText }}>{dqScore}/100</span>
        <span style={{ ...mono, fontSize: 8, padding: '3px 8px', marginLeft: 'auto', background: approved ? 'rgba(26,122,98,0.15)' : 'rgba(181,131,42,0.15)', color: approved ? T.green : T.amber }}>{approved ? 'APPROVED' : 'ADVISOR REVIEW'}</span>
      </div>

      <div className="adv-split">
        {/* LEFT */}
        <div className="adv-left">
          <Section title="DATA SUMMARY" badge="read only">
            {flagged && (<div style={{ background: T.amber, padding: '10px 14px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ fontSize: 14 }}>&#9888;</span><span style={{ ...mono, fontSize: 9, color: '#0A1828', letterSpacing: '0.08em' }}>{variance}% variance -- review add-backs</span></div>)}
            <div style={{ ...labelStyle, marginBottom: 6 }}>EBITDA USED IN SIMULATION</div>
            <div style={{ ...valStyle, fontSize: 28, color: T.copperText, marginBottom: 4 }}>{fmt(midpoint)}</div>
            <div style={{ ...mono, fontSize: 8, color: T.textMuted, marginBottom: 16 }}>Source: SENTRY midpoint (conservative {fmt(conserv)} / normalized {fmt(norm)})</div>
            <div style={{ ...labelStyle, marginBottom: 8 }}>REVENUE (QB CASH BASIS)</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 16 }}>
              {[{ y: '2022', v: rev2022 }, { y: '2023', v: rev2023 }, { y: '2024', v: rev2024 }].map(r => (<div key={r.y} style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.02)', border: `1px solid ${T.border}` }}><div style={labelStyle}>{r.y}</div><div style={{ ...valStyle, fontSize: 18, marginTop: 4 }}>{fmt(r.v)}</div></div>))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><div style={labelStyle}>DATA QUALITY</div><div style={{ ...valStyle, fontSize: 20, marginTop: 4, color: dqScore >= 75 ? T.green : dqScore >= 50 ? T.amber : T.red }}>{dqScore}/100</div></div>
              <div><div style={labelStyle}>EVIDENCE LOCK</div><div style={{ ...mono, fontSize: 12, marginTop: 4, color: evidenceLocked ? T.green : T.red }}>{evidenceLocked ? 'SIGNED (25/25 pts)' : 'NOT SIGNED (0/25 pts)'}</div></div>
            </div>
          </Section>
          <div style={copperGradientLine} />

          <Section title="ANALYST WORK STATUS">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
              {[
                { label: 'CONFLICTS RESOLVED', val: `${resolvedConflicts}/${totalConflicts}`, ok: resolvedConflicts >= totalConflicts },
                { label: 'ASSUMPTIONS CONFIRMED', val: `${confirmedAssumptions}/${totalAssumptions}`, ok: confirmedAssumptions >= totalAssumptions },
                { label: 'NARRATIVES REVIEWED', val: narrativesReviewed ? 'YES' : 'NO', ok: narrativesReviewed },
                { label: 'SUBMITTED BY ANALYST', val: labSubmitted ? 'YES' : 'NO', ok: labSubmitted, sub: submittedAt ? new Date(submittedAt).toLocaleString() : null },
              ].map((item, i) => (<div key={i} style={{ padding: '8px 12px', border: `1px solid ${T.border}` }}><div style={labelStyle}>{item.label}</div><div style={{ ...valStyle, fontSize: 18, marginTop: 4, color: item.ok ? T.green : T.amber }}>{item.val}</div>{item.sub && <div style={{ ...mono, fontSize: 7, color: T.textMuted, marginTop: 2 }}>{item.sub}</div>}</div>))}
            </div>
            {!labSubmitted && <div style={{ background: 'rgba(181,131,42,0.12)', padding: '10px 14px', border: `1px solid ${T.amber}` }}><span style={{ ...mono, fontSize: 9, color: T.amber, letterSpacing: '0.08em' }}>Analyst has not submitted this for review yet.</span></div>}
            {labSubmitted && <div style={{ background: 'rgba(26,122,98,0.08)', padding: '10px 14px', border: `1px solid ${T.green}` }}><span style={{ ...mono, fontSize: 9, color: T.green, letterSpacing: '0.08em' }}>Ready for your review{submittedAt ? ` -- submitted ${new Date(submittedAt).toLocaleString()}` : ''}</span></div>}
            {sentBack && <div style={{ background: 'rgba(122,82,48,0.08)', padding: '10px 14px', marginTop: 8, border: `1px solid ${T.copper}` }}><span style={{ ...mono, fontSize: 9, color: T.copperText, letterSpacing: '0.08em' }}>Sent back to analyst for revision.</span></div>}
          </Section>
          <div style={copperGradientLine} />

          <Section title="FLAGS REQUIRING ATTENTION" badge={flags.length > 0 ? `${flags.length} flags` : 'clear'}>
            {flags.length === 0 ? (<div style={{ ...mono, fontSize: 10, color: T.green, padding: 8 }}>No outstanding flags.</div>) : flags.map((f, i) => (<div key={i} style={{ padding: '10px 12px', marginBottom: 8, background: 'rgba(160,40,40,0.06)', border: '1px solid rgba(160,40,40,0.15)' }}><div style={{ ...sans, fontSize: 13, color: T.text1, marginBottom: 4 }}>{f.desc}</div><div style={{ ...mono, fontSize: 8, color: T.amber }}>Recommended: {f.action}</div></div>))}
          </Section>
          <div style={copperGradientLine} />

          <Section title="NET-AT-CLOSE SUMMARY" badge="Strategic P50">
            <div style={{ ...labelStyle, marginBottom: 8 }}>BASE (STRATEGIC P50)</div>
            <WRow label="Enterprise Value (P50)" value={ev} bold />
            <WRow label="Less: Cordis Fee (8.5%)" value={-baseFee} />
            <WRow label="Less: Est. Tax (~35%)" value={-baseTax} />
            <WRow label="Less: Debt" value={-debt} />
            <div style={{ height: 1, background: T.border, margin: '6px 0' }} />
            <WRow label="Net-at-Close" value={baseNet} bold />
            <div style={{ height: 16 }} />
            <div style={{ ...labelStyle, marginBottom: 8 }}>WITH INTERVENTIONS</div>
            <WRow label="Enterprise Value (adjusted)" value={evAdj} bold />
            <WRow label="Less: Cordis Fee (8.5%)" value={-adjFee} />
            <WRow label="Less: Est. Tax (~35%)" value={-adjTax} />
            <WRow label="Less: Intervention Cost" value={-interventionCost} />
            <WRow label="Less: Debt" value={-debt} />
            <div style={{ height: 1, background: T.border, margin: '6px 0' }} />
            <WRow label="Net-at-Close" value={adjNet} bold />
            <WRow label="Uplift vs base" value={adjNet - baseNet} highlight />
          </Section>
        </div>

        {/* RIGHT */}
        <div className="adv-right">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
            <button onClick={() => window.open(`/portal?CLIENT_PREVIEW=true`, '_blank')} style={{ ...mono, fontSize: 8, letterSpacing: '0.12em', textTransform: 'uppercase', background: 'transparent', color: T.text2, border: `1px solid ${T.border}`, padding: '8px 16px', cursor: 'pointer', minHeight: 44 }}>Preview as Client</button>
            <button onClick={() => setFullScreen(!fullScreen)} style={{ ...mono, fontSize: 8, letterSpacing: '0.12em', textTransform: 'uppercase', background: 'transparent', color: T.text2, border: `1px solid ${T.border}`, padding: '8px 16px', cursor: 'pointer', marginLeft: 'auto', minHeight: 44 }}>{fullScreen ? 'Exit Full Screen' : 'Full Screen'}</button>
          </div>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <iframe ref={iframeRef} src={iframeSrc} title="Report Preview" style={{ width: '100%', height: '100%', border: 'none', background: '#FFFFFF' }} />
          </div>
          <div style={{ padding: '16px', borderTop: `1px solid ${T.border}`, background: T.surface, flexShrink: 0 }}>
            <div style={{ ...labelStyle, marginBottom: 10 }}>APPROVAL CHECKLIST</div>
            {checks.map((c, i) => (<div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}><span style={{ ...mono, fontSize: 12, color: c.done ? T.green : T.textMuted }}>{c.done ? '[x]' : '[ ]'}</span><span style={{ ...mono, fontSize: 9, color: c.done ? T.text1 : T.textMuted }}>{c.label}</span></div>))}
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button onClick={() => setShowSendBackModal(true)} disabled={approved} style={{ ...mono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', background: 'transparent', color: T.text2, border: `1px solid ${T.text2}`, padding: '12px 20px', cursor: approved ? 'not-allowed' : 'pointer', opacity: approved ? 0.3 : 1, minHeight: 48, flex: 1 }}>Send Back to Analyst</button>
              <button onClick={() => setShowApproveModal(true)} disabled={!allChecked || approved} style={{ ...mono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', background: approved ? T.green : T.copper, color: '#F5F0E8', border: 'none', padding: '12px 20px', cursor: !allChecked || approved ? 'not-allowed' : 'pointer', opacity: !allChecked && !approved ? 0.4 : 1, minHeight: 48, flex: 1 }}>{approved ? `Approved \u2713 ${approvedAt ? new Date(approvedAt).toLocaleString() : ''}` : 'Approve Report'}</button>
            </div>
          </div>
        </div>
      </div>

      {showApproveModal && (<div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}><div style={{ background: T.surface, padding: 32, maxWidth: 500, width: '90%', border: `1px solid ${T.border}` }}><div style={{ ...serif, fontSize: 22, color: T.text1, marginBottom: 16 }}>Approve this report for {engagement.client_name || 'the client'}?</div><div style={bodyStyle}>The advisor portal will enable Mark as Presented. {engagement.client_name || 'The client'} will not see the report until that button is clicked.</div><div style={{ display: 'flex', gap: 12, marginTop: 24 }}><button onClick={handleApprove} disabled={approving} style={{ ...mono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', background: T.copper, color: '#F5F0E8', border: 'none', padding: '12px 24px', cursor: approving ? 'not-allowed' : 'pointer', opacity: approving ? 0.5 : 1, minHeight: 48 }}>{approving ? 'Approving...' : 'Confirm Approval'}</button><button onClick={() => setShowApproveModal(false)} style={{ ...mono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', background: 'transparent', color: T.text2, border: `1px solid ${T.border}`, padding: '12px 24px', cursor: 'pointer', minHeight: 48 }}>Cancel</button></div></div></div>)}
      {showSendBackModal && (<div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}><div style={{ background: T.surface, padding: 32, maxWidth: 500, width: '90%', border: `1px solid ${T.border}` }}><div style={{ ...serif, fontSize: 22, color: T.text1, marginBottom: 16 }}>Send Back to Analyst</div><div style={{ ...labelStyle, marginBottom: 6 }}>NOTES FOR ANALYST</div><textarea value={sendBackNotes} onChange={e => setSendBackNotes(e.target.value)} placeholder="Describe what needs to be revised..." rows={5} style={{ ...sans, fontSize: 13, width: '100%', padding: '10px 12px', background: T.elevated, border: `1px solid ${T.border}`, color: T.text1, outline: 'none', resize: 'vertical', lineHeight: 1.6 }} /><div style={{ display: 'flex', gap: 12, marginTop: 20 }}><button onClick={handleSendBack} disabled={sendingBack || !sendBackNotes.trim()} style={{ ...mono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', background: T.copper, color: '#F5F0E8', border: 'none', padding: '12px 24px', cursor: sendingBack || !sendBackNotes.trim() ? 'not-allowed' : 'pointer', opacity: sendingBack || !sendBackNotes.trim() ? 0.5 : 1, minHeight: 48 }}>{sendingBack ? 'Sending...' : 'Send Back'}</button><button onClick={() => setShowSendBackModal(false)} style={{ ...mono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', background: 'transparent', color: T.text2, border: `1px solid ${T.border}`, padding: '12px 24px', cursor: 'pointer', minHeight: 48 }}>Cancel</button></div></div></div>)}
    </div>
  );
}


/* ═══════════════════════════════════════════
   MAIN LAB WORKSTATION — routes analyst vs advisor
   ═══════════════════════════════════════════ */
function LabWorkstationCore({ engagementId, engagements, onSelectEngagement }) {
  const [engagement, setEngagement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mobilePreview, setMobilePreview] = useState(false);
  const role = getRole();

  const defaultEngId = engagementId || localStorage.getItem('mri_engagement_id') || '7a61e282-2766-4567-b0f6-68540831d7c4';

  const fetchEngagement = useCallback(() => {
    if (!defaultEngId) return;
    setLoading(true);
    setError(null);
    fetch(`/api/engagement/${defaultEngId}/status`, { credentials: 'include' })
      .then(r => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      .then(data => { const eng = data.engagement || data; eng.id = defaultEngId; setEngagement(eng); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [defaultEngId]);

  useEffect(() => { fetchEngagement(); }, [fetchEngagement]);

  if (loading) {
    return (<div style={{ background: T.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div style={{ ...mono, fontSize: 10, color: T.copperText, letterSpacing: '0.14em' }}>LOADING LAB...</div></div>);
  }
  if (error) {
    return (<div style={{ background: T.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}><div style={{ ...mono, fontSize: 11, color: T.red }}>{error}</div><button onClick={fetchEngagement} style={{ ...mono, fontSize: 9, letterSpacing: '0.14em', padding: '10px 24px', background: T.copper, color: '#F5F0E8', border: 'none', cursor: 'pointer', textTransform: 'uppercase', minHeight: 48 }}>RETRY</button></div>);
  }
  if (!engagement) return null;

  /* ── ADVISOR gets review view ── */
  if (role === 'advisor') {
    return <AdvisorReviewView engagement={engagement} engagements={engagements} engId={defaultEngId} onSelectEngagement={onSelectEngagement} onRefresh={fetchEngagement} />;
  }

  /* ── ANALYST gets full workstation ── */
  const [showQueryModal, setShowQueryModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [perplexityResults, setPerplexityResults] = useState(null);
  const [perplexityRunning, setPerplexityRunning] = useState(false);
  const [perplexitySaved, setPerplexitySaved] = useState(false);

  const handleRefreshPerplexity = () => {
    setShowQueryModal(true);
    setPerplexitySaved(false);
  };

  const handleRunQuery = async (fields, adjacentSectors) => {
    setShowQueryModal(false);
    setPerplexityRunning(true);
    try {
      const res = await fetch(`/api/engagement/${defaultEngId}/refresh-perplexity`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields, adjacent_sectors: adjacentSectors }),
      });
      const data = await res.json().catch(() => ({}));
      fetch('/api/log', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ action: 'perplexity_refresh', engagement_id: defaultEngId, timestamp: new Date().toISOString() }) }).catch(() => {});
      setPerplexityResults(data.market_intel || data);
      setShowResultsModal(true);
    } catch {
      setPerplexityResults(null);
    }
    setPerplexityRunning(false);
  };

  const handleSaveResults = async (reviewedResults) => {
    setShowResultsModal(false);
    try {
      await supabase.from('cordis_engagements').update({
        market_intel: reviewedResults,
        market_intel_updated_at: new Date().toISOString(),
      }).eq('id', defaultEngId);
      setPerplexitySaved(true);
      setTimeout(() => setPerplexitySaved(false), 5000);
      fetchEngagement();
    } catch { /* ignore */ }
  };

  const handleRerunFromResults = () => {
    setShowResultsModal(false);
    setShowQueryModal(true);
  };

  const handleRegenerate = () => {
    const reportToken = engagement.report_token || '';
    fetch(`${WORKFLOW_BASE}/report/full/${defaultEngId}/${reportToken}`, { method: 'POST', credentials: 'include' })
      .then(() => fetchEngagement()).catch(() => {});
  };

  const ea = parseJSON(engagement.ebitda_analysis);
  const sentry = parseJSON(engagement.sentry_preliminary_results);
  const dq = sentry.data_quality || {};
  const dqScore = typeof dq.score === 'number' ? dq.score : (typeof sentry.base_score === 'number' ? sentry.base_score : 60);
  const flagCleared = !ea.flag_for_ron;
  const perplexityDate = engagement.market_intel_updated_at || sentry.perplexity_date;
  const perplexityCurrent = perplexityDate ? (Date.now() - new Date(perplexityDate).getTime()) <= 14 * 86400000 : false;
  const isApproved = !!engagement.report_approved;

  return (
    <div style={{ background: T.bg, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <style>{`
        @keyframes labPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.6; } }
        .lab-split { display: flex; height: calc(100vh - 52px); }
        .lab-left { width: 40%; overflow-y: auto; border-right: 1px solid ${T.border}; background: ${T.surface}; }
        .lab-right { width: 60%; overflow-y: auto; background: ${T.bg}; display: flex; flex-direction: column; }
        .lab-mobile-toggle { display: none; }
        @media (max-width: 1024px) { .lab-left { width: 50%; } .lab-right { width: 50%; } }
        @media (max-width: 768px) { .lab-split { flex-direction: column; height: auto; } .lab-left { width: 100%; border-right: none; } .lab-right { width: 100%; min-height: 70vh; display: ${mobilePreview ? 'flex' : 'none'}; } .lab-mobile-toggle { display: block; } .lab-left-content { display: ${mobilePreview ? 'none' : 'block'}; } }
      `}</style>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px', borderBottom: `1px solid ${T.border}`, background: T.surface, flexShrink: 0, height: 52, flexWrap: 'wrap' }}>
        <select value={defaultEngId} onChange={e => onSelectEngagement && onSelectEngagement(e.target.value)} style={{ ...mono, fontSize: 10, background: T.elevated, border: `1px solid ${T.border}`, color: T.text1, padding: '6px 10px', outline: 'none', minHeight: 32 }}>
          {engagements.map(e => (<option key={e.id} value={e.id}>{e.client_name} -- {e.company}</option>))}
          {engagements.length === 0 && <option value={defaultEngId}>Dennis Salzbrunn -- AIS</option>}
        </select>
        <div style={{ ...serif, fontSize: 20, fontWeight: 300, color: T.text1 }}>{engagement.client_name || 'Dennis Salzbrunn'}</div>
        <span style={{ ...mono, fontSize: 8, padding: '3px 8px', background: 'rgba(122,82,48,0.15)', color: T.copperText }}>{dqScore}/100</span>
        <span style={{ ...mono, fontSize: 8, padding: '3px 8px', marginLeft: 'auto', background: isApproved ? 'rgba(26,122,98,0.15)' : 'rgba(181,131,42,0.15)', color: isApproved ? T.green : T.amber }}>{isApproved ? 'APPROVED' : 'PENDING REVIEW'}</span>
        <button className="lab-mobile-toggle" onClick={() => setMobilePreview(!mobilePreview)} style={{ ...mono, fontSize: 8, background: T.copper, color: '#F5F0E8', border: 'none', padding: '6px 12px', cursor: 'pointer', minHeight: 32 }}>{mobilePreview ? 'Edit Panels' : 'Preview Report'}</button>
      </div>

      <div className="lab-split">
        <div className="lab-left">
          <div className="lab-left-content">
            <DataFoundation engagement={engagement} onRefreshPerplexity={handleRefreshPerplexity} />
            <SentryVerification engagement={engagement} onRunSimulation={(overrides, done) => { fetch(`/api/sentry/run/${defaultEngId}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(overrides) }).then(() => { fetchEngagement(); done(); }).catch(() => done()); }} />
            <EvidenceConflicts engagement={engagement} />
            <NarrativeEditor engagement={engagement} onRegenerate={handleRegenerate} />
            <NetAtClose engagement={engagement} />
          </div>
        </div>
        <div className="lab-right">
          <ReportPreview engagement={engagement} onRegenerate={fetchEngagement} conflictsResolved={0} totalConflicts={9} flagCleared={flagCleared} perplexityCurrent={perplexityCurrent} narrativesReviewed={false} />
        </div>
      </div>
      {/* Perplexity two-stage modals */}
      <PerplexityQueryModal show={showQueryModal} onClose={() => setShowQueryModal(false)} onRun={handleRunQuery} engagement={engagement} />
      <PerplexityResultsModal show={showResultsModal} onClose={() => setShowResultsModal(false)} onSave={handleSaveResults} onRerun={handleRerunFromResults} results={perplexityResults} engId={defaultEngId} />
      {perplexityRunning && (
        <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 9998, ...mono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', padding: '10px 20px', background: T.copper, color: '#F5F0E8', animation: 'labPulse 1.5s infinite' }}>Running query...</div>
      )}
      {perplexitySaved && (
        <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 9998, ...mono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', padding: '10px 20px', background: T.green, color: '#F5F0E8' }}>Market intel updated</div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════ */

function parseJ(v) { try { return typeof v === 'string' ? JSON.parse(v) : (v || {}); } catch { return {}; } }
function fmt$(n) { if (n == null || isNaN(n)) return '--'; return '$' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 }); }
function fmtX(n) { if (n == null || isNaN(n)) return '--'; return Number(n).toFixed(2) + 'x'; }
function fmtPct(n) { if (n == null || isNaN(n)) return '--'; return Math.round(n * 100) + '%'; }

// Centralize backend base URL (so we can swap Render services without touching code).
const WORKFLOW_BASE =
  process.env.WORKFLOW_BASE ||
  process.env.REACT_APP_WORKFLOW_BASE_URL ||
  'https://mri-portal.onrender.com';

function formatWorkflowApiError(data, status) {
  if (!data || typeof data !== 'object') return status ? `HTTP ${status}` : '';
  const { detail } = data;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const s = detail.map((e) => (e && (e.msg || e.message)) || '').filter(Boolean).join('; ');
    return s || (status ? `HTTP ${status}` : '');
  }
  if (detail && typeof detail === 'object' && detail.msg) return String(detail.msg);
  if (data.message) return String(data.message);
  return status ? `HTTP ${status}` : '';
}

function normalizePublishLinks(data) {
  // Backend returns { links: { p1, p2, report, present_p1, ... } } (map of URLs).
  // Also accept { links: [{ label, url }] }.
  if (!data) return [];
  const raw = data.links;
  if (Array.isArray(raw)) {
    return raw
      .map((l, i) => {
        if (!l) return null;
        if (typeof l === 'string') return { label: `Link ${i + 1}`, url: l };
        const url = l.url || l.href || l.link;
        const label = l.label || l.name || l.title || `Link ${i + 1}`;
        return url ? { label, url } : null;
      })
      .filter(Boolean);
  }
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const order = ['p1', 'p2', 'report', 'present_p1', 'pdf_p1', 'pdf_p2', 'pdf_report'];
    const keys = Object.keys(raw).sort(
      (a, b) => (order.indexOf(a) === -1 ? 999 : order.indexOf(a)) - (order.indexOf(b) === -1 ? 999 : order.indexOf(b))
    );
    return keys
      .map((k) => {
        const v = raw[k];
        if (typeof v !== 'string' || !/^https?:\/\//i.test(v)) return null;
        return { label: k.replace(/_/g, ' ').toUpperCase(), url: v };
      })
      .filter(Boolean);
  }
  // Alternate: top-level http-ish string fields
  if (typeof data === 'object') {
    const candidates = [];
    for (const [k, v] of Object.entries(data)) {
      if (k === 'links') continue;
      if (typeof v === 'string' && /^https?:\/\//i.test(v)) {
        candidates.push({ label: k.replace(/_/g, ' ').toUpperCase(), url: v });
      }
    }
    if (candidates.length) return candidates;
  }
  return [];
}

async function copyTextToClipboard(text) {
  try {
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through
  }
  try {
    const el = document.createElement('textarea');
    el.value = text;
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.focus();
    el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}

const splitStyle = `
  .lab-split-wrap { display: flex; height: calc(100vh - 44px); }
  .lab-pane-left { width: 45%; overflow-y: auto; border-right: 1px solid rgba(255,255,255,0.08); background: #0D2438; }
  .lab-pane-right { width: 55%; overflow-y: auto; background: #07192A; }
  .lab-pane-left-wide { width: 55%; }
  .lab-pane-right-narrow { width: 45%; }
  .lab-pane-left-even { width: 50%; }
  .lab-pane-right-even { width: 50%; }
  @media (max-width: 768px) {
    .lab-split-wrap { flex-direction: column; height: auto; }
    .lab-pane-left, .lab-pane-right, .lab-pane-left-wide, .lab-pane-right-narrow, .lab-pane-left-even, .lab-pane-right-even { width: 100%; min-height: 50vh; }
  }
`;

function SplitScreen({ left, right, leftWide, evenSplit }) {
  const leftCls = evenSplit
    ? 'lab-pane-left lab-pane-left-even'
    : (leftWide ? 'lab-pane-left lab-pane-left-wide' : 'lab-pane-left');
  const rightCls = evenSplit
    ? 'lab-pane-right lab-pane-right-even'
    : (leftWide ? 'lab-pane-right lab-pane-right-narrow' : 'lab-pane-right');
  return (
    <>
      <style>{splitStyle}</style>
      <div className="lab-split-wrap">
        <div className={leftCls}>{left}</div>
        <div className={rightCls}>{right}</div>
      </div>
    </>
  );
}

function TabSection({ title, children }) {
  return (
    <div style={{ borderBottom: `1px solid ${T.border}` }}>
      <div style={{ ...mono, fontSize: 8, letterSpacing: '0.22em', textTransform: 'uppercase', color: T.copperText, padding: '14px 20px' }}>{title}</div>
      <div style={{ padding: '0 20px 20px' }}>{children}</div>
    </div>
  );
}

function DataRow({ label, value, source, conflict }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '6px 0', borderBottom: `1px solid ${T.border}` }}>
      <span style={{ ...mono, fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.12em' }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {conflict && <span style={{ width: 6, height: 6, background: T.amber, display: 'inline-block', flexShrink: 0 }} title="Conflicting values" />}
        <span style={{ ...sans, fontSize: 13, color: T.text1 }}>{value}</span>
        {source && <span style={{ ...mono, fontSize: 7, padding: '1px 5px', background: source === 'VERIFIED' ? 'rgba(26,122,98,0.15)' : 'rgba(181,131,42,0.15)', color: source === 'VERIFIED' ? T.green : T.amber }}>{source}</span>}
      </div>
    </div>
  );
}

function LaneRow({ lane, onToggle, onEdit }) {
  const bg = lane.active !== false ? 'transparent' : 'rgba(255,255,255,0.02)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: `1px solid ${T.border}`, opacity: lane.active !== false ? 1 : 0.4 }}>
      <button onClick={() => onToggle && onToggle(lane)} style={{ ...mono, fontSize: 7, background: lane.active !== false ? 'rgba(26,122,98,0.15)' : 'rgba(160,40,40,0.15)', color: lane.active !== false ? T.green : T.red, border: 'none', padding: '3px 6px', cursor: 'pointer', minHeight: 24 }}>{lane.active !== false ? 'ON' : 'OFF'}</button>
      <div style={{ flex: 1 }}>
        <div style={{ ...sans, fontSize: 13, color: T.text1 }}>{lane.name}</div>
      </div>
      <span style={{ ...mono, fontSize: 10, color: T.text2 }}>{fmtPct(lane.probability)}</span>
      <span style={{ ...mono, fontSize: 10, color: T.text2 }}>{fmtX(lane.multiple_low)}-{fmtX(lane.multiple_high)}</span>
      <span style={{ ...mono, fontSize: 10, color: T.copperText }}>{fmt$(lane.p50)}</span>
      <span style={{ ...mono, fontSize: 9, color: T.amber }}>{lane.retrade_pct ? Math.round(lane.retrade_pct * 100) + '%' : '--'}</span>
    </div>
  );
}

function CopperButton({ label, onClick, disabled, fullWidth }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      ...mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase',
      background: disabled ? T.elevated : T.copper, color: '#F5F0E8',
      border: 'none', padding: '12px 24px', cursor: disabled ? 'not-allowed' : 'pointer',
      borderRadius: 0, minHeight: 44, opacity: disabled ? 0.5 : 1,
      ...(fullWidth ? { width: '100%' } : {}),
    }}>{label}</button>
  );
}

function StatusBanner({ text, color }) {
  return (
    <div style={{ ...mono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', padding: '10px 20px', background: color === 'green' ? 'rgba(26,122,98,0.12)' : 'rgba(181,131,42,0.12)', color: color === 'green' ? T.green : T.amber, borderBottom: `1px solid ${T.border}` }}>
      {text}
    </div>
  );
}

/* ═══════════════════════════════════════════
   TAB 1 -- EVIDENCE BINDER
   ═══════════════════════════════════════════ */

function EvidenceBinderTab({ eng, engId, onRefresh }) {
  const is = parseJ(eng.intake_structured);
  const eb = is._evidence_binder || {};
  const cfRaw = eb.consolidated_fields || {};
  const cfArray = Array.isArray(cfRaw) ? cfRaw : [];
  const [localIfa, setLocalIfa] = useState(() => parseJ(eng.intake_form_answers));
  const mi = parseJ(eng.market_intel);
  const ga = parseJ(eng.gap_analysis);
  const docs = eb.documents || [];
  const years = ['2022', '2023', '2024', '2025'];
  const [sending, setSending] = useState(false);
  const [overrides, setOverrides] = useState(() => parseJ(eng.data_overrides));
  const [editingCell, setEditingCell] = useState(null); // {field, year}
  const [editValue, setEditValue] = useState('');
  const [editingIfa, setEditingIfa] = useState(null); // field key
  const [editIfaValue, setEditIfaValue] = useState('');
  const role = getRole();

  const FIELD_LABELS = {
    revenue: 'Revenue', gross_profit: 'Gross Profit', net_income: 'Net Income',
    owner_compensation: 'Owner Compensation', rent_expense: 'Rent Expense',
    depreciation: 'Depreciation', depreciation_expense: 'Depreciation (P&L)',
    interest_expense: 'Interest Expense',
    total_assets: 'Total Assets', total_liabilities: 'Total Liabilities',
    cash_on_hand: 'Cash on Hand', accounts_receivable: 'Accounts Receivable',
  };

  const FINANCIAL_ROWS = Object.keys(FIELD_LABELS);

  // Build lookup from consolidated_fields array
  const fieldData = {};
  if (cfArray.length > 0) {
    cfArray.forEach(item => {
      const f = (item.field || '').toLowerCase().replace(/\s+/g, '_');
      const y = String(item.year || '');
      if (!f || !y) return;
      if (!fieldData[f]) fieldData[f] = {};
      const existing = fieldData[f][y];
      if (!existing) {
        fieldData[f][y] = { value: item.value, source: item.credibility || 'INTERNAL', confidence: item.confidence || 0, source_file: item.source_file || '', document_type: item.document_type || '' };
      } else {
        if ((item.credibility === 'VERIFIED' && existing.source !== 'VERIFIED') || (item.confidence || 0) > (existing.confidence || 0)) {
          fieldData[f][y] = { value: item.value, source: item.credibility || 'INTERNAL', confidence: item.confidence || 0, source_file: item.source_file || '', document_type: item.document_type || '', conflict: `Also: ${fmt$(existing.value)} (${existing.source})` };
        } else {
          fieldData[f][y] = { ...existing, conflict: `Also: ${fmt$(item.value)} (${item.credibility || 'INTERNAL'})` };
        }
      }
    });
  } else if (typeof cfRaw === 'object' && !Array.isArray(cfRaw)) {
    Object.entries(cfRaw).forEach(([key, val]) => {
      for (const y of years) {
        if (key.endsWith('_' + y) || key.endsWith(y)) {
          const f = key.replace(new RegExp('_?' + y + '$'), '');
          if (!fieldData[f]) fieldData[f] = {};
          if (val && typeof val === 'object') {
            fieldData[f][y] = { value: val.value, source: val.credibility || val.source || 'INTERNAL', confidence: val.confidence || 0, conflict: val.conflict, source_file: val.source_file || '', document_type: val.document_type || '' };
          } else if (val != null) {
            fieldData[f][y] = { value: val, source: 'INTERNAL', confidence: 0 };
          }
        }
      }
    });
  }

  // Get the effective value for a field+year, applying overrides and abs for owner_comp
  const getEffectiveValue = (field, year) => {
    const overrideKey = `${field}_${year}`;
    if (overrides[overrideKey] != null) return Number(overrides[overrideKey].value);
    const raw = fieldData[field] && fieldData[field][year];
    if (!raw) return null;
    let v = Number(raw.value);
    // FIX 2: owner_compensation always positive
    if (field === 'owner_compensation') v = Math.abs(v);
    return v;
  };

  const getField = (field, year) => {
    if (field === 'ebitda') {
      // FIX 1: Use depreciation_expense (P&L) not depreciation (balance sheet)
      // Look for depreciation entries from P&L source files only
      const ni = getEffectiveValue('net_income', year);
      const ie = getEffectiveValue('interest_expense', year);
      const oc = getEffectiveValue('owner_compensation', year);

      // For depreciation in EBITDA, prefer depreciation_expense, or filter depreciation to P&L sources
      let depVal = getEffectiveValue('depreciation_expense', year);
      if (depVal == null) {
        // Check if depreciation entry comes from P&L
        const depEntries = cfArray.filter(item => {
          const f = (item.field || '').toLowerCase().replace(/\s+/g, '_');
          return f === 'depreciation' && String(item.year) === year && ((item.source_file || '').includes('PnL') || (item.source_file || '').includes('P&L') || item.document_type === 'internal_pl');
        });
        depVal = depEntries.length > 0 ? Number(depEntries[0].value) || 0 : 0;
      }

      const sum = (ni || 0) + depVal + (ie || 0) + (oc || 0);
      if (sum === 0 && ni == null && ie == null && oc == null) return null;
      return { value: sum, source: 'CALCULATED', confidence: 100 };
    }

    const overrideKey = `${field}_${year}`;
    const hasOverride = overrides[overrideKey] != null;
    const raw = fieldData[field] && fieldData[field][year];
    if (hasOverride) {
      return { value: Number(overrides[overrideKey].value), source: 'OVERRIDE', confidence: 100, originalValue: raw ? raw.value : null };
    }
    if (!raw) return null;
    let v = Number(raw.value);
    if (field === 'owner_compensation') v = Math.abs(v);
    return { ...raw, value: v };
  };

  const conflicts = FINANCIAL_ROWS.reduce((count, field) => {
    years.forEach(y => { const f = getField(field, y); if (f && f.conflict) count++; });
    return count;
  }, 0);

  const gapQuestions = ga.gap_questions || [
    { priority: 'HIGH', question: 'You mentioned $640K EBITDA. Our document analysis shows $370K before add-backs. Walk me through every add-back you expect a buyer to accept.', reason: 'Every dollar of defensible EBITDA drives $5-9 in enterprise value.' },
    { priority: 'HIGH', question: 'The $75K salary adjustment and $18K vehicle -- do you have documentation showing these are above-market or personal expenses?', reason: 'Undocumented add-backs are automatic retrade events in diligence.' },
    { priority: 'MEDIUM', question: 'You said 72% recurring revenue. Are those formal signed contracts or relationship-based arrangements?', reason: 'Contracted recurring revenue commands 0.5-1.0x multiple premium over relationship-based recurring.' },
    { priority: 'MEDIUM', question: 'How many hours per week are you working in the business versus on it? What specifically would stop working if you were not there for 30 days?', reason: 'Owner dependency is the #1 retrade trigger for PE and search fund buyers.' },
  ];

  const handleSendEvidenceLock = async () => {
    setSending(true);
    try {
      await fetch(`${WORKFLOW_BASE}/email/evidence-lock/${engId}`, { method: 'POST', credentials: 'include' });
      await supabase.from('cordis_engagements').update({ evidence_lock_sent_at: new Date().toISOString() }).eq('id', engId);
      if (onRefresh) onRefresh();
    } catch { /* ignore */ }
    setSending(false);
  };

  // Inline edit: start
  const startEdit = (field, year) => {
    if (field === 'ebitda') return; // not directly editable
    const f = getField(field, year);
    setEditingCell({ field, year });
    setEditValue(f ? String(Math.round(f.value)) : '');
  };

  // Inline edit: save
  const saveEdit = async () => {
    if (!editingCell) return;
    const { field, year } = editingCell;
    const newVal = Number(editValue);
    if (isNaN(newVal)) { setEditingCell(null); return; }
    const oldField = getField(field, year);
    const oldVal = oldField ? oldField.value : null;
    const overrideKey = `${field}_${year}`;
    const updated = { ...overrides, [overrideKey]: { value: newVal, overridden_by: 'analyst', overridden_at: new Date().toISOString() } };
    setOverrides(updated);
    setEditingCell(null);
    // Save to Supabase
    await supabase.from('cordis_engagements').update({ data_overrides: updated }).eq('id', engId);
    // Log
    await supabase.from('cordis_log').insert({ action: 'data_override', engagement_id: engId, detail: JSON.stringify({ field, year, old_value: oldVal, new_value: newVal }), created_at: new Date().toISOString() }).catch(() => {});
  };

  // Reset override
  const resetOverride = async (field, year) => {
    const overrideKey = `${field}_${year}`;
    const updated = { ...overrides };
    delete updated[overrideKey];
    setOverrides(updated);
    await supabase.from('cordis_engagements').update({ data_overrides: updated }).eq('id', engId);
  };

  // Intake field edit
  const startIfaEdit = (key) => {
    setEditingIfa(key);
    setEditIfaValue(localIfa[key] || '');
  };

  const saveIfaEdit = async () => {
    if (!editingIfa) return;
    const updated = { ...localIfa, [editingIfa]: editIfaValue };
    setLocalIfa(updated);
    setEditingIfa(null);
    await supabase.from('cordis_engagements').update({ intake_form_answers: updated }).eq('id', engId);
  };

  const addBacks = [
    { item: 'Personal vehicle', amount: 18000, source: 'Intake Q4', defensibility: 'Medium' },
    { item: 'Above-market salary adjustment', amount: 75000, source: 'Intake Q4', defensibility: 'Medium' },
    { item: 'One-time legal fees', amount: 12000, source: 'Intake Q4', defensibility: 'High' },
  ];

  const ifa = localIfa;

  // Editable cell renderer
  const renderCell = (field, year) => {
    const f = getField(field, year);
    const isEditing = editingCell && editingCell.field === field && editingCell.year === year;
    const overrideKey = `${field}_${year}`;
    const hasOverride = overrides[overrideKey] != null;

    if (isEditing) {
      return (
        <input
          type="number"
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          onBlur={saveEdit}
          onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditingCell(null); }}
          autoFocus
          style={{ ...mono, fontSize: 12, color: T.text1, background: T.elevated, border: `1px solid ${T.copper}`, padding: '4px 6px', width: 100, textAlign: 'right', outline: 'none', borderRadius: 0 }}
        />
      );
    }

    return (
      <span
        onClick={() => startEdit(field, year)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: field === 'ebitda' ? 'default' : 'pointer', position: 'relative' }}
        title={field === 'ebitda' ? 'Net Income + Depreciation (P&L) + Interest Expense + Owner Compensation' : (hasOverride ? `Overridden -- original: ${fmt$(f.originalValue)}` : undefined)}
      >
        {f && f.conflict && <span style={{ width: 5, height: 5, background: T.amber, display: 'inline-block' }} title={f.conflict} />}
        {f ? fmt$(f.value) : '--'}
        {hasOverride && (
          <span
            style={{ width: 5, height: 5, background: T.copper, display: 'inline-block', position: 'absolute', top: -2, right: -8 }}
            title={`Overridden -- original: ${fmt$(f && f.originalValue)}\nClick to reset`}
            onClick={e => { e.stopPropagation(); resetOverride(field, year); }}
          />
        )}
      </span>
    );
  };

  // Editable intake row
  const EditableDataRow = ({ label, fieldKey, value }) => {
    const isEditing = editingIfa === fieldKey;
    if (isEditing) {
      const isLong = ['q1', 'q2a', 'q4', 'q9', 'owner_role', 'top_customers', 'add_backs', 'ideal_outcome'].includes(fieldKey);
      return (
        <div style={{ padding: '6px 0', borderBottom: `1px solid ${T.border}` }}>
          <div style={{ ...mono, fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4 }}>{label}</div>
          {isLong ? (
            <textarea value={editIfaValue} onChange={e => setEditIfaValue(e.target.value)} onBlur={saveIfaEdit} onKeyDown={e => { if (e.key === 'Escape') setEditingIfa(null); }} autoFocus style={{ ...sans, fontSize: 13, color: T.text1, background: T.elevated, border: `1px solid ${T.copper}`, width: '100%', padding: '6px 8px', minHeight: 60, resize: 'vertical', outline: 'none', borderRadius: 0 }} />
          ) : (
            <input value={editIfaValue} onChange={e => setEditIfaValue(e.target.value)} onBlur={saveIfaEdit} onKeyDown={e => { if (e.key === 'Enter') saveIfaEdit(); if (e.key === 'Escape') setEditingIfa(null); }} autoFocus style={{ ...sans, fontSize: 13, color: T.text1, background: T.elevated, border: `1px solid ${T.copper}`, width: '100%', padding: '6px 8px', outline: 'none', borderRadius: 0 }} />
          )}
        </div>
      );
    }
    return (
      <div onClick={() => startIfaEdit(fieldKey)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '6px 0', borderBottom: `1px solid ${T.border}`, cursor: 'pointer' }}>
        <span style={{ ...mono, fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.12em' }}>{label}</span>
        <span style={{ ...sans, fontSize: 13, color: T.text1 }}>{value || '--'}</span>
      </div>
    );
  };

  const leftPanel = (
    <div>
      <TabSection title="Financial Data by Year">
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...mono, fontSize: 7.5, color: T.textMuted, textAlign: 'left', padding: '6px 8px', borderBottom: `1px solid ${T.border}` }}>FIELD</th>
                {years.map(y => <th key={y} style={{ ...mono, fontSize: 7.5, color: T.textMuted, textAlign: 'right', padding: '6px 8px', borderBottom: `1px solid ${T.border}` }}>{y}</th>)}
                <th style={{ ...mono, fontSize: 7.5, color: T.textMuted, textAlign: 'center', padding: '6px 8px', borderBottom: `1px solid ${T.border}` }}>SOURCE</th>
              </tr>
            </thead>
            <tbody>
              {[...FINANCIAL_ROWS, 'ebitda'].map(field => (
                <tr key={field} style={field === 'ebitda' ? { borderTop: `2px solid ${T.copper}` } : {}}>
                  <td style={{ ...mono, fontSize: 9, color: field === 'ebitda' ? T.copperText : T.text2, padding: '6px 8px', borderBottom: `1px solid ${T.border}`, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: field === 'ebitda' ? 500 : 400 }}>{field === 'ebitda' ? 'EBITDA (calc)' : (FIELD_LABELS[field] || field.replace(/_/g, ' '))}</td>
                  {years.map(y => (
                    <td key={y} style={{ ...sans, fontSize: 12, color: getField(field, y) ? T.text1 : T.textMuted, textAlign: 'right', padding: '6px 8px', borderBottom: `1px solid ${T.border}` }}>
                      {renderCell(field, y)}
                    </td>
                  ))}
                  <td style={{ textAlign: 'center', padding: '6px 8px', borderBottom: `1px solid ${T.border}` }}>
                    {(() => { const f = getField(field, '2024') || getField(field, '2023'); if (!f) return null; const src = f.source || 'INTERNAL'; return <span style={{ ...mono, fontSize: 7, padding: '1px 5px', background: src === 'VERIFIED' ? 'rgba(26,122,98,0.15)' : src === 'CALCULATED' ? 'rgba(122,82,48,0.15)' : src === 'OVERRIDE' ? 'rgba(122,82,48,0.25)' : 'rgba(181,131,42,0.15)', color: src === 'VERIFIED' ? T.green : (src === 'CALCULATED' || src === 'OVERRIDE') ? T.copper : T.amber }}>{src}</span>; })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {conflicts > 0 && (
          <div style={{ ...mono, fontSize: 9, color: T.amber, marginTop: 12, padding: '8px 12px', background: 'rgba(181,131,42,0.08)' }}>
            {conflicts} conflicts requiring resolution -- Resolve in Market Mapping
          </div>
        )}
        <div style={{ marginTop: 12, padding: '12px 16px', background: 'rgba(26,122,98,0.08)', borderLeft: `3px solid ${T.copper}` }}>
          <div style={{ ...mono, fontSize: 8, letterSpacing: '0.14em', color: T.copperText, marginBottom: 6 }}>NOTE -- REVENUE RECONCILIATION</div>
          <div style={{ ...sans, fontSize: 12, color: T.text2, lineHeight: 1.6 }}>Tax return and QB P&L figures are being used as the authoritative revenue source. IRS 1120S: $1,671,251 (2024). QB P&L: $1,671,461 (2024). These figures are consistent and verified. FY2025 IRS 1120-S is the authoritative revenue source at $1,988,618. QB P&L figures are consistent and verified.</div>
        </div>
      </TabSection>

      <TabSection title="Intake Q&A Summary">
        <EditableDataRow label="Owner Role" fieldKey="q1" value={ifa.q1 || ifa.owner_role} />
        <EditableDataRow label="Key Team" fieldKey="q1_followup" value={ifa.q1_followup} />
        <EditableDataRow label="Top Customers" fieldKey="q2a" value={(() => {
          const v = ifa.q2a || ifa.top_customers;
          if (!v) return '';
          if (Array.isArray(v)) return v.map(c => `${c.name || c.customer} ${c.pct || c.percentage || ''}%`).join(', ');
          return v;
        })()} />
        <EditableDataRow label="Revenue Mix" fieldKey="q2b" value={ifa.q2b || ifa.revenue_mix} />
        <EditableDataRow label="Revenue Trend" fieldKey="q3" value={ifa.q3} />
        <EditableDataRow label="Recurring %" fieldKey="q3_recurring" value={ifa.q3_recurring ? ifa.q3_recurring + '%' : ''} />
        <EditableDataRow label="Project %" fieldKey="q3_project" value={ifa.q3_project ? ifa.q3_project + '%' : ''} />
        <EditableDataRow label="Add-Backs" fieldKey="q4" value={ifa.q4 || ifa.add_backs} />
        <EditableDataRow label="Add-Back Documentation" fieldKey="q4_followup" value={ifa.q4_followup} />
        <EditableDataRow label="Employees" fieldKey="q5" value={ifa.q5 || ifa.employees} />
        <EditableDataRow label="Geography" fieldKey="q6" value={ifa.q6 || ifa.geography} />
        <EditableDataRow label="Facility" fieldKey="q7" value={ifa.q7 || ifa.facility} />
        <EditableDataRow label="Timeline" fieldKey="q8" value={ifa.q8 || ifa.timeline} />
        <EditableDataRow label="Ideal Outcome" fieldKey="q9" value={ifa.q9 || ifa.ideal_outcome} />
        <EditableDataRow label="Deal Structure" fieldKey="q10" value={ifa.q10 || ifa.deal_structure_preference} />
      </TabSection>

      <TabSection title="Market Context">
        {mi.timing_signal && <div style={{ ...sans, fontSize: 13, color: T.text2, lineHeight: 1.6, marginBottom: 12 }}>{mi.timing_signal}</div>}
        {(() => {
          const mults = mi.current_multiples || mi.multiples;
          const LANE_LABELS = { pe_addon: 'PE Add-On', strategic: 'Strategic', family_office: 'Family Office', search_fund: 'Search Fund', independent_sponsor: 'Independent Sponsor' };
          if (!mults) return null;
          return (
            <div style={{ marginBottom: 12 }}>
              {Object.entries(mults).map(([lane, range]) => (
                <div key={lane} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: `1px solid ${T.border}` }}>
                  <span style={{ ...sans, fontSize: 12, color: T.text2 }}>{LANE_LABELS[lane] || lane.replace(/_/g, ' ')}</span>
                  <span style={{ ...mono, fontSize: 11, color: T.text1 }}>
                    {typeof range === 'string' ? range : range && range.floor != null ? `${fmtX(range.floor)} - ${fmtX(range.ceiling)}` : range && range.low != null ? `${fmtX(range.low)} - ${fmtX(range.high)}` : '--'}
                  </span>
                </div>
              ))}
            </div>
          );
        })()}
        <div style={{ ...mono, fontSize: 8, color: (mi.generated_at || mi.last_refreshed) && (Date.now() - new Date(mi.generated_at || mi.last_refreshed).getTime() > 14 * 86400000) ? T.amber : T.textMuted }}>
          Last refreshed: {(mi.generated_at || mi.last_refreshed) ? new Date(mi.generated_at || mi.last_refreshed).toLocaleDateString() : 'Never'}
        </div>
      </TabSection>

      <TabSection title="Discovery Call Agenda">
        <div style={{ ...mono, fontSize: 8, letterSpacing: '0.16em', color: T.copperText, marginBottom: 12 }}>TOP QUESTIONS FOR DISCOVERY CALL</div>
        {gapQuestions.map((q, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, padding: '10px 0', borderBottom: `1px solid ${T.border}` }}>
            <span style={{ ...mono, fontSize: 7, padding: '2px 6px', background: q.priority === 'HIGH' ? 'rgba(160,40,40,0.15)' : 'rgba(181,131,42,0.15)', color: q.priority === 'HIGH' ? T.red : T.amber, flexShrink: 0, alignSelf: 'flex-start', marginTop: 2 }}>{q.priority}</span>
            <div>
              <div style={{ ...sans, fontSize: 13, color: T.text1, lineHeight: 1.5 }}>{q.question}</div>
              <div style={{ ...mono, fontSize: 9, color: T.textMuted, marginTop: 4 }}>{q.reason}</div>
            </div>
          </div>
        ))}
      </TabSection>
    </div>
  );

  const rightPanel = (
    <div style={{ padding: 32 }}>
      <div style={{ ...mono, fontSize: 9, letterSpacing: '0.28em', color: T.copperText, marginBottom: 8 }}>CORDIS GROUP</div>
      <div style={{ ...serif, fontSize: 32, fontWeight: 300, color: T.text1, marginBottom: 4 }}>Business Profile Review</div>
      <div style={{ ...serif, fontSize: 18, fontStyle: 'italic', color: T.text2, marginBottom: 16 }}>{eng.company || eng.client_name}</div>
      <div style={{ ...mono, fontSize: 8, color: T.textMuted, marginBottom: 24 }}>
        {new Date().toLocaleDateString()} | EL-{engId.slice(0, 8).toUpperCase()} | {eng.data_locked_at ? 'DATA LOCKED' : 'UNLOCKED'}
      </div>

      <TabSection title="Your Business by the Numbers">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>{['METRIC', ...years].map(h => <th key={h} style={{ ...mono, fontSize: 7.5, color: T.textMuted, textAlign: h === 'METRIC' ? 'left' : 'right', padding: '6px 8px', borderBottom: `1px solid ${T.border}` }}>{h}</th>)}</tr></thead>
          <tbody>
            {[...FINANCIAL_ROWS, 'ebitda'].map(field => {
              return (
                <tr key={field} style={field === 'ebitda' ? { borderTop: `2px solid ${T.copper}` } : {}}>
                  <td style={{ ...mono, fontSize: 9, color: field === 'ebitda' ? T.copperText : T.text2, padding: '6px 8px', borderBottom: `1px solid ${T.border}`, textTransform: 'uppercase' }}>{field === 'ebitda' ? 'EBITDA (calc)' : (FIELD_LABELS[field] || field.replace(/_/g, ' '))}</td>
                  {years.map(y => { const f = getField(field, y); return <td key={y} style={{ ...sans, fontSize: 12, color: f ? T.text1 : T.textMuted, textAlign: 'right', padding: '6px 8px', borderBottom: `1px solid ${T.border}` }}>{f ? fmt$(f.value) : '--'}</td>; })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </TabSection>

      <TabSection title="Business Profile">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
          {[
            { l: 'Years Operating', v: is.years_operating },
            { l: 'Geography', v: is.geography || ifa.q6 || ifa.geography },
            { l: 'Employees', v: is.employee_count || ifa.q5 || ifa.employees },
            { l: 'Recurring Revenue', v: is.recurring_revenue_pct ? is.recurring_revenue_pct + '%' : (ifa.q3_recurring ? ifa.q3_recurring + '%' : null) },
            { l: 'Deal Structure', v: is.deal_structure_preference || ifa.q10 || ifa.deal_structure_preference },
            { l: 'Timeline', v: is.timeline || ifa.q8 || ifa.timeline },
            { l: 'Owner Dependency', v: is.owner_dependency },
            { l: 'Top Customer %', v: is.top_customer_pct || (ifa.q2a ? (Array.isArray(ifa.q2a) ? ifa.q2a[0] && (ifa.q2a[0].name + ' ' + (ifa.q2a[0].pct || '') + '%') : String(ifa.q2a).split('\n')[0]) : null) },
          ].map(m => (
            <div key={m.l}>
              <div style={{ ...mono, fontSize: 7.5, color: T.textMuted, marginBottom: 4 }}>{m.l}</div>
              <div style={{ ...sans, fontSize: 13, color: T.text1 }}>{m.v || '--'}</div>
            </div>
          ))}
        </div>
      </TabSection>

      <TabSection title="Add-Backs Summary">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>{['ADD-BACK ITEM', 'AMOUNT', 'SOURCE', 'DEFENSIBILITY'].map(h => <th key={h} style={{ ...mono, fontSize: 7.5, color: T.textMuted, textAlign: 'left', padding: '6px 8px', borderBottom: `1px solid ${T.border}` }}>{h}</th>)}</tr></thead>
          <tbody>
            {addBacks.map((ab, i) => (
              <tr key={i}>
                <td style={{ ...sans, fontSize: 12, color: T.text1, padding: '6px 8px', borderBottom: `1px solid ${T.border}` }}>{ab.item}</td>
                <td style={{ ...mono, fontSize: 11, color: T.text1, padding: '6px 8px', borderBottom: `1px solid ${T.border}` }}>{fmt$(ab.amount)}</td>
                <td style={{ ...mono, fontSize: 9, color: T.text2, padding: '6px 8px', borderBottom: `1px solid ${T.border}` }}>{ab.source}</td>
                <td style={{ padding: '6px 8px', borderBottom: `1px solid ${T.border}` }}><span style={{ ...mono, fontSize: 7, padding: '2px 6px', background: ab.defensibility === 'High' ? 'rgba(26,122,98,0.15)' : 'rgba(181,131,42,0.15)', color: ab.defensibility === 'High' ? T.green : T.amber }}>{ab.defensibility.toUpperCase()}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </TabSection>

      <TabSection title="Data Sources">
        {docs.length === 0 ? <div style={{ ...sans, fontSize: 13, color: T.textMuted }}>No documents uploaded yet.</div> : docs.map((d, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: `1px solid ${T.border}` }}>
            <span style={{ ...mono, fontSize: 7, padding: '2px 6px', background: 'rgba(122,82,48,0.12)', color: T.copperText }}>{d.type || 'DOC'}</span>
            <span style={{ ...sans, fontSize: 12, color: T.text1, flex: 1 }}>{d.name || d.filename || `Document ${i + 1}`}</span>
            {d.year && <span style={{ ...mono, fontSize: 9, color: T.textMuted }}>{d.year}</span>}
          </div>
        ))}
      </TabSection>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 44px)' }}>
      <div style={{ flex: 1, overflow: 'hidden' }}><SplitScreen left={leftPanel} right={rightPanel} /></div>
      <div style={{ padding: '12px 20px', borderTop: `1px solid ${T.border}`, background: T.surface, flexShrink: 0 }}>
        <CopperButton label={sending ? 'Sending...' : 'Send Evidence Lock to Client'} onClick={handleSendEvidenceLock} disabled={sending} />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   TAB 2 -- MARKET MAPPING
   ═══════════════════════════════════════════ */

const LANE_PROFILES = {
  'Strategic': {
    status: 'OPEN',
    profile: 'Strategic acquirers in irrigation and building services are primarily larger regional operators and facilities management platforms looking to add geographic density and recurring maintenance revenue. They buy for the customer base and the contracts, not the equipment.',
    deal: { cash: '80-92%', earnout: '0-15% tied to revenue retention', escrow: '8-10% held 12-18 months', timeline: '6-12 months' },
    priceUp: ['Recurring maintenance contracts (72% of AIS revenue is highly attractive)', 'Geographic density in CT/Westchester', 'No customer above 12% (moderate concentration)', '15 years of operating history', '18 employees with experienced team members'],
    discount: ['Owner handles estimating and sales (key person risk)', 'Month-to-month lease', 'Revenue discrepancy between stated and documented figures', 'No formal QoE documentation'],
    riskTransfer: 'Will reduce headline price for undocumented risks rather than use earnout structure. Prefer clean deals with reps and warranties.',
    favor: ['72% recurring revenue is above sector average', 'No customer above 12% reduces concentration risk', '15 years of operating continuity', 'CT/Westchester geography is consolidation target'],
    against: ['Owner dependency in estimating and sales', 'Revenue documentation gap', 'Month-to-month lease creates uncertainty', 'No QoE study means buyers will apply their own (conservative) normalization'],
    unlock: 'Lane is open. To access the P75-P90 range ($4.1M-$5.0M), resolve financial documentation, complete QoE, and demonstrate owner transition capability.',
  },
  'PE Add-On': {
    status: 'OPEN',
    profile: 'PE add-on buyers are portfolio companies already in building services, facilities management, or landscaping looking to bolt on a complementary business. They have a playbook, a financing package, and a thesis. AIS fits as a geographic or service-line add-on to an existing platform.',
    deal: { cash: '70-85%', earnout: 'Possible if financials unclear', escrow: 'Rollover equity 10-20%', timeline: '4-8 months with platform' },
    priceUp: ['Recurring maintenance book', 'Clean financial documentation', 'Management team that can run post-close', 'Geographic market not already covered by their platform'],
    discount: ['Owner dependency (PE needs business to run without the founder)', 'Undocumented add-backs', 'Customer concentration even at 12%', 'Retrade probability: 49.8% -- highest of all lanes'],
    riskTransfer: 'Use earnout aggressively when financials are undocumented. Will require seller note if QoE not completed. Rollover equity aligns interests but dilutes cash at close.',
    favor: ['EBITDA margins support leveraged structure', 'Recurring revenue fits PE platform thesis', 'Size is ideal add-on range', 'CT geography may fill platform gap'],
    against: ['Highest retrade probability (49.8%)', 'Owner dependency is primary concern for PE buyers', 'Revenue documentation gap will trigger aggressive normalization', 'Without QoE, PE will underwrite conservative EBITDA'],
    unlock: 'Resolve financial documentation first. Then QoE. Then demonstrate management depth. Those three actions shift this lane from a 49.8% retrade risk to under 20%.',
  },
  'Family Office': {
    status: 'OPEN',
    profile: 'Family offices buying in this range are typically looking for stable cash-flowing businesses they can hold for 7-10 years. They value founder involvement and long-term relationships. They are the most patient capital in the market and often the most founder-friendly in deal structure.',
    deal: { cash: '75-90%', earnout: 'Rare, only if growth thesis', escrow: 'Seller note sometimes requested as confidence signal', timeline: '6-10 months' },
    priceUp: ['Stable recurring revenue', 'Long-tenured customer relationships', 'Owner willing to stay involved in transition', 'Simple business model they can understand', '15 years of operating history'],
    discount: ['Complex or undocumented financials', 'High customer concentration', 'Businesses that require specialized operational knowledge to run'],
    riskTransfer: 'Prefer seller note or earnout as confidence mechanism. Less aggressive on escrow than PE. Will negotiate harder on price than structure.',
    favor: ['72% recurring revenue is ideal for family office hold thesis', 'Dennis willing to stay for transition', '15 years of continuity', 'Simple service business model', 'No customer above 12%'],
    against: ['Revenue documentation gap creates uncertainty for conservative capital', 'Owner dependency (they want founder involvement but also want business to be able to run independently)'],
    unlock: 'Lane is open. Family offices will pay P50-P75 range without extensive preparation. Full preparation moves this to P75-P90.',
  },
  'Search Fund': {
    status: 'CONDITIONAL',
    profile: 'Search fund operators are typically first-time acquirers backed by a network of investors. They are buying a business to run personally. They value a manageable operation with clear processes and a willing seller to mentor them through transition.',
    deal: { cash: '65-75%', earnout: 'SBA structure limits flexibility', escrow: 'Seller note often required 10-15%', timeline: '6-10 months with SBA process' },
    priceUp: ['Clear processes that a new operator can learn', 'Manageable team size (18 employees is ideal)', 'Recurring revenue providing stable base for new operator', 'Willing seller for transition period'],
    discount: ['Undocumented processes (they need to learn the business)', 'High owner dependency', 'Revenue discrepancy (SBA underwriters will use IRS figures only)', 'Customer concentration'],
    riskTransfer: 'SBA structure limits earnout flexibility. Seller note is primary risk transfer. SBA underwriting will independently verify all financials.',
    favor: ['Team size is ideal for first-time operator', 'Recurring revenue provides stability', 'Dennis willing to stay for transition'],
    against: ['Owner dependency is the primary blocker', 'Revenue documentation must be resolved before SBA will approve'],
    unlock: 'Resolve revenue documentation to IRS-verified figures. Document processes. Demonstrate that key workflows can be transferred. These three actions move this from CONDITIONAL to OPEN.',
  },
  'Independent Sponsor': {
    status: 'OPEN',
    profile: 'Independent sponsors raise capital deal-by-deal from LP networks. They operate like PE but without a committed fund. They are typically more flexible on structure and more willing to be creative on deal terms. They often take longer to close because financing is not pre-committed.',
    deal: { cash: '70-80%', earnout: 'Common as financing tool', escrow: 'Rollover equity often requested', timeline: '6-12 months (deal can fall apart if LP financing fails)' },
    priceUp: ['Recurring revenue, clean financials', 'Management depth', 'Margin stability', 'Flexible structure options available'],
    discount: ['Same as PE Add-On but slightly more flexible', 'Will work around some documentation gaps with creative structure'],
    riskTransfer: 'Use deal structure more aggressively than funded PE. Earnout and seller note are standard tools for managing uncertainty.',
    favor: ['EBITDA supports the deal math', 'Recurring revenue is attractive', 'Flexible structure options available'],
    against: ['Revenue documentation gap will increase earnout component', 'Owner dependency reduces post-close confidence', 'Deal financing risk is real -- longer close timeline possible'],
    unlock: 'Clean financials and QoE are the highest-value actions for this lane. They reduce earnout component and increase cash at close percentage.',
  },
};

/* -- Lane Detail Card (expandable) -- */
function LaneDetailCard({ lane, onToggle, expanded, onExpand, flashP50 }) {
  const normalizeLaneName = (name) => {
    if (!name) return '';
    const n = name.toLowerCase()
      .replace(/_/g, ' ')
      .replace(/-/g, ' ')
      .trim();
    if (n.includes('strategic')) return 'Strategic';
    if (n.includes('independent') || n.includes('sponsor')) return 'Independent Sponsor';
    if (n.includes('pe') || n.includes('add')) return 'PE Add-On';
    if (n.includes('family')) return 'Family Office';
    if (n.includes('search')) return 'Search Fund';
    return name;
  };
  const profile = LANE_PROFILES[normalizeLaneName(lane.name)] || {};
  const status = profile.status || 'OPEN';
  const statusColor = status === 'OPEN' ? '#4DBDBD' : status === 'CONDITIONAL' ? '#C4956A' : 'rgba(160,40,40,0.70)';
  const statusBg = status === 'OPEN' ? 'rgba(26,107,107,0.25)' : status === 'CONDITIONAL' ? 'rgba(122,82,48,0.25)' : 'rgba(160,40,40,0.15)';
  const statusBd = status === 'OPEN' ? 'rgba(26,107,107,0.40)' : status === 'CONDITIONAL' ? 'rgba(122,82,48,0.40)' : 'rgba(160,40,40,0.40)';
  const statusLeft = status === 'OPEN' ? '#1A6B6B' : status === 'CONDITIONAL' ? '#C4956A' : 'rgba(160,40,40,0.50)';
  const retradeColor = lane.retrade_pct > 0.40 ? 'rgba(160,40,40,0.85)' : lane.retrade_pct > 0.20 ? '#B5832A' : '#4DBDBD';

  const p10 = lane.p10 || lane.p50 * 0.7;
  const p25 = lane.p25 || lane.p50 * 0.85;
  const p75 = lane.p75 || lane.p50 * 1.15;
  const p90 = lane.p90 || lane.p50 * 1.3;
  const iqrLeft = ((p25 - p10) / (p90 - p10)) * 100;
  const iqrRight = ((p75 - p10) / (p90 - p10)) * 100;
  const medPos = ((lane.p50 - p10) / (p90 - p10)) * 100;

  return (
    <div
      style={{
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        background: expanded ? 'rgba(255,255,255,0.03)' : 'transparent',
        borderLeft: expanded ? `3px solid ${statusLeft}` : '3px solid transparent',
        transition: 'background 0.2s, border-color 0.2s',
      }}
    >
      {/* Collapsed summary row */}
      <div
        onClick={onExpand}
        onMouseEnter={(e) => { if (!expanded) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
        onMouseLeave={(e) => { if (!expanded) e.currentTarget.style.background = 'transparent'; }}
        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px', minHeight: 52, cursor: 'pointer', opacity: lane.active !== false ? 1 : 0.4, transition: 'background 0.15s' }}
      >
        <button onClick={e => { e.stopPropagation(); onToggle(lane); }} style={{ ...mono, fontSize: 7, letterSpacing: '0.10em', background: lane.active !== false ? 'rgba(26,107,107,0.20)' : 'rgba(160,40,40,0.20)', color: lane.active !== false ? '#4DBDBD' : 'rgba(160,40,40,0.85)', border: 'none', padding: '3px 8px', cursor: 'pointer', minHeight: 24, borderRadius: 0 }}>{lane.active !== false ? 'ON' : 'OFF'}</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...sans, fontSize: 14, fontWeight: 500, color: 'rgba(255,255,255,0.90)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lane.name}</div>
        </div>
        <span style={{ ...mono, fontSize: 7, letterSpacing: '0.10em', padding: '3px 8px', background: statusBg, color: statusColor, border: `1px solid ${statusBd}`, borderRadius: 0 }}>{status}</span>
        <span style={{ ...mono, fontSize: 10, color: T.text2, minWidth: 32 }}>{fmtPct(lane.probability)}</span>
        <span style={{ ...mono, fontSize: 10, color: T.text2, minWidth: 70 }}>{fmtX(lane.multiple_low)}-{fmtX(lane.multiple_high)}</span>
        <span style={{ ...mono, fontSize: 12, fontWeight: 500, color: flashP50 ? '#4DBDBD' : '#C4956A', minWidth: 60, transition: 'color 1s ease' }}>{fmt$(lane.p50)}</span>
        <span style={{ ...mono, fontSize: 9, color: retradeColor, minWidth: 36 }}>{lane.retrade_pct ? Math.round(lane.retrade_pct * 100) + '%' : '--'}</span>
        <span style={{ ...mono, fontSize: 10, color: T.textMuted, transform: expanded ? 'rotate(90deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>&#9654;</span>
      </div>

      {/* Expanded detail */}
      <div style={{ maxHeight: expanded ? 2000 : 0, overflow: 'hidden', transition: 'max-height 300ms cubic-bezier(.16,1,.3,1)' }}>
        <div style={{ padding: '0 16px 20px 20px' }}>

          {/* Section A: Lane Header */}
          <div style={{ background: T.surface, padding: '16px 16px 12px', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span style={{ ...serif, fontSize: 22, fontWeight: 300, color: T.text1 }}>{lane.name}</span>
              <span style={{ ...mono, fontSize: 7, padding: '2px 8px', background: statusBg, color: statusColor }}>{status}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
              <div><div style={{ ...mono, fontSize: 7, color: T.textMuted, letterSpacing: '0.14em', marginBottom: 2 }}>PROBABILITY</div><div style={{ ...sans, fontSize: 14, color: T.text1 }}>{fmtPct(lane.probability)}</div></div>
              <div><div style={{ ...mono, fontSize: 7, color: T.textMuted, letterSpacing: '0.14em', marginBottom: 2 }}>MULTIPLE RANGE</div><div style={{ ...sans, fontSize: 14, color: T.text1 }}>{fmtX(lane.multiple_low)}-{fmtX(lane.multiple_high)}</div></div>
              <div><div style={{ ...mono, fontSize: 7, color: T.textMuted, letterSpacing: '0.14em', marginBottom: 2 }}>P50 TODAY</div><div style={{ ...sans, fontSize: 14, color: T.copperText }}>{fmt$(lane.p50)}</div></div>
              <div><div style={{ ...mono, fontSize: 7, color: T.textMuted, letterSpacing: '0.14em', marginBottom: 2 }}>RETRADE RISK</div><div style={{ ...sans, fontSize: 14, color: T.amber }}>{lane.retrade_pct ? Math.round(lane.retrade_pct * 100) + '%' : '--'}</div></div>
            </div>
          </div>

          {/* Section B: Buyer Profile */}
          {profile.profile && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ ...mono, fontSize: 8, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.copperText, marginBottom: 8 }}>WHO BUYS THIS WAY</div>
              <div style={{ ...sans, fontSize: 13, color: T.text2, lineHeight: 1.7, marginBottom: 12 }}>{profile.profile}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {profile.deal && (
                  <div style={{ background: T.surface, padding: 12 }}>
                    <div style={{ ...mono, fontSize: 7, color: T.textMuted, letterSpacing: '0.14em', marginBottom: 8 }}>DEAL STRUCTURE</div>
                    <div style={{ ...mono, fontSize: 9, color: T.text2, lineHeight: 1.8 }}>
                      Cash at close: {profile.deal.cash}<br />
                      Earnout: {profile.deal.earnout}<br />
                      Escrow: {profile.deal.escrow}<br />
                      Timeline: {profile.deal.timeline}
                    </div>
                  </div>
                )}
                {profile.priceUp && (
                  <div style={{ background: T.surface, padding: 12 }}>
                    <div style={{ ...mono, fontSize: 7, color: T.textMuted, letterSpacing: '0.14em', marginBottom: 8 }}>WHAT THEY PRICE UP</div>
                    {profile.priceUp.map((p, i) => <div key={i} style={{ ...mono, fontSize: 9, color: T.green, lineHeight: 1.8 }}>+ {p}</div>)}
                  </div>
                )}
                {profile.discount && (
                  <div style={{ background: T.surface, padding: 12 }}>
                    <div style={{ ...mono, fontSize: 7, color: T.textMuted, letterSpacing: '0.14em', marginBottom: 8 }}>WHAT THEY DISCOUNT</div>
                    {profile.discount.map((d, i) => <div key={i} style={{ ...mono, fontSize: 9, color: T.amber, lineHeight: 1.8 }}>- {d}</div>)}
                  </div>
                )}
                {profile.riskTransfer && (
                  <div style={{ background: T.surface, padding: 12 }}>
                    <div style={{ ...mono, fontSize: 7, color: T.textMuted, letterSpacing: '0.14em', marginBottom: 8 }}>HOW THEY TRANSFER RISK</div>
                    <div style={{ ...mono, fontSize: 9, color: T.text2, lineHeight: 1.8 }}>{profile.riskTransfer}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Section C: AIS-Specific Analysis */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ ...mono, fontSize: 8, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.copperText, marginBottom: 8 }}>FOR AMERICAN IRRIGATION SYSTEMS</div>
            {profile.favor && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ ...mono, fontSize: 7, color: T.green, letterSpacing: '0.12em', marginBottom: 4 }}>WORKS IN YOUR FAVOR</div>
                {profile.favor.map((f, i) => <div key={i} style={{ ...sans, fontSize: 12, color: T.text2, lineHeight: 1.7, paddingLeft: 12 }}><span style={{ color: T.green, marginRight: 6 }}>&#9679;</span>{f}</div>)}
              </div>
            )}
            {profile.against && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ ...mono, fontSize: 7, color: T.copperText, letterSpacing: '0.12em', marginBottom: 4 }}>WORKS AGAINST YOU</div>
                {profile.against.map((a, i) => <div key={i} style={{ ...sans, fontSize: 12, color: T.text2, lineHeight: 1.7, paddingLeft: 12 }}><span style={{ color: T.copperText, marginRight: 6 }}>&#9679;</span>{a}</div>)}
              </div>
            )}
            {profile.unlock && (
              <div style={{ background: T.surface, padding: '10px 14px', borderLeft: `2px solid ${statusColor}` }}>
                <div style={{ ...mono, fontSize: 7, color: statusColor, letterSpacing: '0.12em', marginBottom: 4 }}>
                  {status === 'OPEN' ? 'WHAT WOULD UNLOCK FULL PREMIUM' : status === 'CONDITIONAL' ? 'WHAT WOULD UNLOCK THIS LANE' : 'FULL PREPARATION REQUIRED'}
                </div>
                <div style={{ ...sans, fontSize: 12, color: T.text2, lineHeight: 1.7 }}>{profile.unlock}</div>
              </div>
            )}
          </div>

          {/* Section D: Distribution for this lane */}
          <div>
            <div style={{ ...mono, fontSize: 8, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.copperText, marginBottom: 8 }}>DISTRIBUTION FOR THIS LANE</div>
            <div style={{ height: 10, background: T.surface, position: 'relative', marginBottom: 6 }}>
              <div style={{ position: 'absolute', left: `${iqrLeft}%`, width: `${iqrRight - iqrLeft}%`, height: '100%', background: `linear-gradient(90deg, ${T.copper}, ${T.green})` }} />
              <div style={{ position: 'absolute', left: `${medPos}%`, top: -2, width: 2, height: 14, background: T.copper }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', ...mono, fontSize: 8, color: T.textMuted }}>
              <span>P10: {fmt$(p10)}</span><span>P25: {fmt$(p25)}</span><span style={{ color: T.copperText }}>P50: {fmt$(lane.p50)}</span><span>P75: {fmt$(p75)}</span><span>P90: {fmt$(p90)}</span>
            </div>
            <div style={{ ...sans, fontSize: 12, color: T.text2, lineHeight: 1.6, marginTop: 8, fontStyle: 'italic' }}>
              In 50% of simulated outcomes for this buyer type, American Irrigation Systems transacts between {fmt$(p25)} and {fmt$(p75)}.
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

/* -- Market Intelligence Section (collapsible) -- */
function MarketIntelSection({ mi }) {
  const [open, setOpen] = useState(false);
  const timing = mi.timing_signal || mi.timing || '';
  const multiples = mi.current_multiples || mi.multiples || {};
  const btn = mi.between_the_numbers || mi.active_buyers || [];
  const nww = mi.numbers_worth_watching || [];
  const lastRefresh = mi.refreshed_at || mi.updated_at || '';
  const stale = lastRefresh && (Date.now() - new Date(lastRefresh).getTime()) > 14 * 86400000;

  if (!timing && !Object.keys(multiples).length && !btn.length && !nww.length) return null;

  return (
    <div style={{ borderBottom: `1px solid ${T.border}` }}>
      <button onClick={() => setOpen(!open)} style={{ ...mono, fontSize: 8, letterSpacing: '0.22em', textTransform: 'uppercase', color: T.copperText, padding: '14px 20px', background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>MARKET INTELLIGENCE</span>
        <span style={{ ...mono, fontSize: 7, color: T.textMuted }}>
          {lastRefresh ? new Date(lastRefresh).toLocaleDateString() : ''} {stale ? '(STALE)' : ''}
          <span style={{ marginLeft: 8, transform: open ? 'rotate(90deg)' : 'rotate(0)', display: 'inline-block', transition: 'transform 0.2s' }}>&#9654;</span>
        </span>
      </button>
      <div style={{ maxHeight: open ? 3000 : 0, overflow: 'hidden', transition: 'max-height 300ms cubic-bezier(.16,1,.3,1)' }}>
        <div style={{ padding: '0 20px 20px' }}>

          {/* 1. Timing Signal */}
          {timing && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ ...mono, fontSize: 7, color: T.textMuted, letterSpacing: '0.14em', marginBottom: 6 }}>TIMING SIGNAL</div>
              <div style={{ borderLeft: `2px solid ${T.copper}`, padding: '10px 14px', background: T.surface }}>
                <div style={{ ...sans, fontSize: 12, color: T.copperText, lineHeight: 1.6, marginBottom: 8 }}>A prepared business commands a premium in any market condition. In a compressed market, scarcity of quality assets amplifies that premium further.</div>
                <div style={{ ...sans, fontSize: 12, color: T.text2, lineHeight: 1.6 }}>{typeof timing === 'string' ? timing : JSON.stringify(timing)}</div>
              </div>
            </div>
          )}

          {/* 2. Current Multiples */}
          {Object.keys(multiples).length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ ...mono, fontSize: 7, color: T.textMuted, letterSpacing: '0.14em', marginBottom: 6 }}>CURRENT MULTIPLES BY BUYER TYPE</div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>{['LANE', 'FLOOR', 'MEDIAN', 'CEILING', 'SOURCE'].map(h => <th key={h} style={{ ...mono, fontSize: 7, color: T.textMuted, textAlign: 'left', padding: '4px 6px', borderBottom: `1px solid ${T.border}` }}>{h}</th>)}</tr></thead>
                <tbody>
                  {Object.entries(multiples).map(([key, val]) => {
                    if (!val || typeof val !== 'object') return null;
                    return (
                      <tr key={key}>
                        <td style={{ ...sans, fontSize: 11, color: T.text1, padding: '4px 6px', borderBottom: `1px solid ${T.border}`, textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}</td>
                        <td style={{ ...mono, fontSize: 10, color: T.text2, padding: '4px 6px', borderBottom: `1px solid ${T.border}` }}>{fmtX(val.floor || val.low)}</td>
                        <td style={{ ...mono, fontSize: 10, color: T.copperText, padding: '4px 6px', borderBottom: `1px solid ${T.border}` }}>{fmtX(val.median || ((val.floor || val.low || 0) + (val.ceiling || val.high || 0)) / 2)}</td>
                        <td style={{ ...mono, fontSize: 10, color: T.text2, padding: '4px 6px', borderBottom: `1px solid ${T.border}` }}>{fmtX(val.ceiling || val.high)}</td>
                        <td style={{ ...mono, fontSize: 8, color: stale ? T.amber : T.textMuted, padding: '4px 6px', borderBottom: `1px solid ${T.border}` }}>{lastRefresh ? `Perplexity ${new Date(lastRefresh).toLocaleDateString()}` : '--'}{stale ? ' STALE' : ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* 3. Active Buyer Types */}
          {btn.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ ...mono, fontSize: 7, color: T.textMuted, letterSpacing: '0.14em', marginBottom: 6 }}>ACTIVE BUYER TYPES IN SECTOR</div>
              {(Array.isArray(btn) ? btn : [btn]).map((entry, i) => {
                const type = entry.type || entry.buyer_type || '';
                const headline = entry.headline || entry.title || '';
                const summary = entry.summary || entry.description || '';
                if (!type && !headline) return null;
                return (
                  <div key={i} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: `1px solid ${T.border}`, alignItems: 'flex-start' }}>
                    {type && <span style={{ ...mono, fontSize: 7, padding: '2px 6px', background: 'rgba(122,82,48,0.15)', color: T.copperText, flexShrink: 0 }}>{type}</span>}
                    <div>
                      {headline && <div style={{ ...sans, fontSize: 12, color: T.text1 }}>{headline}</div>}
                      {summary && <div style={{ ...sans, fontSize: 11, color: T.text2, lineHeight: 1.6, marginTop: 2 }}>{summary}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* 4. Numbers Worth Watching */}
          {nww.length > 0 && (
            <div>
              <div style={{ ...mono, fontSize: 7, color: T.textMuted, letterSpacing: '0.14em', marginBottom: 6 }}>NUMBERS WORTH WATCHING</div>
              {nww.map((item, i) => (
                <div key={i} style={{ padding: '6px 0', borderBottom: `1px solid ${T.border}` }}>
                  <div style={{ ...sans, fontSize: 13, color: T.text1, marginBottom: 2 }}>{i + 1}. {item.signal || item.title || ''}</div>
                  {(item.why || item.why_it_matters) && <div style={{ ...sans, fontSize: 11, color: T.text2, lineHeight: 1.6 }}>{item.why || item.why_it_matters}</div>}
                  {item.watch && <div style={{ ...mono, fontSize: 9, color: T.copperText, marginTop: 2 }}>Watch: {item.watch}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MarketMappingTab({ eng, engId, onRefresh }) {
  const sentry = parseJ(eng.sentry_preliminary_results);
  const mi = parseJ(eng.market_intel);
  const mults = mi.current_multiples || mi.multiples || {};
  const MULT_MAP = {
    'Strategic': mults.strategic, 'PE Add-On': mults.pe_addon, 'Family Office': mults.family_office,
    'Search Fund': mults.search_fund, 'Independent Sponsor': mults.independent_sponsor,
  };

  const normalizeLane = (l) => {
    const name = l.lane || l.name || 'Unknown';
    const prob = l.lane_probability != null ? l.lane_probability / 100 : (l.probability || 0);
    const multRange = MULT_MAP[name];
    const mLow = multRange ? (multRange.floor || multRange.low || 0) : (l.multiple_low || 0);
    const mHigh = multRange ? (multRange.ceiling || multRange.high || 0) : (l.multiple_high || 0);
    return {
      name, probability: prob,
      multiple_low: mLow, multiple_high: mHigh,
      p50: l.p50 || l.p50_value || l.median || l.value || l.ev_p50 || 0,
      p10: l.p10 || l.p10_value || 0,
      p25: l.p25 || l.p25_value || 0,
      p75: l.p75 || l.p75_value || 0,
      p90: l.p90 || l.p90_value || 0,
      retrade_pct: l.retrade_probability != null ? l.retrade_probability / 100 : (l.retrade_pct || 0),
      active: true,
    };
  };

  const rawLanes = sentry.lanes || [];
  const lanes = rawLanes.length > 0 ? rawLanes.map(normalizeLane) : [
    { name: 'Strategic', probability: 0.65, multiple_low: 7.0, multiple_high: 9.5, p50: 3090000, retrade_pct: 0.30, active: true },
    { name: 'PE Add-On', probability: 0.70, multiple_low: 5.5, multiple_high: 7.0, p50: 2380000, retrade_pct: 0.50, active: true },
    { name: 'Family Office', probability: 0.70, multiple_low: 5.8, multiple_high: 7.8, p50: 2680000, retrade_pct: 0.35, active: true },
    { name: 'Search Fund', probability: 0.55, multiple_low: 5.2, multiple_high: 6.8, p50: 1870000, retrade_pct: 0.55, active: true },
    { name: 'Independent Sponsor', probability: 0.65, multiple_low: 5.0, multiple_high: 6.5, p50: 2170000, retrade_pct: 0.45, active: true },
  ];
  const [activeLanes, setActiveLanes] = useState(lanes);
  useEffect(() => {
    if (lanes && lanes.length > 0) {
      setActiveLanes(lanes);
    }
  }, [lanes]);
  const [expandedLane, setExpandedLane] = useState(null);
  const [scenario, setScenario] = useState('base');
  const [running, setRunning] = useState(false);
  const [sentryError, setSentryError] = useState(null);
  const [sentrySuccess, setSentrySuccess] = useState(null);
  const [flashP50, setFlashP50] = useState(false);

  // Presentation 1 preview state
  const [previewHtml, setPreviewHtml] = useState(() => {
    const _ps = parseJ(eng.presentation_scripts);
    return (_ps.presentation_1_html && _ps.presentation_1_html.length > 100) ? _ps.presentation_1_html : null;
  });
  const [previewGenerating, setPreviewGenerating] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [scriptOpen, setScriptOpen] = useState(false);
  const [previewScriptTab, setPreviewScriptTab] = useState('opening');
  const [pdfReady, setPdfReady] = useState(true);
  const [pdfHover, setPdfHover] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [publishError, setPublishError] = useState('');
  const [publishLinks, setPublishLinks] = useState([]);
  const [publishCopied, setPublishCopied] = useState('');
  // Mirrors the 5 section tabs shown above the preview iframe in Market Mapping.
  // This is the source of truth for "Open Deck" deep-linking (deck mode uses these hashes).
  const [previewTab, setPreviewTab] = useState('#marketContext');

  const PREVIEW_TABS = [
    { hash: '#marketContext', label: 'MARKET CONTEXT' },
    { hash: '#buyerLanes', label: 'BUYER LANES' },
    { hash: '#distributions', label: 'DISTRIBUTIONS' },
    { hash: '#dealStructure', label: 'DEAL STRUCTURE' },
    { hash: '#whatToConsider', label: 'WHAT TO CONSIDER' },
  ];

  useEffect(() => {
    fetch(`${WORKFLOW_BASE}/pdf-status/${engId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data && data.presentation_1 === false) setPdfReady(false); })
      .catch(() => {});
  }, [engId]);

  const handleDownloadPdf = async (doc) => {
    try {
      setPdfLoading(true);
      // Prefer a direct navigation download (GET endpoint) to avoid cross-origin
      // fetch/blob issues that can appear as "button does nothing".
      const url = `${WORKFLOW_BASE}/generate-pdf/${engId}?doc=${doc}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('PDF fetch failed:', err);
    } finally {
      setPdfLoading(false);
    }
  };

  useEffect(() => {
    const _ps = parseJ(eng.presentation_scripts);
    if (_ps.presentation_1_html && _ps.presentation_1_html.length > 100) {
      setPreviewHtml(_ps.presentation_1_html);
    }
  }, [eng.presentation_scripts]);

  const role = getRole();
  const dq = sentry.data_quality || {};
  const dqScore = typeof dq.score === 'number' ? dq.score : 60;

  const scenarios = {
    conservative: { label: 'Conservative', defensibility: 'STRONG' },
    base: { label: 'Base / Normalized', defensibility: 'STANDARD' },
    aggressive: { label: 'Aggressive', defensibility: 'CONTESTED' },
  };

  const ebitdaLines = [
    {
      item: 'Net Income (IRS 1120-S)',
      amount: 278495,
      def: 'HIGH',
      defensibility: 'high',
      notes: 'FY2025 IRS 1120-S Line 21 -- verified',
    },
    {
      item: 'Depreciation (MACRS)',
      amount: 61929,
      def: 'HIGH',
      defensibility: 'high',
      notes: 'Form 4562 Line 14 -- recurring non-cash',
    },
    {
      item: 'Interest Expense',
      amount: 9477,
      def: 'HIGH',
      defensibility: 'high',
      notes: 'Other deductions statement -- non-cash',
    },
    {
      item: 'Section 179 (one-time vehicles)',
      amount: 86481,
      def: 'HIGH',
      defensibility: 'high',
      notes: 'Chevy Express + Ford Expedition -- non-recurring',
    },
    {
      item: 'Owner Comp Add-Back',
      amount: 139275,
      def: 'MEDIUM',
      defensibility: 'medium',
      notes: 'Actual $229,275 minus $90K market rate',
    },
    {
      item: 'Rent Expense',
      amount: 42000,
      def: 'MEDIUM',
      defensibility: 'medium',
      notes: 'Month-to-month lease -- needs market comp',
    },
    {
      item: 'Personal Vehicle',
      amount: 18000,
      def: 'LOW',
      defensibility: 'low',
      notes: 'Needs documentation -- buyer will challenge',
    },
    {
      item: 'Above-market Salary Adj',
      amount: 40000,
      def: 'LOW',
      defensibility: 'low',
      notes: 'Discretionary -- needs comp study to defend',
    },
  ];

  const visibleLines = ebitdaLines.filter(line => {
    if (scenario === 'conservative') return line.defensibility === 'high';
    if (scenario === 'base') return ['high', 'medium'].includes(line.defensibility);
    return true; // aggressive shows all
  });

  const ebitdaTotal = visibleLines.reduce((sum, line) => sum + (line.amount || 0), 0);

  const toggleLane = (lane) => {
    setActiveLanes(prev => prev.map(l => l.name === lane.name ? { ...l, active: !l.active } : l));
  };

  const handleRunSentry = async () => {
    setRunning(true);
    setSentryError(null);
    setSentrySuccess(null);
    try {
      const apiKey = process.env.REACT_APP_INTERNAL_API_KEY || localStorage.getItem('cordis_api_key') || '';
      const res = await fetch(`${WORKFLOW_BASE}/run-sentry/${engId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          baseline: scenario,
          ebitda_override: ebitdaTotal,
          engagement_id: engId,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      if (onRefresh) await onRefresh();
      setFlashP50(true);
      setSentrySuccess('Simulation complete. Lane distributions updated.');
      setTimeout(() => setFlashP50(false), 1000);
      setTimeout(() => setSentrySuccess(null), 3000);
    } catch (err) {
      console.error('SENTRY run failed:', err);
      setSentryError('Simulation failed. Check connection and try again.');
      setRunning(false);
      return;
    }
    setRunning(false);
  };

  const handlePreviewRegenerate = async () => {
    setPreviewGenerating(true);
    setPreviewError('');
    try {
      const apiKey = process.env.REACT_APP_INTERNAL_API_KEY || localStorage.getItem('cordis_api_key') || '';
      const res = await fetch(`${WORKFLOW_BASE}/presentation/1/generate/${engId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          baseline: scenario,
          ebitda_override: ebitdaTotal,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { data } = await supabase.from('cordis_engagements').select('presentation_scripts').eq('id', engId).single();
      const _rps = parseJ(data && data.presentation_scripts);
      if (_rps.presentation_1_html && _rps.presentation_1_html.length > 100) {
        setPreviewHtml(_rps.presentation_1_html);
      } else {
        setPreviewError('Generation completed but HTML not found in presentation_scripts.');
      }
    } catch (err) {
      setPreviewError('Generation failed. Check that Render has deployed the latest backend.');
    }
    setPreviewGenerating(false);
  };

  const handlePublishV1 = async () => {
    setPublishLoading(true);
    setPublishError('');
    setPublishLinks([]);
    try {
      const apiKey = process.env.REACT_APP_INTERNAL_API_KEY || localStorage.getItem('cordis_api_key') || '';
      const res = await fetch(`${WORKFLOW_BASE}/publish/${encodeURIComponent(engId)}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(apiKey ? { 'Authorization': `Bearer ${apiKey}` } : {}),
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(formatWorkflowApiError(data, res.status) || `HTTP ${res.status}`);
      }
      const links = normalizePublishLinks(data);
      if (!links.length) {
        setPublishError('Publish succeeded but no links were returned.');
      } else {
        setPublishLinks(links);
      }
    } catch (err) {
      setPublishError(err.message || 'Publish failed. Check session, Supabase table, and deployment.');
    }
    setPublishLoading(false);
  };

  const leftPanel = (
    <div>
      {/* Market Intelligence (collapsible, above EBITDA) */}
      <MarketIntelSection mi={mi} />

      <TabSection title="EBITDA Builder">
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {['conservative', 'base', 'aggressive'].map(s => {
            const active = scenario === s;
            return (
              <button
                key={s}
                onClick={() => setScenario(s)}
                style={{
                  ...mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase',
                  padding: '8px 14px',
                  background: active ? '#C4956A' : 'transparent',
                  color: active ? '#07192A' : 'rgba(255,255,255,0.45)',
                  border: active ? '1px solid #C4956A' : '1px solid rgba(255,255,255,0.15)',
                  cursor: 'pointer', borderRadius: 0, minHeight: 32,
                }}
              >{s}</button>
            );
          })}
        </div>
        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>{['ITEM', 'AMOUNT', 'DEF.', 'NOTES'].map(h => <th key={h} style={{ ...mono, fontSize: 8, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)', textAlign: 'left', padding: '8px 14px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>{h}</th>)}</tr></thead>
          <tbody>
            {visibleLines.map((line, i) => {
              const defStyle = line.def === 'HIGH'
                ? { bg: 'rgba(26,107,107,0.20)', fg: '#4DBDBD', bd: 'rgba(26,107,107,0.30)' }
                : line.def === 'MEDIUM'
                  ? { bg: 'rgba(122,82,48,0.20)', fg: '#C4956A', bd: 'rgba(122,82,48,0.30)' }
                  : { bg: 'rgba(160,40,40,0.20)', fg: '#E07070', bd: 'rgba(160,40,40,0.30)' };
              return (
                <tr key={i}>
                  <td style={{ ...sans, fontSize: 13, color: 'rgba(255,255,255,0.85)', padding: '12px 14px', borderBottom: '1px solid rgba(255,255,255,0.04)', minHeight: 44 }}>{line.item}</td>
                  <td style={{ ...mono, fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.90)', padding: '12px 14px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>{fmt$(line.amount)}</td>
                  <td style={{ padding: '12px 14px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}><span style={{ ...mono, fontSize: 7, letterSpacing: '0.10em', textTransform: 'uppercase', padding: '2px 8px', background: defStyle.bg, color: defStyle.fg, border: `1px solid ${defStyle.bd}`, borderRadius: 0 }}>{line.def}</span></td>
                  <td style={{ ...mono, fontSize: 9, color: 'rgba(255,255,255,0.35)', fontStyle: 'italic', padding: '12px 14px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>{line.notes}</td>
                </tr>
              );
            })}
            <tr><td colSpan={4} style={{ background: 'rgba(122,82,48,0.10)', borderTop: '1px solid rgba(122,82,48,0.30)', borderBottom: '1px solid rgba(122,82,48,0.30)', padding: '14px' }}><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span style={{ ...mono, fontSize: 9, letterSpacing: '0.20em', textTransform: 'uppercase', color: '#C4956A' }}>EBITDA ({scenarios[scenario].label})</span><span style={{ ...serif, fontSize: 28, fontWeight: 300, color: '#C4956A', textAlign: 'right' }}>{fmt$(ebitdaTotal)}</span></div><div style={{ ...mono, fontSize: 8, color: scenarios[scenario].defensibility === 'STRONG' ? '#4DBDBD' : scenarios[scenario].defensibility === 'STANDARD' ? '#C4956A' : '#E07070', marginTop: 4 }}>Defensibility: {scenarios[scenario].defensibility}</div></td></tr>
          </tbody>
        </table>
        </div>
        <div style={{ marginTop: 16, padding: '10px 12px', background: T.elevated }}>
          <div style={{ ...mono, fontSize: 8, color: T.textMuted, marginBottom: 8 }}>SENTRY BASELINE</div>
          <div style={{ display: 'flex', gap: 12 }}>
            {['conservative', 'base', 'aggressive'].map(s => (
              <label key={s} style={{ ...mono, fontSize: 9, color: scenario === s ? T.copperText : T.text2, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                <input type="radio" name="baseline" checked={scenario === s} onChange={() => setScenario(s)} style={{ accentColor: T.copper }} />{s.toUpperCase()}
              </label>
            ))}
          </div>
        </div>
      </TabSection>

      <TabSection title="Buyer Lanes">
        <style>{`@keyframes labLanesPulse { 0%,100% { opacity: 1 } 50% { opacity: 0.5 } }`}</style>
        <div style={{ ...mono, fontSize: 7.5, color: T.textMuted, display: 'flex', gap: 10, padding: '0 0 8px', borderBottom: `1px solid ${T.border}`, alignItems: 'center' }}>
          <span style={{ width: 32 }} /><span style={{ flex: 1 }}>LANE</span><span style={{ minWidth: 48 }}>STATUS</span><span style={{ minWidth: 32 }}>PROB</span><span style={{ minWidth: 70 }}>MULTIPLE</span><span style={{ color: T.copperText, minWidth: 60 }}>P50</span><span style={{ minWidth: 36 }}>RETRADE</span><span style={{ width: 12 }} />
        </div>
        <div style={{ animation: running ? 'labLanesPulse 1.6s ease-in-out infinite' : 'none' }}>
          {activeLanes.map((lane, i) => (
            <LaneDetailCard
              key={i}
              lane={lane}
              onToggle={toggleLane}
              expanded={expandedLane === lane.name}
              onExpand={() => setExpandedLane(expandedLane === lane.name ? null : lane.name)}
              flashP50={flashP50}
            />
          ))}
        </div>
      </TabSection>

      <div style={{ padding: 20 }}>
        <CopperButton label={running ? 'Running simulation...' : 'Re-run SENTRY'} onClick={handleRunSentry} disabled={running} fullWidth />
        {sentryError && (
          <div style={{ ...mono, fontSize: 9, color: 'rgba(160,40,40,0.85)', marginTop: 8 }}>
            {sentryError}
          </div>
        )}
        {sentrySuccess && (
          <div style={{ ...mono, fontSize: 9, color: '#4DBDBD', marginTop: 8 }}>
            {sentrySuccess}
          </div>
        )}
      </div>
    </div>
  );

  const hasPreviewHtml = !!previewHtml && previewHtml.length > 100;
  const psStructured = parseJ(eng.presentation_scripts);
  const scriptData = psStructured.presentation_1 || {};
  const PREVIEW_SCRIPT_TABS = ['OPENING', 'BUYER LANES', 'NET AT CLOSE', 'INTERVENTIONS', 'CLOSING'];
  const currentSection = scriptData[previewScriptTab] || {};
  const OPENING_FALLBACK = {
    say: 'Welcome, Dennis. Today we are going to walk through the market landscape for AIS and show you exactly where buyers are positioned.',
    ask: 'Before we begin, has anything changed since our last conversation that I should know about?',
    listen_for: 'New information about timing, deal structure preferences, or any concerns about the process.',
    pushback: 'These ranges are based on verified comparable transactions in your sector and geography. Let me show you the specific data points that support this range.',
    pushback_prompt: 'That multiple seems high.',
    transition: 'Now let us look at each buyer lane individually.',
  };
  const sectionToRender = (previewScriptTab === 'opening' && Object.keys(currentSection).length === 0)
    ? OPENING_FALLBACK
    : currentSection;

  const rightPanel = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', position: 'relative' }}>
      {/* Sticky header bar — FIX 9 */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '0 16px', background: '#0A1828',
        borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0, minHeight: 44,
        gap: 8, flexWrap: 'wrap',
      }}>
        <div style={{ ...mono, fontSize: 8, color: '#C4956A', letterSpacing: '0.20em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
          Presentation 1 Preview
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button
            onClick={() => {
              window.open(`${WORKFLOW_BASE}/presentation/1/${engId}?mode=deck${previewTab}`, '_blank', 'noopener,noreferrer');
            }}
            style={{
              ...mono, fontSize: 8, letterSpacing: '0.10em', textTransform: 'uppercase',
              padding: '6px 14px', background: 'transparent', color: 'rgba(255,255,255,0.75)',
              border: '1px solid rgba(255,255,255,0.20)', borderRadius: 0, cursor: 'pointer', height: 30,
            }}
          >
            Open Deck
          </button>
          {pdfReady && (
            <button
              onClick={() => handleDownloadPdf('presentation_1')}
              disabled={pdfLoading}
              style={{
                ...mono, fontSize: 8, letterSpacing: '0.10em', textTransform: 'uppercase',
                padding: '6px 14px', background: '#7A5230', color: '#FFFFFF',
                border: 'none', borderRadius: 0, cursor: pdfLoading ? 'not-allowed' : 'pointer', height: 30,
                opacity: pdfLoading ? 0.6 : 1,
              }}
            >{pdfLoading ? 'Generating...' : 'Download PDF'}</button>
          )}
          <button
            onClick={handlePublishV1}
            disabled={publishLoading}
            style={{
              ...mono, fontSize: 8, letterSpacing: '0.10em', textTransform: 'uppercase',
              padding: '6px 14px', background: 'transparent', color: 'rgba(255,255,255,0.75)',
              border: '1px solid rgba(255,255,255,0.20)', borderRadius: 0,
              cursor: publishLoading ? 'not-allowed' : 'pointer', height: 30,
              opacity: publishLoading ? 0.6 : 1,
            }}
            title="Publish Market Mapping v1 (returns share links)"
          >
            {publishLoading ? 'Publishing...' : 'Publish v1'}
          </button>
          <button
            onClick={handlePreviewRegenerate}
            disabled={previewGenerating}
            style={{
              ...mono, fontSize: 8, letterSpacing: '0.10em', textTransform: 'uppercase',
              padding: '6px 14px', background: '#7A5230', color: '#FFFFFF',
              border: 'none', borderRadius: 0, cursor: previewGenerating ? 'not-allowed' : 'pointer',
              opacity: previewGenerating ? 0.6 : 1, height: 30,
            }}
          >
            {previewGenerating ? 'Generating...' : 'Regenerate'}
          </button>
          <button
            onClick={() => setScriptOpen(true)}
            style={{
              ...mono, fontSize: 8, letterSpacing: '0.10em', textTransform: 'uppercase',
              padding: '6px 14px', background: 'transparent', color: 'rgba(255,255,255,0.65)',
              border: '1px solid rgba(255,255,255,0.20)', borderRadius: 0, cursor: 'pointer',
              height: 30,
            }}
          >
            Open Script
          </button>
        </div>
      </div>

      {(publishError || publishLinks.length > 0) && (
        <div style={{
          padding: '10px 16px',
          background: '#0A1828',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          flexShrink: 0,
        }}>
          {publishError && (
            <div style={{ ...mono, fontSize: 9, color: 'rgba(224,112,112,0.95)' }}>
              {publishError}
            </div>
          )}
          {publishLinks.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {publishLinks.map((l, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ ...mono, fontSize: 8, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.10em', textTransform: 'uppercase' }}>
                    {l.label}
                  </div>
                  <a href={l.url} target="_blank" rel="noopener noreferrer" style={{ ...mono, fontSize: 9, color: '#C4956A', textDecoration: 'none' }}>
                    {l.url}
                  </a>
                  <button
                    onClick={async () => {
                      const ok = await copyTextToClipboard(l.url);
                      if (ok) {
                        setPublishCopied(l.url);
                        setTimeout(() => setPublishCopied(''), 1200);
                      }
                    }}
                    style={{
                      ...mono, fontSize: 8, letterSpacing: '0.10em', textTransform: 'uppercase',
                      padding: '4px 10px',
                      background: 'transparent',
                      color: publishCopied === l.url ? '#4DBDBD' : 'rgba(255,255,255,0.70)',
                      border: '1px solid rgba(255,255,255,0.20)',
                      borderRadius: 0,
                      cursor: 'pointer',
                      height: 26,
                    }}
                  >
                    {publishCopied === l.url ? 'Copied' : 'Copy'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Preview section tabs (React-owned, matches deck deep links) */}
      {hasPreviewHtml && (
        <div style={{
          display: 'flex', gap: 0,
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          background: '#0A1828',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          flexShrink: 0,
        }}>
          {PREVIEW_TABS.map((t) => {
            const active = previewTab === t.hash;
            return (
              <button
                key={t.hash}
                type="button"
                onClick={() => setPreviewTab(t.hash)}
                style={{
                  ...mono,
                  fontSize: 8,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  padding: '10px 14px',
                  border: 'none',
                  borderBottom: active ? '2px solid #C4956A' : '2px solid transparent',
                  background: 'transparent',
                  color: active ? '#FFFFFF' : 'rgba(255,255,255,0.45)',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Iframe / placeholder */}
      <div style={{ flex: 1, width: '100%', minHeight: 0, overflow: 'hidden' }}>
        {hasPreviewHtml ? (
          <iframe
            srcDoc={previewHtml}
            title="Presentation 1 Preview"
            style={{ width: '100%', height: '100%', border: 'none', display: 'block', background: '#FAFAF8' }}
          />
        ) : (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            height: '100%', background: '#07192A', padding: 40,
          }}>
            <div style={{ ...mono, fontSize: 8, letterSpacing: '0.28em', textTransform: 'uppercase', color: T.copperText, marginBottom: 12 }}>
              Presentation 1
            </div>
            <div style={{ ...sans, fontSize: 14, color: T.textMuted, marginBottom: 24 }}>
              Not yet generated
            </div>
            <button
              onClick={handlePreviewRegenerate}
              disabled={previewGenerating}
              style={{
                ...mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase',
                background: T.copper, color: '#F5F0E8', border: 'none', borderRadius: 0,
                padding: '12px 24px', cursor: previewGenerating ? 'not-allowed' : 'pointer',
                minHeight: 44, opacity: previewGenerating ? 0.5 : 1,
              }}
            >
              {previewGenerating ? 'Generating...' : 'Generate'}
            </button>
            {previewError && (
              <div style={{ ...mono, fontSize: 9, color: T.red, marginTop: 12, textAlign: 'center', maxWidth: 400 }}>
                {previewError}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Overlay */}
      {scriptOpen && (
        <div
          onClick={() => setScriptOpen(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.4)', zIndex: 199,
          }}
        />
      )}

      {/* Slide-out script popout */}
      <div
        style={{
          position: 'fixed', top: 0, right: 0, width: 420, height: '100vh',
          background: '#0D2438', boxShadow: '-4px 0 24px rgba(0,0,0,0.4)',
          zIndex: 200, transform: scriptOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 300ms ease', display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0,
        }}>
          <div style={{ ...mono, fontSize: 8, color: T.copperText, letterSpacing: '0.18em', textTransform: 'uppercase' }}>
            Presenter Script
          </div>
          <button
            onClick={() => setScriptOpen(false)}
            style={{
              background: 'none', border: 'none', color: 'rgba(255,255,255,0.50)',
              fontSize: 18, cursor: 'pointer', padding: '0 4px', lineHeight: 1,
            }}
            aria-label="Close script"
          >×</button>
        </div>

        <div style={{
          display: 'flex', gap: 0, borderBottom: `1px solid ${T.border}`, flexShrink: 0, overflowX: 'auto',
        }}>
          {PREVIEW_SCRIPT_TABS.map(t => {
            const key = t.toLowerCase().replace(/ /g, '_');
            const active = previewScriptTab === key;
            return (
              <button
                key={t}
                onClick={() => setPreviewScriptTab(key)}
                style={{
                  ...mono, fontSize: 8, letterSpacing: '0.12em', textTransform: 'uppercase',
                  padding: '12px 16px', background: 'none', border: 'none',
                  borderBottom: active ? `2px solid ${T.copper}` : '2px solid transparent',
                  color: active ? T.text1 : T.textMuted, cursor: 'pointer',
                  whiteSpace: 'nowrap', minHeight: 40,
                }}
              >{t}</button>
            );
          })}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {sectionToRender.say && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ ...mono, fontSize: 8, color: T.copperText, marginBottom: 4 }}>SAY:</div>
              <div style={{ ...sans, fontSize: 14, color: T.text1, lineHeight: 1.6 }}>{sectionToRender.say}</div>
            </div>
          )}
          {sectionToRender.ask && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ ...mono, fontSize: 8, color: T.copperText, marginBottom: 4 }}>ASK:</div>
              <div style={{ ...sans, fontSize: 14, color: T.text1, fontStyle: 'italic', lineHeight: 1.6 }}>{sectionToRender.ask}</div>
            </div>
          )}
          {sectionToRender.listen_for && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ ...mono, fontSize: 8, color: T.textMuted, marginBottom: 4 }}>LISTEN FOR:</div>
              <div style={{ ...sans, fontSize: 13, color: T.text2, lineHeight: 1.6 }}>{sectionToRender.listen_for}</div>
            </div>
          )}
          {sectionToRender.pushback && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ ...mono, fontSize: 8, color: T.amber, marginBottom: 4 }}>
                PUSHBACK{sectionToRender.pushback_prompt ? ` -- "${sectionToRender.pushback_prompt}":` : ':'}
              </div>
              <div style={{ ...sans, fontSize: 13, color: T.text2, lineHeight: 1.6 }}>{sectionToRender.pushback}</div>
            </div>
          )}
          {sectionToRender.transition && (
            <div>
              <div style={{ ...mono, fontSize: 8, color: T.textMuted, marginBottom: 4 }}>TRANSITION:</div>
              <div style={{ ...sans, fontSize: 13, color: T.text2, fontStyle: 'italic', lineHeight: 1.6 }}>{sectionToRender.transition}</div>
            </div>
          )}
          {Object.keys(sectionToRender).length === 0 && (
            <div style={{ ...mono, fontSize: 9, color: T.textMuted, textAlign: 'center', marginTop: 40 }}>
              Script content not yet generated for this section.
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return <SplitScreen left={leftPanel} right={rightPanel} evenSplit />;
}

/* ═══════════════════════════════════════════
   TAB 3 -- PRESENTATION 1
   ═══════════════════════════════════════════ */

function Presentation1Tab({ eng, engId }) {
  const [scriptTab, setScriptTab] = useState('opening');
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');
  const [localHtml, setLocalHtml] = useState(null);
  const SCRIPT_TABS = ['OPENING', 'BUYER LANES', 'NET AT CLOSE', 'INTERVENTIONS', 'CLOSING'];

  const ps = parseJ(eng.presentation_scripts);
  const p1 = ps.presentation_1 || {};

  // On mount + when eng updates: read presentation_1_html from presentation_scripts JSON
  useEffect(() => {
    const _ps = parseJ(eng.presentation_scripts);
    if (_ps.presentation_1_html && _ps.presentation_1_html.length > 100) {
      setLocalHtml(_ps.presentation_1_html);
      return;
    }
    // If not in prop, fetch directly from Supabase
    supabase.from('cordis_engagements').select('presentation_scripts').eq('id', engId).single()
      .then(({ data }) => {
        const fetched = parseJ(data && data.presentation_scripts);
        if (fetched.presentation_1_html && fetched.presentation_1_html.length > 100) {
          setLocalHtml(fetched.presentation_1_html);
        }
      })
      .catch(() => {});
  }, [eng.presentation_scripts, engId]);

  const handleGenerate = async () => {
    setGenerating(true);
    setGenError('');
    try {
      const apiKey = process.env.REACT_APP_INTERNAL_API_KEY || localStorage.getItem('cordis_api_key') || '';
      const res = await fetch(`${WORKFLOW_BASE}/presentation/1/generate/${engId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Refetch from Supabase to get the saved HTML
      const { data } = await supabase.from('cordis_engagements').select('presentation_scripts').eq('id', engId).single();
      const _rps = parseJ(data && data.presentation_scripts);
      if (_rps.presentation_1_html && _rps.presentation_1_html.length > 100) {
        setLocalHtml(_rps.presentation_1_html);
      } else {
        setGenError('Generation completed but HTML not found in presentation_scripts. Check backend logs.');
      }
    } catch (err) {
      setGenError('Generation failed. Check that Render has deployed the latest backend.');
    }
    setGenerating(false);
  };

  const hasHtml = !!localHtml && localHtml.length > 100;

  const leftPanel = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Top bar */}
      <div style={{ display: 'flex', gap: 8, padding: '12px 20px', flexWrap: 'wrap', borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
        <a href={`/api/engagement/${engId}/net-at-close`} target="_blank" rel="noopener noreferrer" style={{ ...mono, fontSize: 8, color: T.copperText, textDecoration: 'none', padding: '6px 12px', border: `1px solid ${T.copper}`, minHeight: 32, display: 'inline-flex', alignItems: 'center', borderRadius: 0 }}>VIEW NET-AT-CLOSE</a>
        <a href={`/api/engagement/${engId}/pre-call-brief`} target="_blank" rel="noopener noreferrer" style={{ ...mono, fontSize: 8, color: T.copperText, textDecoration: 'none', padding: '6px 12px', border: `1px solid ${T.copper}`, minHeight: 32, display: 'inline-flex', alignItems: 'center', borderRadius: 0 }}>OPEN PRE-CALL BRIEF</a>
        <a href={`/api/presentation/1/${engId}/script`} target="_blank" rel="noopener noreferrer" style={{ ...mono, fontSize: 8, color: T.copperText, textDecoration: 'none', padding: '6px 12px', border: `1px solid ${T.copper}`, minHeight: 32, display: 'inline-flex', alignItems: 'center', borderRadius: 0 }}>VIEW SCRIPT ONLY</a>
        <button onClick={handleGenerate} disabled={generating} style={{ ...mono, fontSize: 8, letterSpacing: '0.10em', textTransform: 'uppercase', padding: '6px 12px', background: T.copper, color: '#F5F0E8', border: 'none', cursor: generating ? 'not-allowed' : 'pointer', minHeight: 32, borderRadius: 0, opacity: generating ? 0.5 : 1 }}>
          {generating ? 'Regenerating...' : 'REGENERATE'}
        </button>
      </div>

      {/* Content area */}
      <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        {hasHtml ? (
          <iframe
            srcDoc={localHtml}
            style={{ width: '100%', height: '100%', minHeight: 'calc(100vh - 120px)', border: 'none', display: 'block', background: '#FAFAF8' }}
            title="Presentation 1 -- Market Mapping"
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#0D2438', padding: 40 }}>
            <div style={{ ...mono, fontSize: 9, letterSpacing: '0.28em', textTransform: 'uppercase', color: T.copperText, marginBottom: 12 }}>PRESENTATION 1</div>
            <div style={{ ...serif, fontSize: 28, fontWeight: 300, color: T.text1, marginBottom: 8 }}>Market Mapping</div>
            <div style={{ ...sans, fontSize: 14, color: T.text2, marginBottom: 24 }}>Not yet generated.</div>
            <button onClick={handleGenerate} disabled={generating} style={{ ...mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', background: T.copper, color: '#F5F0E8', border: 'none', padding: '12px 24px', cursor: generating ? 'not-allowed' : 'pointer', minHeight: 44, borderRadius: 0, opacity: generating ? 0.5 : 1 }}>
              {generating ? 'Generating presentation...' : 'Generate Presentation'}
            </button>
            {genError && <div style={{ ...mono, fontSize: 9, color: T.red, marginTop: 12 }}>{genError}</div>}
          </div>
        )}
      </div>
    </div>
  );

  const rightPanel = (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: `1px solid ${T.border}` }}>
        {SCRIPT_TABS.map(t => (
          <button key={t} onClick={() => setScriptTab(t.toLowerCase().replace(/ /g, '_'))} style={{ ...mono, fontSize: 8, letterSpacing: '0.12em', padding: '8px 12px', background: 'none', border: 'none', borderBottom: scriptTab === t.toLowerCase().replace(/ /g, '_') ? `2px solid ${T.copper}` : '2px solid transparent', color: scriptTab === t.toLowerCase().replace(/ /g, '_') ? T.text1 : T.textMuted, cursor: 'pointer', minHeight: 36 }}>{t}</button>
        ))}
      </div>
      <TabSection title={scriptTab.replace(/_/g, ' ').toUpperCase()}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ ...mono, fontSize: 8, color: T.copperText, marginBottom: 4 }}>SAY:</div>
          <div style={{ ...sans, fontSize: 14, color: T.text1, lineHeight: 1.6 }}>Welcome, Dennis. Today we are going to walk through the market landscape for AIS and show you exactly where buyers are positioned.</div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ ...mono, fontSize: 8, color: T.copperText, marginBottom: 4 }}>ASK:</div>
          <div style={{ ...sans, fontSize: 14, color: T.text1, fontStyle: 'italic', lineHeight: 1.6 }}>Before we begin, has anything changed since our last conversation that I should know about?</div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ ...mono, fontSize: 8, color: T.textMuted, marginBottom: 4 }}>LISTEN FOR:</div>
          <div style={{ ...sans, fontSize: 13, color: T.text2, lineHeight: 1.6 }}>New information about timing, deal structure preferences, or any concerns about the process.</div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ ...mono, fontSize: 8, color: T.amber, marginBottom: 4 }}>PUSHBACK -- "That multiple seems high.":</div>
          <div style={{ ...sans, fontSize: 13, color: T.text2, lineHeight: 1.6 }}>These ranges are based on verified comparable transactions in your sector and geography. Let me show you the specific data points that support this range.</div>
        </div>
        <div>
          <div style={{ ...mono, fontSize: 8, color: T.textMuted, marginBottom: 4 }}>TRANSITION:</div>
          <div style={{ ...sans, fontSize: 13, color: T.text2, fontStyle: 'italic', lineHeight: 1.6 }}>Now let us look at each buyer lane individually.</div>
        </div>
      </TabSection>
    </div>
  );

  return <SplitScreen left={leftPanel} right={rightPanel} leftWide />;
}

/* ═══════════════════════════════════════════
   TAB 4 -- RETRADE REHEARSAL
   ═══════════════════════════════════════════ */

function RetradeRehearsalTab({ eng, engId, onRefresh }) {
  const [html, setHtml] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');
  const [pdfReady, setPdfReady] = useState(false);
  const [pdfHover, setPdfHover] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [publishError, setPublishError] = useState('');
  const [publishLinks, setPublishLinks] = useState([]);
  const [publishCopied, setPublishCopied] = useState('');

  useEffect(() => {
    fetch(`${WORKFLOW_BASE}/pdf-status/${engId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data && data.presentation_2) setPdfReady(true); })
      .catch(() => {});
  }, [engId]);

  const handleDownloadPdf = async (doc) => {
    try {
      setPdfLoading(true);
      const url = `${WORKFLOW_BASE}/generate-pdf/${engId}?doc=${doc}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('PDF fetch failed:', err);
    } finally {
      setPdfLoading(false);
    }
  };

  const pdfBtnStyle = {
    fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase',
    background: pdfHover ? 'rgba(122,82,48,.08)' : 'transparent',
    color: '#7A5230', border: '1px solid #7A5230',
    padding: '8px 16px', cursor: 'pointer', borderRadius: 0,
  };

  useEffect(() => {
    const _ps = parseJ(eng.presentation_scripts);
    if (_ps.presentation_2_html && _ps.presentation_2_html.length > 100) {
      setHtml(_ps.presentation_2_html);
      return;
    }
    supabase.from('cordis_engagements').select('presentation_scripts').eq('id', engId).single()
      .then(({ data }) => {
        const fetched = parseJ(data && data.presentation_scripts);
        if (fetched.presentation_2_html && fetched.presentation_2_html.length > 100) {
          setHtml(fetched.presentation_2_html);
        }
      })
      .catch(() => {});
  }, [eng.presentation_scripts, engId]);

  const handleGenerate = async () => {
    setGenerating(true);
    setGenError('');
    try {
      const res = await fetch(`${WORKFLOW_BASE}/generate-presentation-2/${engId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { data } = await supabase.from('cordis_engagements').select('presentation_scripts').eq('id', engId).single();
      const fetched = parseJ(data && data.presentation_scripts);
      if (fetched.presentation_2_html && fetched.presentation_2_html.length > 100) {
        setHtml(fetched.presentation_2_html);
      } else {
        setGenError('Generation completed but HTML not found.');
      }
      if (onRefresh) onRefresh();
    } catch (err) {
      setGenError('Generation failed. Please try again.');
    }
    setGenerating(false);
  };

  const handlePublishV1 = async () => {
    setPublishLoading(true);
    setPublishError('');
    setPublishLinks([]);
    try {
      const apiKey = process.env.REACT_APP_INTERNAL_API_KEY || localStorage.getItem('cordis_api_key') || '';
      const res = await fetch(`${WORKFLOW_BASE}/publish/${encodeURIComponent(engId)}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(apiKey ? { 'Authorization': `Bearer ${apiKey}` } : {}),
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(formatWorkflowApiError(data, res.status) || `HTTP ${res.status}`);
      }
      const links = normalizePublishLinks(data);
      if (!links.length) {
        setPublishError('Publish succeeded but no links were returned.');
      } else {
        setPublishLinks(links);
      }
    } catch (err) {
      setPublishError(err.message || 'Publish failed. Check session, Supabase table, and deployment.');
    }
    setPublishLoading(false);
  };

  const hasHtml = !!html && html.length > 100;

  // Presenter script extraction
  const ps = parseJ(eng.presentation_scripts);
  const p2Root = ps.presentation_2 || {};
  const script = (p2Root && p2Root.presenter_script) ? p2Root.presenter_script : p2Root;

  const SCRIPT_TABS = ['OPENING', 'RETRADE SCENARIOS', 'NET AT CLOSE', 'INTERVENTIONS', 'CLOSING'];
  const [scriptTab, setScriptTab] = useState('opening');
  const sectionKey = scriptTab;
  const sectionToRender = (script && typeof script === 'object' && !Array.isArray(script)) ? (script[sectionKey] || {}) : {};

  const leftPanel = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {hasHtml && (
        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          padding: '0 16px', background: '#0A1828',
          borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0, minHeight: 44, height: 44,
          alignItems: 'center',
        }}>
          <button
            onClick={() => window.open(`${WORKFLOW_BASE}/presentation/2/${engId}?mode=deck`, '_blank', 'noopener,noreferrer')}
            style={{
              ...mono, fontSize: 8, letterSpacing: '0.10em', textTransform: 'uppercase',
              padding: '6px 14px', background: 'transparent', color: 'rgba(255,255,255,0.75)',
              border: '1px solid rgba(255,255,255,0.20)', borderRadius: 0, cursor: 'pointer', height: 30,
            }}
          >
            Open Deck
          </button>
          <button onClick={handleGenerate} disabled={generating} style={{
            ...mono, fontSize: 8, letterSpacing: '0.10em', textTransform: 'uppercase',
            padding: '6px 14px', background: '#7A5230', color: '#FFFFFF',
            border: 'none', borderRadius: 0, cursor: generating ? 'not-allowed' : 'pointer',
            opacity: generating ? 0.6 : 1, height: 30,
          }}>
            {generating ? 'Running simulation...' : 'Regenerate'}
          </button>
          {pdfReady && (
            <button
              onClick={() => handleDownloadPdf('presentation_2')}
              disabled={pdfLoading}
              onMouseEnter={() => setPdfHover(true)}
              onMouseLeave={() => setPdfHover(false)}
              style={{
                ...mono, fontSize: 8, letterSpacing: '0.10em', textTransform: 'uppercase',
                padding: '6px 14px',
                background: pdfHover ? 'rgba(122,82,48,.18)' : 'transparent',
                color: '#C4956A', border: '1px solid #7A5230',
                borderRadius: 0, cursor: pdfLoading ? 'not-allowed' : 'pointer', height: 30,
                opacity: pdfLoading ? 0.6 : 1,
              }}
            >{pdfLoading ? 'Generating...' : 'Download PDF'}</button>
          )}
          <button
            onClick={handlePublishV1}
            disabled={publishLoading}
            style={{
              ...mono, fontSize: 8, letterSpacing: '0.10em', textTransform: 'uppercase',
              padding: '6px 14px', background: 'transparent', color: 'rgba(255,255,255,0.75)',
              border: '1px solid rgba(255,255,255,0.20)', borderRadius: 0,
              cursor: publishLoading ? 'not-allowed' : 'pointer', height: 30,
              opacity: publishLoading ? 0.6 : 1,
            }}
            title="Publish Retrade v1 (returns share links)"
          >
            {publishLoading ? 'Publishing...' : 'Publish v1'}
          </button>
        </div>
      )}
      {(publishError || publishLinks.length > 0) && (
        <div style={{
          padding: '10px 16px',
          background: '#0A1828',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          flexShrink: 0,
        }}>
          {publishError && (
            <div style={{ ...mono, fontSize: 9, color: 'rgba(224,112,112,0.95)' }}>
              {publishError}
            </div>
          )}
          {publishLinks.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {publishLinks.map((l, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ ...mono, fontSize: 8, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.10em', textTransform: 'uppercase' }}>
                    {l.label}
                  </div>
                  <a href={l.url} target="_blank" rel="noopener noreferrer" style={{ ...mono, fontSize: 9, color: '#C4956A', textDecoration: 'none' }}>
                    {l.url}
                  </a>
                  <button
                    onClick={async () => {
                      const ok = await copyTextToClipboard(l.url);
                      if (ok) {
                        setPublishCopied(l.url);
                        setTimeout(() => setPublishCopied(''), 1200);
                      }
                    }}
                    style={{
                      ...mono, fontSize: 8, letterSpacing: '0.10em', textTransform: 'uppercase',
                      padding: '4px 10px',
                      background: 'transparent',
                      color: publishCopied === l.url ? '#4DBDBD' : 'rgba(255,255,255,0.70)',
                      border: '1px solid rgba(255,255,255,0.20)',
                      borderRadius: 0,
                      cursor: 'pointer',
                      height: 26,
                    }}
                  >
                    {publishCopied === l.url ? 'Copied' : 'Copy'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div style={{ flex: 1, width: '100%', overflow: 'hidden' }}>
        {hasHtml ? (
          <iframe
            srcDoc={html}
            title="Presentation 2"
            style={{ width: '100%', height: '100%', border: 'none', display: 'block', background: '#FAFAF8' }}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#07192A', padding: 40 }}>
            <div style={{ ...mono, fontSize: 8, letterSpacing: '0.28em', textTransform: 'uppercase', color: T.copperText, marginBottom: 12 }}>RETRADE REHEARSAL</div>
            <div style={{ ...serif, fontSize: 28, color: T.text1, marginBottom: 8 }}>Presentation 2</div>
            <div style={{ ...sans, fontSize: 14, color: T.textMuted, marginBottom: 24 }}>Not yet generated.</div>
            {generating ? (
              <div style={{ ...mono, fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.copperText }}>Running simulation...</div>
            ) : (
              <button onClick={handleGenerate} style={{
                ...mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase',
                background: T.copper, color: '#F5F0E8', border: 'none', padding: '12px 24px',
                cursor: 'pointer', borderRadius: 0, minHeight: 44,
              }}>Generate</button>
            )}
            {genError && <div style={{ ...mono, fontSize: 9, color: T.red, marginTop: 12, textAlign: 'center', maxWidth: 400 }}>{genError}</div>}
          </div>
        )}
      </div>
    </div>
  );

  const rightPanel = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{
        display: 'flex', alignItems: 'center',
        padding: '0 20px', background: '#0A1828',
        borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0, minHeight: 44, height: 44,
      }}>
        <div style={{ ...mono, fontSize: 8, color: T.copperText, letterSpacing: '0.20em', textTransform: 'uppercase' }}>
          Presenter Script
        </div>
      </div>
      <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${T.border}`, flexShrink: 0, overflowX: 'auto' }}>
        {SCRIPT_TABS.map(t => {
          const key = t.toLowerCase().replace(/ /g, '_');
          const active = scriptTab === key;
          return (
            <button
              key={t}
              onClick={() => setScriptTab(key)}
              style={{
                ...mono, fontSize: 8, letterSpacing: '0.12em', textTransform: 'uppercase',
                padding: '12px 16px', background: 'none', border: 'none',
                borderBottom: active ? `2px solid ${T.copper}` : '2px solid transparent',
                color: active ? T.text1 : T.textMuted, cursor: 'pointer',
                whiteSpace: 'nowrap', minHeight: 40,
              }}
            >{t}</button>
          );
        })}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {sectionToRender.say && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ ...mono, fontSize: 8, color: T.copperText, marginBottom: 4 }}>SAY:</div>
            <div style={{ ...sans, fontSize: 14, color: T.text1, lineHeight: 1.6 }}>{sectionToRender.say}</div>
          </div>
        )}
        {sectionToRender.ask && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ ...mono, fontSize: 8, color: T.copperText, marginBottom: 4 }}>ASK:</div>
            <div style={{ ...sans, fontSize: 14, color: T.text1, fontStyle: 'italic', lineHeight: 1.6 }}>{sectionToRender.ask}</div>
          </div>
        )}
        {sectionToRender.listen_for && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ ...mono, fontSize: 8, color: T.textMuted, marginBottom: 4 }}>LISTEN FOR:</div>
            <div style={{ ...sans, fontSize: 13, color: T.text2, lineHeight: 1.6 }}>{sectionToRender.listen_for}</div>
          </div>
        )}
        {sectionToRender.pushback && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ ...mono, fontSize: 8, color: T.amber, marginBottom: 4 }}>
              PUSHBACK{sectionToRender.pushback_prompt ? ` -- "${sectionToRender.pushback_prompt}":` : ':'}
            </div>
            <div style={{ ...sans, fontSize: 13, color: T.text2, lineHeight: 1.6 }}>{sectionToRender.pushback}</div>
          </div>
        )}
        {sectionToRender.transition && (
          <div>
            <div style={{ ...mono, fontSize: 8, color: T.textMuted, marginBottom: 4 }}>TRANSITION:</div>
            <div style={{ ...sans, fontSize: 13, color: T.text2, fontStyle: 'italic', lineHeight: 1.6 }}>{sectionToRender.transition}</div>
          </div>
        )}
        {Object.keys(sectionToRender).length === 0 && (
          <div style={{ ...mono, fontSize: 9, color: T.textMuted, textAlign: 'center', marginTop: 40 }}>
            Script content not yet generated for this section.
          </div>
        )}
      </div>
    </div>
  );

  return <SplitScreen left={leftPanel} right={rightPanel} evenSplit />;
}

/* ═══════════════════════════════════════════
   TAB 5 -- PRESENTATION 2
   ═══════════════════════════════════════════ */


/* ═══════════════════════════════════════════
   TAB 6 -- INTERVENTION INDEX
   ═══════════════════════════════════════════ */

function InterventionIndexTab({ eng: engProp, engId, onRefresh }) {
  // Local light-mode palette per Cordis design system spec
  const C = {
    navy: '#FAFAF8', n1: '#F3F2EF', cr: '#0A1828',
    mu: 'rgba(10,24,40,.46)', cu: '#7A5230', cu3: 'rgba(122,82,48,.10)',
    te: '#1A6B6B', bd: 'rgba(10,24,40,.10)',
  };
  const fSerif = { fontFamily: "'Cormorant Garamond', serif" };
  const fMono = { fontFamily: "'DM Mono', monospace" };
  const fSans = { fontFamily: "'DM Sans', sans-serif" };

  const [eng, setEng] = useState(engProp);
  const sentry = parseJ(eng.sentry_preliminary_results);
  const gap = parseJ(eng.gap_analysis);
  const interventions = sentry.interventions || parseJ(eng.intervention_simulation) || [];

  const [localDecisions, setLocalDecisions] = useState(parseJ(eng.intervention_decisions));
  const [notes, setNotes] = useState(eng.mira_notes || '');
  const [savedFlash, setSavedFlash] = useState(false);
  const [sortKey, setSortKey] = useState('roi');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    let active = true;
    (async () => {
      if (!engId) return;
      const { data } = await supabase
        .from('cordis_engagements')
        .select('sentry_preliminary_results, gap_analysis, intervention_decisions, intervention_simulation, mira_notes')
        .eq('id', engId).single();
      if (active && data) {
        setEng(prev => ({ ...prev, ...data }));
        setLocalDecisions(parseJ(data.intervention_decisions));
        if (data.mira_notes !== undefined && data.mira_notes !== null) setNotes(data.mira_notes || '');
      }
    })();
    return () => { active = false; };
  }, [engId]);

  const handleDecision = async (name, decision) => {
    const updated = { ...localDecisions, [name]: decision };
    setLocalDecisions(updated);
    await supabase.from('cordis_engagements').update({ intervention_decisions: updated }).eq('id', engId);
  };

  const handleNotesBlur = async () => {
    await supabase.from('cordis_engagements').update({ mira_notes: notes }).eq('id', engId);
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 2000);
  };

  const fmtMoney = (n) => {
    const num = parseFloat(n);
    if (!isFinite(num)) return '--';
    if (Math.abs(num) >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    if (Math.abs(num) >= 1e3) return `$${Math.round(num).toLocaleString()}`;
    return `$${num.toFixed(0)}`;
  };

  const getCost = (iv) => parseFloat(iv.cost_estimate ?? iv.cost ?? 0) || 0;
  const getROI = (iv) => {
    if (iv.roi != null) return parseFloat(iv.roi) || 0;
    const c = getCost(iv); return c > 0 ? (parseFloat(iv.ev_add) || 0) / c : 0;
  };
  const getTimeline = (iv) => parseFloat(iv.timeline_months ?? iv.timeline ?? 0) || 0;

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const sorted = [...interventions].sort((a, b) => {
    const get = (iv) => sortKey === 'ev_add' ? (parseFloat(iv.ev_add) || 0)
      : sortKey === 'timeline' ? getTimeline(iv) : getROI(iv);
    const d = get(a) - get(b);
    return sortDir === 'desc' ? -d : d;
  });

  const totalEV = interventions.reduce((s, iv) => s + (parseFloat(iv.ev_add) || 0), 0);

  // Document name readability map + fallback
  const docNameMap = {
    tax_return_y3: 'Tax Return (Year 3)',
    tax_return_y2: 'Tax Return (Year 2)',
    tax_return_y1: 'Tax Return (Year 1)',
    customer_list: 'Customer List',
    employee_list: 'Employee List',
    lease_agreements: 'Lease Agreements',
    corporate_docs: 'Corporate Documents',
    financial_statements: 'Financial Statements',
    org_chart: 'Organizational Chart',
  };
  const readableDoc = (k) => {
    if (typeof k !== 'string') return String(k);
    if (docNameMap[k]) return docNameMap[k];
    return k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };
  const SUPPLEMENTARY = new Set(['org_chart']);
  const docPriority = (k) => SUPPLEMENTARY.has(k) ? 'MED' : 'HIGH';

  const missingDocs = Array.isArray(gap.missing_docs) ? gap.missing_docs : [];
  const inconsistencies = Array.isArray(gap.inconsistencies) ? gap.inconsistencies : [];

  // Styles
  const thStyle = {
    ...fMono, fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase',
    color: C.cr, background: C.n1, padding: '12px 14px',
    borderBottom: `1px solid ${C.bd}`, textAlign: 'left',
    cursor: 'default', userSelect: 'none',
  };
  const thSortable = { ...thStyle, cursor: 'pointer' };
  const tdStyle = {
    padding: '14px', borderBottom: `1px solid ${C.bd}`,
    ...fSans, fontSize: 13, color: C.cr, verticalAlign: 'middle',
  };

  const ACTIONS = [
    { key: 'will_do', label: 'Will do' },
    { key: 'wont_do', label: "Won't do" },
    { key: 'maybe', label: 'Maybe' },
  ];

  const sortIndicator = (k) => sortKey === k ? (sortDir === 'desc' ? ' ↓' : ' ↑') : '';

  return (
    <div style={{ background: C.navy, minHeight: 'calc(100vh - 48px)', padding: 28 }}>
      {/* SECTION 1 — Scorecard */}
      <div style={{ ...fMono, fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase', color: C.cu, marginBottom: 14 }}>
        INTERVENTION SCORECARD
      </div>
      <div style={{ border: `1px solid ${C.bd}`, background: C.navy, marginBottom: 36 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, width: 60 }}>Rank</th>
              <th style={thStyle}>Intervention</th>
              <th style={thSortable} onClick={() => toggleSort('ev_add')}>EV Add{sortIndicator('ev_add')}</th>
              <th style={thStyle}>Cost</th>
              <th style={thSortable} onClick={() => toggleSort('roi')}>ROI{sortIndicator('roi')}</th>
              <th style={thSortable} onClick={() => toggleSort('timeline')}>Timeline{sortIndicator('timeline')}</th>
              <th style={{ ...thStyle, width: 240 }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr><td colSpan={7} style={{ ...tdStyle, ...fMono, fontSize: 10, color: C.mu, textAlign: 'center', padding: 32 }}>
                No interventions generated yet.
              </td></tr>
            ) : sorted.map((iv, i) => {
              const roi = getROI(iv);
              const roiCopper = roi > 20;
              const cost = getCost(iv);
              const timeline = getTimeline(iv);
              const current = localDecisions[iv.name];
              return (
                <tr key={iv.name || i}>
                  <td style={{
                    ...tdStyle,
                    borderLeft: i === 0 ? `2px solid ${C.cu}` : '2px solid transparent',
                    ...fMono, fontSize: 11, color: C.cu,
                  }}>{String(i + 1).padStart(2, '0')}</td>
                  <td style={tdStyle}>
                    <div style={{ ...fSans, fontSize: 14, color: C.cr, fontWeight: 500 }}>{iv.name}</div>
                    {iv.description && (
                      <div style={{ ...fSans, fontSize: 12, color: C.mu, marginTop: 4, lineHeight: 1.5 }}>{iv.description}</div>
                    )}
                  </td>
                  <td style={{ ...tdStyle, ...fSerif, fontSize: 18, color: C.cu }}>{fmtMoney(iv.ev_add)}</td>
                  <td style={{ ...tdStyle, ...fMono, fontSize: 12, color: C.cr }}>{fmtMoney(cost)}</td>
                  <td style={{ ...tdStyle, ...fMono, fontSize: 12, color: roiCopper ? C.cu : C.cr }}>
                    {roi ? roi.toFixed(1) + 'x' : '--'}
                  </td>
                  <td style={{ ...tdStyle, ...fMono, fontSize: 12, color: C.cr }}>
                    {timeline ? `${timeline} months` : '--'}
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {ACTIONS.map(a => {
                        const active = current === a.key;
                        return (
                          <button
                            key={a.key}
                            onClick={() => handleDecision(iv.name, a.key)}
                            style={{
                              ...fMono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase',
                              padding: '6px 10px', border: `1px solid ${active ? C.cu : C.bd}`,
                              background: active ? C.cu3 : C.navy,
                              color: active ? C.cu : C.cr,
                              cursor: 'pointer', borderRadius: 0,
                            }}
                          >
                            {a.label}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* SECTION 2 — Gap Analysis */}
      <div style={{ ...fMono, fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase', color: C.cu, marginBottom: 14 }}>
        GAP ANALYSIS
      </div>
      <div style={{ border: `1px solid ${C.bd}`, display: 'grid', gridTemplateColumns: '1fr 1fr', marginBottom: 36 }}>
        <div style={{ borderRight: `1px solid ${C.bd}` }}>
          <div style={{ ...fMono, fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: C.cu, padding: '14px 18px', borderBottom: `1px solid ${C.bd}`, background: C.n1 }}>
            Missing Documents
          </div>
          <div>
            {missingDocs.length === 0 ? (
              <div style={{ ...fMono, fontSize: 10, color: C.mu, padding: 18 }}>None.</div>
            ) : missingDocs.map((d, i) => {
              const key = typeof d === 'string' ? d : (d.key || d.name || '');
              const priority = docPriority(key);
              const isHigh = priority === 'HIGH';
              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 18px', borderBottom: i < missingDocs.length - 1 ? `1px solid ${C.bd}` : 'none',
                }}>
                  <span style={{ ...fSans, fontSize: 13, color: C.cr }}>{readableDoc(key)}</span>
                  <span style={{
                    ...fMono, fontSize: 9, letterSpacing: '0.14em',
                    padding: '4px 8px',
                    background: isHigh ? C.cu : C.n1,
                    color: isHigh ? C.navy : C.cr,
                  }}>{priority}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div>
          <div style={{ ...fMono, fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: C.cu, padding: '14px 18px', borderBottom: `1px solid ${C.bd}`, background: C.n1 }}>
            Open Inconsistencies
          </div>
          <div>
            {inconsistencies.length === 0 ? (
              <div style={{ ...fMono, fontSize: 10, color: C.mu, padding: 18 }}>None.</div>
            ) : inconsistencies.map((row, i) => {
              const text = typeof row === 'string' ? row : (row.description || row.text || JSON.stringify(row));
              return (
                <div key={i} style={{
                  ...fSans, fontSize: 13, color: C.cr,
                  padding: '12px 18px',
                  borderLeft: `2px solid ${C.cu}`,
                  borderBottom: i < inconsistencies.length - 1 ? `1px solid ${C.bd}` : 'none',
                  lineHeight: 1.5,
                }}>{text}</div>
              );
            })}
          </div>
        </div>
      </div>

      {/* SECTION 3 — Advisor Notes */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <div style={{ ...fMono, fontSize: 9, letterSpacing: '0.20em', textTransform: 'uppercase', color: C.cu }}>
          ADVISOR NOTES
        </div>
        {savedFlash && (
          <div style={{ ...fMono, fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: C.cu }}>
            Saved
          </div>
        )}
      </div>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        onBlur={handleNotesBlur}
        onFocus={(e) => { e.currentTarget.style.border = `1px solid ${C.cu}`; }}
        onBlurCapture={(e) => { e.currentTarget.style.border = `1px solid ${C.bd}`; }}
        placeholder="Notes visible to advisors only — not included in any client deliverable"
        style={{
          ...fSans, fontSize: 13, color: C.cr,
          border: `1px solid ${C.bd}`, background: C.navy,
          padding: 14, minHeight: 120, width: '100%',
          outline: 'none', borderRadius: 0, resize: 'vertical',
          marginBottom: 36, boxSizing: 'border-box',
        }}
      />

      {/* SECTION 4 — Misalignment Tax Summary */}
      <div style={{
        border: `1px solid ${C.bd}`, background: C.n1,
        padding: '22px 28px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap',
      }}>
        <div>
          <div style={{ ...fMono, fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase', color: C.cu, marginBottom: 6 }}>
            MISALIGNMENT TAX
          </div>
          <div style={{ ...fSerif, fontSize: 36, color: C.cu, lineHeight: 1 }}>{fmtMoney(totalEV)}</div>
        </div>
        <div style={{ ...fSans, fontSize: 13, color: C.mu, maxWidth: 420, textAlign: 'right' }}>
          Maximum upside if all interventions completed
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   TAB 7 -- FINAL REPORT
   ═══════════════════════════════════════════ */

function FinalReportTab({ eng, engId, onRefresh }) {
  const [html, setHtml] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');
  const [delivered, setDelivered] = useState(eng.status === 'report_delivered' || !!eng.report_delivered_at);
  const [deliveredAt, setDeliveredAt] = useState(eng.report_delivered_at || null);
  const [pdfReady, setPdfReady] = useState(false);
  const [pdfHover, setPdfHover] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    fetch(`${WORKFLOW_BASE}/pdf-status/${engId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data && data.final_report) setPdfReady(true); })
      .catch(() => {});
  }, [engId]);

  const handleDownloadPdf = async (doc) => {
    try {
      setPdfLoading(true);
      const url = `${WORKFLOW_BASE}/generate-pdf/${engId}?doc=${doc}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('PDF fetch failed:', err);
    } finally {
      setPdfLoading(false);
    }
  };

  const pdfBtnStyle = {
    fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase',
    background: pdfHover ? 'rgba(122,82,48,.08)' : 'transparent',
    color: '#7A5230', border: '1px solid #7A5230',
    padding: '8px 16px', cursor: 'pointer', borderRadius: 0,
  };

  useEffect(() => {
    const _ps = parseJ(eng.presentation_scripts);
    if (_ps.final_report_html && _ps.final_report_html.length > 100) {
      setHtml(_ps.final_report_html);
      return;
    }
    supabase.from('cordis_engagements').select('presentation_scripts, status, report_delivered_at').eq('id', engId).single()
      .then(({ data }) => {
        if (!data) return;
        const fetched = parseJ(data.presentation_scripts);
        if (fetched.final_report_html && fetched.final_report_html.length > 100) {
          setHtml(fetched.final_report_html);
        }
        if (data.status === 'report_delivered' || data.report_delivered_at) {
          setDelivered(true);
          setDeliveredAt(data.report_delivered_at);
        }
      })
      .catch(() => {});
  }, [eng.presentation_scripts, engId]);

  const handleGenerate = async () => {
    setGenerating(true);
    setGenError('');
    try {
      const res = await fetch(`${WORKFLOW_BASE}/generate-final-report/${engId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { data } = await supabase.from('cordis_engagements').select('presentation_scripts').eq('id', engId).single();
      const fetched = parseJ(data && data.presentation_scripts);
      if (fetched.final_report_html && fetched.final_report_html.length > 100) {
        setHtml(fetched.final_report_html);
      } else {
        setGenError('Generation completed but HTML not found.');
      }
      if (onRefresh) onRefresh();
    } catch (err) {
      setGenError('Generation failed. Please try again.');
    }
    setGenerating(false);
  };

  const handleDeliver = async () => {
    const ts = new Date().toISOString();
    await supabase.from('cordis_engagements').update({ status: 'report_delivered', report_delivered_at: ts }).eq('id', engId);
    setDelivered(true);
    setDeliveredAt(ts);
    if (onRefresh) onRefresh();
  };

  const hasHtml = !!html && html.length > 100;

  const fmtMoney = (n) => {
    const num = parseFloat(n);
    if (!isFinite(num)) return '--';
    if (Math.abs(num) >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    if (Math.abs(num) >= 1e3) return `$${Math.round(num).toLocaleString()}`;
    return `$${num.toFixed(0)}`;
  };

  const clientName = eng.client_name || eng.primary_contact_name || '--';
  const companyName = eng.company_name || eng.company || '--';
  const fee = eng.engagement_fee != null ? eng.engagement_fee : (eng.fee != null ? eng.fee : null);
  const dqs = eng.data_quality_score != null ? eng.data_quality_score : (parseJ(eng.data_quality).score != null ? parseJ(eng.data_quality).score : null);

  const deliveredDate = deliveredAt ? new Date(deliveredAt).toLocaleDateString() : '';

  const labelCss = { fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#7A5230', marginBottom: 4 };
  const valueCss = { fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: '#0A1828', lineHeight: 1.1 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 48px)', background: '#FAFAF8' }}>
      {hasHtml && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '10px 16px', borderBottom: '1px solid rgba(10,24,40,.10)', background: '#FAFAF8' }}>
          <button onClick={handleGenerate} disabled={generating} style={{
            fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase',
            background: 'transparent', color: '#7A5230', border: '1px solid #7A5230',
            padding: '8px 16px', cursor: generating ? 'not-allowed' : 'pointer',
            borderRadius: 0, opacity: generating ? 0.6 : 1,
          }}>
            {generating ? 'Regenerating...' : 'Regenerate'}
          </button>
          {pdfReady && (
            <button
              onClick={() => handleDownloadPdf('final_report')}
              disabled={pdfLoading}
              onMouseEnter={() => setPdfHover(true)}
              onMouseLeave={() => setPdfHover(false)}
              style={{ ...pdfBtnStyle, cursor: pdfLoading ? 'not-allowed' : 'pointer', opacity: pdfLoading ? 0.6 : 1 }}
            >{pdfLoading ? 'Generating...' : 'Download PDF'}</button>
          )}
        </div>
      )}
      <div style={{ flex: 1 }}>
        {hasHtml ? (
          <iframe
            srcDoc={html}
            title="Final Report"
            style={{ width: '100%', minHeight: '85vh', border: 'none', background: '#FFFFFF', display: 'block' }}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 60 }}>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: '0.28em', textTransform: 'uppercase', color: '#7A5230', marginBottom: 12 }}>FINAL REPORT</div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, color: '#0A1828', marginBottom: 8 }}>Not yet generated</div>
            {generating ? (
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#7A5230', marginTop: 16 }}>Running simulation...</div>
            ) : (
              <button onClick={handleGenerate} style={{
                fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase',
                background: '#7A5230', color: '#FFFFFF', border: 'none', padding: '14px 28px',
                cursor: 'pointer', borderRadius: 0, marginTop: 16,
              }}>GENERATE FINAL REPORT</button>
            )}
            {genError && <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: 'rgba(160,40,40,0.85)', marginTop: 12 }}>{genError}</div>}
          </div>
        )}
      </div>

      <div style={{
        background: '#F3F2EF', padding: 24, borderTop: '1px solid rgba(10,24,40,.10)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
          <div>
            <div style={labelCss}>CLIENT</div>
            <div style={valueCss}>{clientName}</div>
          </div>
          <div>
            <div style={labelCss}>COMPANY</div>
            <div style={valueCss}>{companyName}</div>
          </div>
          <div>
            <div style={labelCss}>FEE</div>
            <div style={valueCss}>{fee != null ? fmtMoney(fee) : '--'}</div>
          </div>
          <div>
            <div style={labelCss}>DATA QUALITY</div>
            <div style={valueCss}>{dqs != null ? `${dqs}/100` : '--'}</div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
          <button
            onClick={handleDeliver}
            disabled={delivered || !hasHtml}
            style={{
              fontFamily: "'DM Mono', monospace", fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
              background: '#7A5230', color: '#FFFFFF', border: 'none', padding: '16px 32px',
              cursor: (delivered || !hasHtml) ? 'not-allowed' : 'pointer',
              borderRadius: 0, opacity: (delivered || !hasHtml) ? 0.5 : 1,
            }}
          >APPROVE AND DELIVER</button>
          {delivered && (
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#7A5230' }}>
              Delivered {deliveredDate}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   LAB SUB-TAB WRAPPER
   ═══════════════════════════════════════════ */

const LAB_TABS = [
  { key: 'buyer_lens_audit', label: 'BUYER LENS AUDIT' },
  { key: 'evidence', label: 'EVIDENCE BINDER' },
  { key: 'market', label: 'MARKET MAPPING' },
  { key: 'retrade', label: 'RETRADE REHEARSAL' },
  { key: 'interventions', label: 'INTERVENTION INDEX' },
  { key: 'report', label: 'FINAL REPORT' },
];

const labTabBase = {
  ...mono, fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase',
  background: 'none', border: 'none',
  borderBottom: '2px solid transparent',
  color: 'rgba(255,255,255,0.40)',
  padding: '0 20px', height: 48, lineHeight: '48px', cursor: 'pointer',
  whiteSpace: 'nowrap', flexShrink: 0,
  transition: 'color 0.15s, border-color 0.15s',
};

const labTabActive = {
  color: '#FFFFFF',
  borderBottomColor: '#C4956A',
};

export default function LabWorkstation({ engagementId, engagements, onSelectEngagement }) {
  const params = useParams();
  const resolvedId = engagementId || params.engagement_id || localStorage.getItem('cordis_active_engagement_id') || '';
  const [activeTab, setActiveTab] = useState('buyer_lens_audit');
  const [eng, setEng] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchEng = useCallback(async () => {
    if (!resolvedId) return;
    const { data } = await supabase.from('cordis_engagements').select('*').eq('id', resolvedId).single();
    if (data) setEng(data);
    setLoading(false);
  }, [resolvedId]);

  useEffect(() => { fetchEng(); }, [fetchEng]);

  if (loading || !eng) {
    return (
      <div style={{ background: T.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ ...mono, fontSize: 10, color: T.copperText, letterSpacing: '0.14em' }}>LOADING LAB...</div>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'buyer_lens_audit': return <BuyerLensAuditTab engagement={eng} />;
      case 'evidence': return <EvidenceBinderTab eng={eng} engId={resolvedId} onRefresh={fetchEng} />;
      case 'market': return <MarketMappingTab eng={eng} engId={resolvedId} onRefresh={fetchEng} />;
      case 'retrade': return <RetradeRehearsalTab eng={eng} engId={resolvedId} onRefresh={fetchEng} />;
      case 'interventions': return <InterventionIndexTab eng={eng} engId={resolvedId} onRefresh={fetchEng} />;
      case 'report': return <FinalReportTab eng={eng} engId={resolvedId} onRefresh={fetchEng} />;
      default: return null;
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column' }}>
      {/* Tab bar — FIX 1 */}
      <style>{`.lab-tab-bar::-webkit-scrollbar{display:none}.lab-tab-bar{-ms-overflow-style:none;scrollbar-width:none}`}</style>
      <div className="lab-tab-bar" style={{
        display: 'flex', overflowX: 'auto', WebkitOverflowScrolling: 'touch',
        background: '#0A1828', borderBottom: '1px solid rgba(255,255,255,0.06)',
        height: 48, whiteSpace: 'nowrap', flexShrink: 0,
      }}>
        {LAB_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{ ...labTabBase, ...(activeTab === tab.key ? labTabActive : {}) }}
            onMouseEnter={e => { if (activeTab !== tab.key) e.currentTarget.style.color = 'rgba(255,255,255,0.70)'; }}
            onMouseLeave={e => { if (activeTab !== tab.key) e.currentTarget.style.color = 'rgba(255,255,255,0.40)'; }}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {/* Content */}
      <div style={{ flex: 1 }}>{renderTabContent()}</div>
    </div>
  );
}
